import React, { useState, useMemo, useRef, useEffect, useCallback, useContext } from 'react';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  FolderIcon,
  ListBulletIcon,
  ShieldExclamationIcon,
  LinkIcon,
  ClockIcon,
  CodeBracketIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
  EyeSlashIcon,
  EyeIcon,
  CursorArrowRaysIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import { getRiskBgClass, getRiskLabel, getUrlStatusTextClass, getUrlStatusLabel, getNextUrlStatus, getIPStatusBgClass, getIPStatusTextClass, getIPStatusLabel, urlStatusTone, sessionStatusTone } from '../utils/riskColors';
import { assessAttendance, nearbySignIns, meetingSeenBeforeAttacker, MEETING_SIGNIN_WINDOW_MIN } from '../utils/teamsMeetings';
import StatusCircle from './ui/StatusCircle';
import { isPostCompromiseSessionEvent } from '../utils/attackerWindow';
import { formatDateRange } from '../utils/dateRange';
import { isAliasIdentityEvent } from '../utils/identityResolution';
import { collapseMessageTrace, describeMessageTrace, recipientSummary, recipientStatusSummary, MT_RECIPIENT_DISPLAY_CAP } from '../utils/messageTraceCollapse';
import { describeNonInteractive } from '../utils/nonInteractiveRollup';
import { describeAccessPeriod, describeRolledRow } from '../utils/accessPeriods';
import { actionByproductSuffix } from '../utils/directoryActions';
import { describeDeviceRegistration, isDeviceRegistrationOp, eventDeviceNames } from '../utils/deviceRegistration';
import { describeQuarantineRelease } from '../utils/quarantineRelease';
import { describeRecoveredLeg } from '../utils/signInTransactions';
import { sessionStatusOf, attackerSessionMarks } from '../utils/sessionMarks';
import { SessionMarkContext } from './SessionMarkContext';
import { applyAllEventsRollups, rowAppLabel, signInApplicationLabel } from '../utils/allEventsRollups';
import { CASE_ALERT_EVENT_TYPE, CASE_ALERT_LABEL, describeCaseAlert, caseAlertHref } from '../utils/caseAlerts';
import { isGuessedIpMethod, guessedIpBasis, appOnlyReader } from '../utils/guessedIp';
import {
  buildAttackerContext, attackerBasis, attackerBasisLabel, attackerBasisTitle, rowEventIds, analystMarkOf, analystSafeOf,
} from '../utils/attackerAttribution';
import { getDirAuditDetail, getDisplayOperation, enrichDirAuditWithDeviceNames, isAutoMfaUpdate, getSecurityAlertSummary, getSecurityAlertUrl, deduplicateAlerts, mustShowDespiteImplicitTrust, maskSubject, maskFileName, FILENAME_MASK, describeDeletedMailItems, describeExchangeItemChange, exchangeItemActor, parseItemAttachments, topFolderSegment, parseModifiedProperties, serviceInitiatorLabel, dirAuditTargetLabel, OUTLOOK_NOISE_OPS, selectTimelineMessageTrace, selectTimelineUrlClicks
} from '../utils/eventUtils';
import EnterpriseAppsTab from './EnterpriseAppsTab';
import Pagination from './Pagination';
import CopyButton from './CopyButton';
import Badge from './ui/Badge';
import { severityTone, alertStatusTone } from './ui/badgeTones';

// ── Sub-tab definitions ──────────────────────────────────────────────────────

const SUB_TABS = [
  { id: 'all', label: 'All Events' },
  { id: 'signins', label: 'Sign-In Logs' },
  { id: 'apps', label: 'Enterprise Apps' },
  { id: 'directory', label: 'Directory Audit' },
  { id: 'msgtrace', label: 'Message Trace' },
  { id: 'phishing', label: 'Related Recipients' },
  { id: 'urlclicks', label: 'URL Clicks' },
  { id: 'spfiles', label: 'SharePoint Files' },
  { id: 'splists', label: 'SharePoint Lists' },
  { id: 'riskdetection', label: 'MSFT Risk Detections' },
  { id: 'riskyuser', label: 'MSFT Risky Users' },
  { id: 'dlp', label: 'DLP' },
  { id: 'general', label: 'General Audit' },
  { id: 'azureactivity', label: 'Azure Activity' },
  { id: 'securityalerts', label: 'MSFT Security Alerts' },
  { id: 'securityincidents', label: 'MSFT Security Incidents' },
];

// ── Per-case IP comments (read-only in the timeline) ─────────────────────────
// Freeform per-IP labels ({ "1.2.3.4": "Atlanta Office IP" }) are edited only on the
// IP Addresses tab. They're provided via context here so any IP render site can drop
// in a subtle inline note without threading a prop through every inner component.
const IpCommentsContext = React.createContext({});

/** Subtle inline gray note shown after an IP when the case has a comment for it. */
function IpCommentNote({ ip }) {
  const comments = useContext(IpCommentsContext);
  const note = ip && comments ? comments[ip] : '';
  if (!note) return null;
  return <span className="ml-1 text-[10px] text-gray-500 font-normal not-italic" title={note}>{note}</span>;
}

// Recovered sign-in legs (v1.188.0) ----------------------------------------
// "Recovered · no UPN" is Microsoft's ordinary logging for MFA sub-steps, not an AiTM signal.
// CaseDetail attributes each leg by its sign-in TRANSACTION (utils/signInTransactions.js) and
// provides the result here, so the All Events row, the Sign-In tab row and the expanded detail
// — three surfaces that render independently — all say the same thing about the same leg.
// Plans/2026-09-30_Recovered-Legs-Transaction-Attribution.md §2.6.
const LegAttributionContext = React.createContext(null);

const LEG_TONE = {
  red: { badge: 'bg-red-900/50 text-red-300 border-red-700/50', box: 'bg-red-900/30 border-red-700/50', text: 'text-red-200/90' },
  amber: { badge: 'bg-amber-900/40 text-amber-300 border-amber-800/50', box: 'bg-amber-900/30 border-amber-700/50', text: 'text-amber-200/90' },
  neutral: { badge: 'bg-gray-800 text-gray-300 border-gray-600', box: 'bg-gray-800/60 border-gray-600', text: 'text-gray-300' },
};
// Without an analysis (it has not been computed) the badge keeps its old amber tone but
// no longer claims anything about AiTM.
const LEG_FALLBACK = {
  tone: 'amber',
  summary: 'Microsoft logged this sign-in without a username; it was recovered by matching the user\'s object ID.',
  detail: 'Microsoft logs multi-factor sign-in steps without a username. This one was recovered by matching the user\'s Azure AD object ID and attributed to this user.',
};

function useRecoveredLegCopy(event) {
  const analysis = useContext(LegAttributionContext);
  return describeRecoveredLeg(analysis?.byEventId?.get(event?.EventId)) || LEG_FALLBACK;
}

/** The "recovered · no UPN" row badge, toned and explained by the leg's verdict. */
function RecoveredLegBadge({ event, className = 'ml-2' }) {
  const copy = useRecoveredLegCopy(event);
  if (event?.UnattributedRecovery !== true) return null;
  return (
    <span
      className={`${className} inline-flex items-center px-1.5 py-0 rounded text-[9px] font-semibold border ${LEG_TONE[copy.tone].badge}`}
      title={copy.summary}
    >
      recovered &middot; no UPN
    </span>
  );
}

/** The expanded-row explanation for a recovered leg. */
function RecoveredLegBanner({ event }) {
  const copy = useRecoveredLegCopy(event);
  if (event?.UnattributedRecovery !== true) return null;
  const tone = LEG_TONE[copy.tone];
  return (
    <div className={`mb-3 px-3 py-2 rounded border ${tone.box}`}>
      <div className="flex items-start gap-2">
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border whitespace-nowrap ${tone.badge}`}>
          Recovered &middot; no UPN
        </span>
        <p className={`text-[11px] leading-snug ${tone.text}`}>{copy.detail}</p>
      </div>
    </div>
  );
}

// One action, several rows (directory audit) -------------------------------
// Microsoft writes one thing a person did as several audit events -- a B2B guest
// invitation arrives as `Add user` + `Update PasswordProfile` a quarter-second apart --
// and the timeline shows them as unrelated rows. The index is keyed by EventId so any
// row can look itself up; provided by context for the same reason as IpCommentsContext,
// since the three renderers that need it (All Events, the Directory Audit tab, and the
// expanded detail) sit at different depths.

/** Small marker on a directory-audit row that is one fragment of a larger action. */
function CorrelatedActionBadge({ event, className = 'ml-1.5' }) {
  if (!event?._action) return null;
  const n = event._actionRowCount || 1;
  const others = (event._actionOtherOps || [])
    .map((o) => (o.count > 1 ? `${o.op} x${o.count}` : o.op)).join(', ');
  const spans = event._actionSpansCorrelationIds
    ? ' Microsoft split this action across more than one correlation id, so the rows do not '
      + 'share an identifier -- they are grouped by target and time.'
    : '';
  return (
    <span
      className={`${className} inline-flex items-center px-1.5 py-0 rounded text-[9px] font-semibold border bg-sky-900/40 text-sky-300 border-sky-800/50`}
      title={`One action, recorded by Microsoft as ${n} directory-audit rows.`
        + (others ? ` Also written: ${others}.` : '')
        + spans
        + ' Expand the row to see each.'}
    >
      {`⛓ ${n} rows`}
    </span>
  );
}

function CorrelatedActionPanel({ event, selectedTz }) {
  const rows = event?._action ? event._actionRows : null;
  if (!rows || rows.length < 2) return null;
  return (
    <div className="mb-3 px-3 py-2 bg-sky-900/20 border border-sky-800/30 rounded-lg text-xs text-sky-200">
      <span className="font-medium">{`One action, ${rows.length} audit events`}</span>
      {' — Microsoft records a single change as several directory-audit rows. '}
      {event._actionSpansCorrelationIds
        ? 'These do NOT all share one Entra correlation id (Microsoft split the invite API call from the '
          + 'directory writes it triggered), so they are grouped by the object they acted on and the moment '
          + 'they happened:'
        : 'These are one thing that happened, not separate activity:'}
      <ul className="mt-1.5 space-y-0.5">
        {rows.map((r, i) => (
          <li key={r.eventId} className={r.isPrincipal ? 'text-sky-100 font-medium' : 'text-sky-300/80'}>
            {`${i + 1}. `}
            <span className="font-mono">{formatTimestamp(r.timeUtc, selectedTz)}</span>
            {` · ${r.op || 'unknown operation'}`}
            {r.isPrincipal ? ' (this row)' : ''}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The events behind an access period.
 *
 * The roll-up is only defensible because nothing becomes unreachable — this is where the
 * constituents are shown. An analyst can see whether 196 sign-ins were a steady
 * background rhythm or arrived in bursts, which the single collapsed row cannot say.
 */
function AccessPeriodPanel({ event, selectedTz }) {
  const rows = event?._period ? event._periodEvents : null;
  if (!rows || rows.length < 2) return null;
  const noun = event._eventType === 'NonInteractiveSignIn' ? 'authentications' : 'sign-ins';
  return (
    <div className="mb-3 px-3 py-2 bg-slate-800/40 border border-slate-700/50 rounded-lg text-xs text-gray-300">
      <span className="font-medium text-gray-200">
        {`${event._periodAuthCount} ${noun} in ${rows.length} records, one access period`}
      </span>
      {' — same address, contiguous activity. Shown as recorded:'}
      <ul className="mt-1.5 space-y-0.5 max-h-48 overflow-y-auto">
        {rows.map((r) => (
          <li key={r.eventId} className="text-gray-400">
            <span className="font-mono text-gray-300">{formatTimestamp(r.startUtc, selectedTz)}</span>
            {r.count > 1 ? ` · ${r.count}` : ''}
            {r.app ? <span className="text-gray-500">{` · ${String(r.app).split(',').slice(0, 2).join(', ')}`}</span> : null}
            {r.result && String(r.result).toLowerCase() !== 'success'
              ? <span className="text-amber-400">{` · ${r.result}`}</span> : null}
          </li>
        ))}
      </ul>
      {(event._periodErrorCodes || []).length ? (
        <div className="mt-1.5 text-amber-300">
          {`Failure codes: ${event._periodErrorCodes.map((c) => `${c.code} ×${c.count}`).join(', ')}`}
        </div>
      ) : null}
      {(event._periodSessions || []).length ? (
        <div className="mt-1 text-gray-500 font-mono text-[10px]">
          {`session${event._periodSessions.length > 1 ? 's' : ''}: ${event._periodSessions.slice(0, 3).join(', ')}`}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Marks an event as attributable to the attacker.
 *
 * 🔴 The three grounds are NOT equal and are rendered differently. An address the analyst
 * classified Malicious is the analyst's own determination on this event's own record. A
 * DEVICE only ever seen from malicious addresses is inferred from sign-in history — it is
 * what attributes a device registration, which has no address of its own. A SESSION is
 * corroboration only — a stolen token keeps the victim's session id, so the victim's own
 * activity necessarily shares the attacker's session. Each tooltip says which it is,
 * rather than leaving the reader to assume.
 */
const AttackerContext = React.createContext(null);

const ATTACKER_BADGE_TONE = {
  ip: 'bg-red-900/50 text-red-300 border-red-800/60',
  analyst: 'bg-rose-900/50 text-rose-200 border-rose-700/60',
  device: 'bg-orange-900/45 text-orange-300 border-orange-800/55',
  session: 'bg-amber-900/40 text-amber-300 border-amber-800/50',
};

function AttackerBadge({ event, className = 'ml-1.5' }) {
  const attackerCtx = useContext(AttackerContext);
  const basis = attackerBasis(event, attackerCtx);
  if (!basis) {
    // v1.189.8: a row the analyst marked Safe says so when it is shown ("Safe IPs shown").
    const safe = analystSafeOf(event, attackerCtx);
    if (!safe) return null;
    return (
      <span
        className={`${className} inline-flex items-center px-1.5 py-0 rounded text-[9px] font-semibold border bg-green-900/40 text-green-300 border-green-700/50`}
        title={`Marked Safe by ${safe.by || 'an analyst'}${safe.reason ? `: ${safe.reason}` : ''}. Hidden with the Safe IPs; never counted as the attacker’s. The address keeps its own classification.`}
      >
        safe · analyst
      </span>
    );
  }
  return (
    <span
      className={`${className} inline-flex items-center px-1.5 py-0 rounded text-[9px] font-semibold border ${
        ATTACKER_BADGE_TONE[basis.basis] || ATTACKER_BADGE_TONE.session}`}
      title={attackerBasisTitle(basis)}
    >
      {attackerBasisLabel(basis)}
    </span>
  );
}

// ── Analyst "attacker activity" marks (v1.189.0) ─────────────────────────────
// For a row whose own recorded address is a Microsoft service's (an authenticator registered
// through a stolen session carries My Signins' backend IP), classifying the IP can never
// attribute it and marking that IP Malicious would be wrong. The analyst marks the ROW
// instead, with a written reason; the IP keeps its own classification. The Executive Report
// reads the marks off the Cases entity. Context, so no prop threading through inner views.
const AnalystMarkContext = React.createContext(null);

const MARK_REASON_MAX = 500;
const MARK_REASON_EXAMPLE = "Performed through the attacker's session; the address shown is Microsoft's registration service.";

function AnalystMarkControl({ event }) {
  const ctx = useContext(AnalystMarkContext);
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  if (!ctx) return null;
  const ids = rowEventIds(event);
  if (!ids.length) return null;
  const mark = analystMarkOf(event, { analystEvents: ctx.marks });
  const safeMark = mark ? null : analystSafeOf(event, { analystEvents: ctx.marks });
  const canEdit = typeof ctx.onSet === 'function';
  if (!mark && !safeMark && !canEdit) return null;
  // Safe is refused on a row whose OWN address is classified Malicious: the address outranks
  // a row mark (it does for the attacker mark too), so a Safe mark there would say one thing
  // and do another. Reclassify the address instead.
  const ownMalicious = typeof ctx.isMaliciousRow === 'function' && ctx.isMaliciousRow(event);

  const save = async (value) => {
    setSaving(true);
    setError('');
    try {
      await ctx.onSet(ids, value);
      setEditing(false);
      setReason('');
    } catch (err) {
      setError(err?.message || 'Could not save. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };
  const stop = (e) => e.stopPropagation();

  // 🔴 Same shape as the IP classifier in this panel (IpClassSelector): a label, then one
  // small button per state; the active one is filled with its status colour and a ✓, the
  // others are muted outlines. Operator asked for the expanded row to be uniform (2026-10-01),
  // and for a per-row Safe so a Microsoft service address need not be marked Safe for the
  // whole case (v1.189.8).
  const optClass = (active, statusKey) => `inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border transition-colors disabled:opacity-40 ${
    active ? getIPStatusBgClass(statusKey) : 'bg-transparent border-gray-700 text-gray-500 hover:text-gray-300 hover:border-gray-500'}`;
  const smallBtn = 'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border bg-transparent border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-500 disabled:opacity-40';
  const marked = Boolean(mark);
  const safe = Boolean(safeMark);
  const selecting = editing && !marked;
  const none = !marked && !safe && !selecting;

  // The server stamps `at` as ISO UTC ('2026-10-01T18:19:24Z'). Shown AS UTC, from the string
  // itself: date-fns `format(parseISO(...))` renders in the BROWSER's zone, and labelling that
  // "UTC" printed 14:19 for an 18:19Z stamp (TIMEZONE_DISPLAY_HISTORY.md — the recurring bug).
  const whenOf = (m) => (m && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(m.at || '')
    ? `${m.at.slice(0, 10)} ${m.at.slice(11, 16)}`
    : (m?.at || ''));
  const when = whenOf(mark);
  const safeWhen = whenOf(safeMark);

  const trimmed = reason.trim();
  return (
    <div className="mb-3 text-xs" onClick={stop}>
      <span className="inline-flex items-center gap-1 align-middle flex-wrap">
        <span
          className="text-[10px] text-gray-500 mr-0.5"
          title="Judge this one row without changing its address's classification. Safe sets it aside with the Safe IPs; Attacker attributes it to the attacker when its own recorded address cannot — for example a sign-in method registered through a stolen session, which Microsoft logs with its own service address."
        >
          This event:
        </span>
        <button
          type="button"
          aria-pressed={none}
          disabled={!canEdit || saving}
          className={optClass(none, 'default')}
          title={marked || safe ? 'Remove the mark from this row' : 'Not marked (current)'}
          onClick={() => { if (marked || safe) save(null); else { setEditing(false); setError(''); } }}
        >
          {none ? '✓ Not marked' : (saving && (marked || safe) ? 'Removing…' : 'Not marked')}
        </button>
        <button
          type="button"
          aria-pressed={safe && !selecting}
          disabled={!canEdit || saving || (ownMalicious && !safe)}
          className={optClass(safe && !selecting, 'safe')}
          title={ownMalicious && !safe
            ? "This row's own address is classified Malicious, which outranks a row mark. Reclassify the address to mark this row Safe."
            : (safe ? 'Marked Safe (current)' : 'Mark this row Safe — set aside with the Safe IPs and never counted as the attacker’s. Its address keeps its own classification.')}
          onClick={() => { if (!safe || selecting) save({ verdict: 'safe' }); }}
        >
          {safe && !selecting ? '✓ Safe' : 'Safe'}
        </button>
        <button
          type="button"
          aria-pressed={marked || selecting}
          disabled={!canEdit || saving}
          className={optClass(marked || selecting, 'malicious')}
          title={marked ? 'Marked as attacker activity (current)' : 'Mark this row as attacker activity — you will be asked why'}
          onClick={() => { if (!marked) setEditing(true); }}
        >
          {marked || selecting ? '✓ Attacker' : 'Attacker'}
        </button>
        {marked && canEdit && !editing && (
          <button type="button" className={smallBtn} disabled={saving} onClick={() => { setReason(mark.reason); setEditing(true); }}>
            Edit reason
          </button>
        )}
      </span>

      {marked && !editing && (
        <div className="mt-1.5 px-2.5 py-1.5 rounded border bg-rose-900/20 border-rose-800/50" data-testid="analyst-mark">
          <span className="text-rose-200 font-medium">Marked as attacker activity</span>
          {mark.by ? <span className="text-rose-300/80"> by {mark.by}</span> : null}
          {when ? <span className="text-rose-300/80"> on {when} UTC</span> : null}
          <div className="mt-0.5 text-gray-200">Reason: {mark.reason}</div>
          <div className="mt-0.5 text-gray-500">
            Counted as the attacker&rsquo;s in the window and the reports. The address on this row keeps its own classification.
          </div>
        </div>
      )}

      {safe && !editing && (
        <div className="mt-1.5 px-2.5 py-1.5 rounded border bg-green-900/20 border-green-800/50" data-testid="analyst-safe-mark">
          <span className="text-green-200 font-medium">Marked Safe</span>
          {safeMark.by ? <span className="text-green-300/80"> by {safeMark.by}</span> : null}
          {safeWhen ? <span className="text-green-300/80"> on {safeWhen} UTC</span> : null}
          {safeMark.reason ? <div className="mt-0.5 text-gray-200">Reason: {safeMark.reason}</div> : null}
          <div className="mt-0.5 text-gray-500">
            Hidden with the Safe IPs (and so left out of the reports), and never counted as the attacker&rsquo;s. The address on this row keeps its own classification.
          </div>
        </div>
      )}

      {editing && (
        <div className="mt-1.5 px-2.5 py-1.5 rounded border border-gray-700 bg-gray-900/60">
          <label className="block text-gray-300 mb-1" htmlFor={`mark-reason-${ids[0]}`}>
            Why is this the attacker&rsquo;s? <span className="text-gray-500">(required — shown in the reports)</span>
          </label>
          <textarea
            id={`mark-reason-${ids[0]}`}
            className="input-field w-full text-xs"
            rows={2}
            maxLength={MARK_REASON_MAX}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={`e.g. ${MARK_REASON_EXAMPLE}`}
          />
          <div className="mt-1.5 flex items-center gap-1 flex-wrap">
            <button type="button" className={optClass(Boolean(trimmed), 'malicious')} disabled={!trimmed || saving} onClick={() => save(trimmed)}>
              {saving ? 'Saving…' : (marked ? 'Save reason' : 'Save mark')}
            </button>
            {/* The example is a reasonable reason for the case this exists for; one click uses
                it as the starting text, which the analyst can still edit before saving. */}
            <button type="button" className={smallBtn} disabled={saving} onClick={() => setReason(MARK_REASON_EXAMPLE)}>
              Use example
            </button>
            <button type="button" className={smallBtn} disabled={saving} onClick={() => { setEditing(false); setError(''); setReason(''); }}>
              Cancel
            </button>
            {ids.length > 1 && <span className="text-gray-500 ml-1">Applies to the {ids.length} records this row groups.</span>}
          </div>
        </div>
      )}
      {error && <div className="mt-1 text-red-300">{error}</div>}
    </div>
  );
}

// ── Multi-UPN identity (read-only in the timeline) ───────────────────────────
// A person's sign-ins and their mailbox activity are often logged under different
// addresses. The run resolves both and searches each; this context lets any row
// mark the events that came in under an alternate address, without threading the
// identity through every inner view (same reasoning as IpCommentsContext above).
const IdentityContext = React.createContext(null);

/** Small sky pill on events logged under one of this person's other addresses. */
function AliasIdentityBadge({ event, className = 'ml-2' }) {
  const identity = useContext(IdentityContext);
  if (!isAliasIdentityEvent(event, identity)) return null;
  return (
    <span
      className={`${className} inline-flex items-center px-1.5 py-0 rounded text-[9px] font-semibold border bg-sky-900/40 text-sky-300 border-sky-800/50`}
      title={`Logged under ${event.UserPrincipalName}, an alternate address for this user (their sign-in name is ${identity.PrimaryUpn}). Same directory account — resolved from Entra during this run.`}
    >
      alias identity
    </span>
  );
}

// ── Timezone definitions ─────────────────────────────────────────────────────

export const TIMEZONE_OPTIONS = [
  // 'Local' resolves to the browser's detected IANA zone at format time. Empty iana is the
  // sentinel for "detect at runtime"; resolveIana() handles it and other callers see a stable
  // IANA string. Listed first so any page that defaults to TIMEZONE_OPTIONS[0] gets Local.
  { id: 'Local', label: 'Local',    iana: '' },
  { id: 'UTC',   label: 'UTC',      iana: 'UTC' },
  { id: 'ET',    label: 'Eastern',  iana: 'America/New_York' },
  { id: 'CT',    label: 'Central',  iana: 'America/Chicago' },
  { id: 'MT',    label: 'Mountain', iana: 'America/Denver' },
  { id: 'AZ',    label: 'Arizona',  iana: 'America/Phoenix' },
  { id: 'PT',    label: 'Pacific',  iana: 'America/Los_Angeles' },
  { id: 'HI',    label: 'Hawaii',   iana: 'Pacific/Honolulu' },
];

/** Resolve an IANA name, treating empty/falsey as "browser-detected local zone". */
export function resolveIana(iana) {
  if (iana) return iana;
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
  catch { return 'UTC'; }
}

/** Get the current UTC offset string (e.g., "-5:00", "-4:00") for an IANA timezone. */
export function getTzOffset(iana, refDate = new Date()) {
  const resolved = resolveIana(iana);
  const fmt = new Intl.DateTimeFormat('en-US', { timeZone: resolved, timeZoneName: 'shortOffset' });
  const parts = fmt.formatToParts(refDate);
  const offsetPart = parts.find(p => p.type === 'timeZoneName');
  // offsetPart.value is like "GMT-5", "GMT-4", or just "GMT" for UTC
  const raw = offsetPart?.value || '';
  if (raw === 'GMT') return '0';
  const m = raw.match(/GMT([+-]?\d+(?::\d+)?)/);
  return m ? m[1] : '';
}

/** Get the short abbreviation (e.g., "EST", "EDT") for an IANA timezone at a given date. */
function getTzAbbr(iana, date) {
  const fmt = new Intl.DateTimeFormat('en-US', { timeZone: resolveIana(iana), timeZoneName: 'short' });
  const parts = fmt.formatToParts(date);
  return parts.find(p => p.type === 'timeZoneName')?.value || '';
}

/** Get the current short abbreviation for a tz ID (e.g., "ET" → "EST" or "EDT"). */
export function getTzLabel(tzId) {
  const tz = TIMEZONE_OPTIONS.find(t => t.id === tzId) || TIMEZONE_OPTIONS[0];
  return getTzAbbr(tz.iana, new Date());
}

export const TZ_STORAGE_KEY = 'm365signal-timezone';

export function getDefaultTimezone() {
  try {
    const stored = localStorage.getItem(TZ_STORAGE_KEY);
    if (stored && TIMEZONE_OPTIONS.find(tz => tz.id === stored)) return stored;
  } catch { /* ignore */ }
  return 'ET';
}

// ── Utility functions ────────────────────────────────────────────────────────

function formatTimestamp(ts, tzId) {
  if (!ts) return '\u2014';
  try {
    const d = parseISO(ts);
    const tz = TIMEZONE_OPTIONS.find(t => t.id === tzId) || TIMEZONE_OPTIONS[0];
    const resolved = resolveIana(tz.iana);
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: resolved,
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    });
    return fmt.format(d) + ' ' + getTzAbbr(resolved, d);
  } catch {
    return ts;
  }
}

export function formatTimestampShort(ts, tzId) {
  if (!ts) return '\u2014';
  try {
    const d = parseISO(ts);
    const tz = TIMEZONE_OPTIONS.find(t => t.id === tzId) || TIMEZONE_OPTIONS[0];
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: resolveIana(tz.iana),
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    });
    return fmt.format(d);
  } catch {
    return ts;
  }
}



/**
 * Parse browser name from UserAgent string.
 * Exported for SessionsTab, which needs the same labels the timeline shows.
 * (DevicesTab and IPAddressTable still carry their own near-copies — folding
 * those in is a separate change, since their outputs are load-bearing for the
 * device grouping key and the IP-table sort respectively.)
 */
export function parseBrowser(userAgent) {
  if (!userAgent) return 'Unknown';
  const ua = userAgent.toLowerCase();
  if (ua === 'bav2ropc') return 'Legacy Auth';
  if (ua.includes('teams/')) return 'Teams';
  if (ua.includes('outlook-ios') || ua.includes('outlook-android')) return 'Outlook Mobile';
  if (ua.includes('microsoft office')) return 'Office Desktop';
  if (ua.includes('edg/') || ua.includes('edge/')) return 'Edge';
  if (ua.includes('chrome/') && !ua.includes('edg/')) return 'Chrome';
  if (ua.includes('firefox/')) return 'Firefox';
  if (ua.includes('safari/') && !ua.includes('chrome/')) return 'Safari';
  if (ua.includes('python') || ua.includes('powershell')) return 'Script/API';
  return 'Other';
}

/**
 * Parse folder/file info from the Folders JSON string.
 */
function parseFolders(foldersStr) {
  if (!foldersStr) return null;
  try {
    return typeof foldersStr === 'string' ? JSON.parse(foldersStr) : foldersStr;
  } catch {
    return null;
  }
}

/**
 * Parse Parameters JSON string (e.g., inbox rule parameters).
 * Returns an array of {Name, Value} objects, or null.
 */
function parseParameters(paramsStr) {
  if (!paramsStr) return null;
  try {
    const parsed = typeof paramsStr === 'string' ? JSON.parse(paramsStr) : paramsStr;
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Get a specific parameter value from a Parameters array by name (case-insensitive).
 */
function getParam(params, name) {
  if (!params) return null;
  const p = params.find(p => p.Name?.toLowerCase() === name.toLowerCase() || p.name?.toLowerCase() === name.toLowerCase());
  return p ? (p.Value ?? p.value ?? null) : null;
}

/**
 * Compute a diff between two parsed parameter arrays (previous vs current).
 * Returns an array of diff entries for each parameter name.
 */
function computeParameterDiff(prevParams, currentParams) {
  if (!prevParams || !currentParams) return null;

  const prevMap = {};
  prevParams.forEach(p => { prevMap[(p.Name || p.name || '').toLowerCase()] = { name: p.Name || p.name, value: p.Value ?? p.value ?? '' }; });
  const currMap = {};
  currentParams.forEach(p => { currMap[(p.Name || p.name || '').toLowerCase()] = { name: p.Name || p.name, value: p.Value ?? p.value ?? '' }; });

  const allKeys = new Set([...Object.keys(prevMap), ...Object.keys(currMap)]);
  const diffs = [];

  for (const key of allKeys) {
    const prev = prevMap[key];
    const curr = currMap[key];
    const displayName = (curr || prev).name;
    const prevVal = prev ? String(prev.value) : '';
    const currVal = curr ? String(curr.value) : '';

    if (prevVal === currVal) {
      diffs.push({ name: displayName, type: 'unchanged', current: currVal });
      continue;
    }

    if (!prev) {
      diffs.push({ name: displayName, type: 'added_param', current: currVal });
      continue;
    }
    if (!curr) {
      diffs.push({ name: displayName, type: 'removed_param', previous: prevVal });
      continue;
    }

    // Check if semicolon-delimited list (e.g., BlockedSendersAndDomains)
    const isList = prevVal.includes(';') || currVal.includes(';');
    if (isList) {
      const prevSet = new Set(prevVal.split(';').map(s => s.trim().toLowerCase()).filter(Boolean));
      const currSet = new Set(currVal.split(';').map(s => s.trim().toLowerCase()).filter(Boolean));
      // Preserve original casing from current for added, from previous for removed
      const currItems = currVal.split(';').map(s => s.trim()).filter(Boolean);
      const prevItems = prevVal.split(';').map(s => s.trim()).filter(Boolean);
      const added = currItems.filter(x => !prevSet.has(x.toLowerCase()));
      const removed = prevItems.filter(x => !currSet.has(x.toLowerCase()));
      const unchangedCount = [...currSet].filter(x => prevSet.has(x)).length;
      diffs.push({ name: displayName, type: 'list', added, removed, unchangedCount });
    } else {
      diffs.push({ name: displayName, type: 'scalar', previous: prevVal, current: currVal });
    }
  }

  return diffs;
}

/**
 * Summarize parameter diff for description line (e.g., "+3 blocked, -1 trusted").
 */
function summarizeParamDiff(prevParams, currentParams) {
  const diff = computeParameterDiff(prevParams, currentParams);
  if (!diff) return '';
  const parts = [];
  for (const d of diff) {
    if (d.type === 'list' && (d.added.length > 0 || d.removed.length > 0)) {
      // Shorten the parameter name for display
      const shortName = d.name.replace(/SendersAndDomains$/, '').replace(/RecipientsAndDomains$/, 'Recip').replace(/^Blocked/, 'blocked').replace(/^Trusted/, 'trusted');
      const changes = [];
      if (d.added.length > 0) changes.push(`+${d.added.length}`);
      if (d.removed.length > 0) changes.push(`-${d.removed.length}`);
      parts.push(`${changes.join('/')} ${shortName}`);
    } else if (d.type === 'scalar') {
      parts.push(`${d.name}: ${d.previous} → ${d.current}`);
    }
  }
  return parts.length > 0 ? ` (${parts.join(', ')})` : '';
}

/**
 * Build a short summary of inbox rule parameters for the description line.
 */
function summarizeInboxRule(params) {
  if (!params) return '';
  const parts = [];
  const name = getParam(params, 'Name');
  if (name) parts.push(`"${name}"`);
  const forwardTo = getParam(params, 'ForwardTo');
  const forwardAsAttachment = getParam(params, 'ForwardAsAttachmentTo');
  const redirectTo = getParam(params, 'RedirectTo');
  const deleteMessage = getParam(params, 'DeleteMessage');
  const moveToFolder = getParam(params, 'MoveToFolder');
  const markAsRead = getParam(params, 'MarkAsRead');
  if (forwardTo) parts.push(`forward to ${forwardTo}`);
  if (forwardAsAttachment) parts.push(`forward as attachment to ${forwardAsAttachment}`);
  if (redirectTo) parts.push(`redirect to ${redirectTo}`);
  if (deleteMessage === 'True' || deleteMessage === true) parts.push('delete message');
  if (moveToFolder) parts.push(`move to ${moveToFolder}`);
  if (markAsRead === 'True' || markAsRead === true) parts.push('mark as read');
  return parts.length > 0 ? ` — ${parts.join(', ')}` : '';
}

/**
 * Determine MailItemsAccessed access type: 'Sync', 'Read', 'AppAccess', or null.
 * - Sync: MailAccessType=Sync or ClientInfo Action contains 'sync'
 * - Read: Bind + user-interactive client (OWA with user action)
 * - AppAccess: Bind + service/app client (OutlookService, REST, MSExchangeRPC, ActiveSync, etc.)
 */
function getMailAccessType(event) {
  const mat = event.MailAccessType || event.mailAccessType;
  const clientInfo = event.ClientInfo || '';

  // Check for Sync first (explicit field or ClientInfo action)
  if (mat && mat.toLowerCase() === 'sync') return 'Sync';
  if (!mat) {
    const actionMatch = clientInfo.match(/Action=([^;]+)/i);
    if (actionMatch && actionMatch[1].toLowerCase().includes('sync')) return 'Sync';
  }

  // For Bind (or unknown), distinguish user-interactive vs app/service access
  if (mat && mat.toLowerCase() === 'bind') {
    // Service/app clients — background access, not user-initiated reading
    const serviceClients = /Client=(OutlookService|REST|MSExchangeRPC|ActiveSync|EWS|IMAP|POP)/i;
    if (serviceClients.test(clientInfo)) return 'AppAccess';
    return 'Read';
  }

  // No MailAccessType — try to infer from ClientInfo
  if (clientInfo) {
    const serviceClients = /Client=(OutlookService|REST|MSExchangeRPC|ActiveSync|EWS|IMAP|POP)/i;
    if (serviceClients.test(clientInfo)) return 'AppAccess';
    if (/Client=OWA/i.test(clientInfo)) return 'Read';
  }

  return null;
}

/**
 * Generate a human-readable description for an event.
 */
function generateDescription(event, revealSubjects = false) {
  // A row produced by a story roll-up describes itself. ONE shared helper, because these
  // two copies of generateDescription have diverged before.
  const rolled = describeRolledRow(event);
  if (rolled) return rolled;

  const upn = event.UserPrincipalName || 'Unknown user';

  if (event._eventType === 'SignIn') {
    const op = event.Operation || 'SignIn';
    const app = signInApplicationLabel(event);
    const os = event.DeviceOS || 'Unknown';
    const browser = parseBrowser(event.UserAgent);
    const failed = event.ResultStatus && event.ResultStatus.toLowerCase() === 'failed' ? ' [FAILED]' : '';
    return `${op} for ${upn} on '${app}' (${os} / ${browser})${failed}`;
  }

  // Non-interactive sign-in activity: ONE ROW IS N AUTHENTICATIONS over a span within a
  // single hour, never a single event. Say so explicitly -- a reader who takes it for one
  // event will under-count token reuse by an order of magnitude.
  //
  // Outcome is stated because success-vs-failure is the CONTAINMENT signal in this feed
  // (on the reference case nothing succeeded after 2026-08-03, which is the point access
  // was cut off). Failure codes are named when present -- 50173 is a refresh-token
  // rejection, i.e. the revoke biting.
  //
  // Deliberately NOT said: anything attributing the activity to the attacker. The row
  // carries a session id, and a stolen token keeps the VICTIM's session id, so a shared
  // session is corroboration only. Attribution comes from the IP classification the
  // analyst applies, which colours the row through the normal mechanism.
  if (event._eventType === 'NonInteractiveSignIn') {
    // Delegates to the ONE shared helper (see describeNonInteractive) so a rolled row
    // cannot describe itself one way on screen and another in a report.
    return describeNonInteractive(event);
  }

  if (event._eventType === 'MessageTrace') {
    // Delegates to the ONE shared helper (see describeMessageTrace). Note the inverted
    // reveal polarity between the two copies -- hence the explicit argument.
    return describeMessageTrace(event, revealSubjects, maskSubject);
  }

  if (event._eventType === 'UrlClick') {
    const action = event.ActionType || 'clicked';
    const url = event.Url || 'unknown URL';
    const truncUrl = url.length > 80 ? url.substring(0, 77) + '...' : url;
    const threat = event.ThreatType ? ` (${event.ThreatType})` : '';
    return `${action}: ${truncUrl}${threat}`;
  }

  if (event._eventType === 'AlertEvidence') {
    const url = event._alertUrl || getSecurityAlertUrl(event);
    if (url) {
      const truncUrl = url.length > 90 ? url.substring(0, 87) + '...' : url;
      return `Clicked suspicious URL: ${truncUrl}`;
    }
    return `User activity detected by MSFT Defender`;
  }

  if (event._eventType === 'SecurityAlert') {
    const title = event.Title || event.Operation || 'Security Alert';
    const severity = event.Severity ? `[${event.Severity.toUpperCase()}]` : '';
    const evidence = getSecurityAlertSummary(event);
    const truncEvidence = evidence && evidence.length > 80 ? evidence.substring(0, 77) + '...' : evidence;
    const parts = [severity, title];
    if (truncEvidence) parts.push(`— ${truncEvidence}`);
    return parts.filter(Boolean).join(' ');
  }

  if (event._eventType === CASE_ALERT_EVENT_TYPE) return describeCaseAlert(event);

  if (event._eventType === 'SecurityIncident') {
    const name = event.DisplayName || event.Operation || 'Security Incident';
    const severity = event.Severity ? `[${event.Severity.toUpperCase()}]` : '';
    return [severity, name].filter(Boolean).join(' ');
  }

  // Directory Audit event — enrich description with target resource name + detail
  if (event._eventType === 'DirectoryAudit') {
    const op = event.Operation || 'unknown operation';
    const opLower = op.toLowerCase();
    const target = dirAuditTargetLabel(event);
    // Surface the specific action (e.g. "User registered Passkey") from
    // RawLog modifiedProperties / resultReason — this is what distinguishes
    // otherwise identical-looking "User registered security info" rows.
    let detail = getDirAuditDetail(event);
    // Drop detail that merely repeats the operation name (generic summary rows)
    // or restates the passkey action ("passkey (FIDO2)" on a platform-credential op).
    if (detail && (detail.trim().toLowerCase() === opLower || detail.trim().toLowerCase() === 'passkey (fido2)')) detail = '';

    // "by <initiator>" — name who performed the action, but ONLY when initiated
    // by someone other than the account it acted on (admin/attacker, not self-
    // service). `upn` is the initiator (runbook resolves object-id-only ones).
    const initId = (event.RawLog?.initiatedBy?.user?.id || '').toLowerCase();
    const initUpn = (event.RawLog?.initiatedBy?.user?.userPrincipalName || '').toLowerCase();
    const tgtId = (event.RawLog?.targetResources?.[0]?.id || '').toLowerCase();
    const tgtUpn = (event.RawLog?.targetResources?.[0]?.userPrincipalName || '').toLowerCase();
    const adminInitiated = (initId && tgtId && initId !== tgtId) || (initUpn && tgtUpn && initUpn !== tgtUpn);
    const by = (adminInitiated && upn && upn !== 'Unknown user') ? ` by ${upn}` : '';

    // Device registration / removal. Delegates to the ONE shared helper (see
    // describeDeviceRegistration) so this copy and eventUtils' cannot drift. Falls
    // through to the wording below when the row names no device.
    const deviceDesc = describeDeviceRegistration(event, target);
    if (deviceDesc) return deviceDesc;

    // Passkey / FIDO platform credential — Microsoft often logs the initiator as
    // an object-id only; the runbook resolves it so `by` can name the actor.
    if (opLower.includes('platform credential')) {
      const verb = opLower.startsWith('delete') ? 'Passkey removed' : 'Passkey registered';
      const dev = detail ? ` — ${detail}` : '';
      const forWhom = target ? ` for '${target}'` : '';
      return `${verb}${by}${forWhom}${dev}`;
    }

    if (detail) {
      if (opLower.startsWith('update user')) {
        return target ? `MFA device updated on '${target}'${by} — ${detail}` : `MFA device updated${by} — ${detail}`;
      }
      if (opLower.includes('deleted security info')) {
        return target ? `Security info removed${by} from '${target}' — ${detail}` : `Security info removed${by} — ${detail}`;
      }
      if (opLower.includes('registered security info')) {
        return target ? `Security info registered on '${target}'${by} — ${detail}` : `Security info registered${by} — ${detail}`;
      }
      if (opLower === 'disable account') {
        return target ? `Account disabled: '${target}'${by} — ${detail}` : `Account disabled${by} — ${detail}`;
      }
      if (opLower === 'enable account') {
        return target ? `Account enabled: '${target}'${by} — ${detail}` : `Account enabled${by} — ${detail}`;
      }
    }
    if (opLower.includes('started security info registration')) {
      return target ? `Security info registration started on '${target}'${by}` : `Security info registration started${by}`;
    }
    if (opLower.includes('changed default security info')) {
      return target ? `Default security info changed on '${target}'${by}` : `Default security info changed${by}`;
    }

    // Omit the "X performed" prefix when no actor is known (service-initiated).
    const hasActor = upn && upn !== 'Unknown user';
    const prefix = hasActor ? `${upn} performed ` : '';
    if (detail) {
      const base = target ? `${prefix}${op} on '${target}'` : `${prefix}${op}`;
      return `${base} — ${detail}`;
    }
    if (target) {
      return `${prefix}${op} '${target}'`;
    }
    return prefix ? `${prefix}${op}` : op;
  }

  // Exchange / SharePoint / Azure Activity event
  const rawOp = event.Operation || '';
  const op = (typeof rawOp === 'object') ? (rawOp.localizedValue || rawOp.value || '') : rawOp;
  const folders = parseFolders(event.Folders);
  const params = parseParameters(event.Parameters);

  // Use app name as actor when an enterprise app performed the action (e.g., "Druva M365 Advanced")
  // Falls back to UPN for user-driven actions (UPN gets stripped later by enrichment logic)
  const actor = event.ClientAppName || upn;

  switch (op) {
    case 'MailItemsAccessed': {
      const path = folders?.Path || '\\Inbox';
      const items = folders?.FolderItems;
      const accessType = getMailAccessType(event);
      const accessLabel = accessType === 'Sync' ? 'synced' : accessType === 'Read' ? 'read' : accessType === 'AppAccess' ? 'app-accessed' : 'accessed';
      if (items && items.length > 0) {
        const subjects = items.map(i => i.Subject).filter(Boolean);
        if (subjects.length === 1) return `${actor} ${accessLabel} '${maskSubject(subjects[0], revealSubjects)}' in ${path}`;
        return `${actor} ${accessLabel} ${subjects.length} mail items in ${path}`;
      }
      return `${actor} ${accessLabel} mail items in ${path}`;
    }
    case 'New-InboxRule':
      return `${actor} created new inbox rule${summarizeInboxRule(params)}`;
    case 'Set-InboxRule':
      return `${actor} modified inbox rule${summarizeInboxRule(params)}`;
    case 'Remove-InboxRule':
      return `${actor} removed inbox rule${summarizeInboxRule(params)}`;
    case 'MoveToDeletedItems':
      return describeDeletedMailItems(actor, 'moved', folders, revealSubjects, ' to Deleted Items')
        || `${actor} moved items to Deleted Items`;
    case 'SoftDelete':
      return describeDeletedMailItems(actor, 'soft-deleted', folders, revealSubjects)
        || `${actor} soft-deleted mail items`;
    case 'HardDelete':
      return describeDeletedMailItems(actor, 'permanently deleted', folders, revealSubjects)
        || `${actor} permanently deleted mail items`;
    case 'Send':
    case 'SendAs':
    case 'SendOnBehalf':
      return `${actor} sent email`;
    case 'Update':
    case 'Create':
      // Same shared helper as the eventUtils copy, with THIS copy's polarity:
      // revealSubjects is already "reveal", where eventUtils passes !maskSubjects.
      return describeExchangeItemChange(event, revealSubjects, upn)
        || `${actor} performed ${op}`;
    case 'Release-QuarantineMessage':
    case 'QuarantineReleaseMessage':
      // Same shared helper as the eventUtils copy, with THIS copy's polarity.
      return describeQuarantineRelease(event, revealSubjects, maskSubject)
        || `${actor} performed ${op}`;
    case 'FileAccessed':
    case 'FilePreviewed': {
      // File names masked with the same gate as subjects (v1.151.0) — see the
      // eventUtils copy for why these were a leak.
      const fileName = maskFileName(folders?.FileName, revealSubjects) || 'unknown file';
      const site = folders?.Path || '';
      const verb = op === 'FilePreviewed' ? 'previewed' : 'accessed';
      return site
        ? `${actor} ${verb} '${fileName}' on ${site}`
        : `${actor} ${verb} '${fileName}'`;
    }
    case 'FileDownloaded': {
      const fileName = maskFileName(folders?.FileName, revealSubjects) || 'unknown file';
      return `${actor} downloaded '${fileName}'`;
    }
    case 'FileModified':
    case 'FileModifiedExtended': {
      const fileName = maskFileName(folders?.FileName, revealSubjects) || 'unknown file';
      return `${actor} modified '${fileName}'`;
    }
    case 'FileUploaded': {
      const fileName = maskFileName(folders?.FileName, revealSubjects) || 'unknown file';
      return `${actor} uploaded '${fileName}'`;
    }
    case 'FileMoved':
    case 'FileRenamed':
    case 'FileDeleted':
    case 'FileRecycled': {
      const fileName = maskFileName(folders?.FileName, revealSubjects) || 'unknown file';
      const verb = op.replace('File', '').toLowerCase();
      return `${actor} ${verb} '${fileName}'`;
    }
    case 'ListViewed':
    case 'ListUpdated': {
      const path = folders?.Path || '';
      const listName = path.split('/').pop() || path || 'unknown list';
      const verb = op === 'ListViewed' ? 'viewed' : 'updated';
      return `${actor} ${verb} list '${listName}'`;
    }
    default: {
      // For Set-* operations with a previous baseline, append a diff summary
      if (op.startsWith('Set-') && event.PreviousParameters) {
        const prevParams = parseParameters(event.PreviousParameters);
        if (prevParams) {
          const diffSummary = summarizeParamDiff(prevParams, params);
          return `${actor} performed ${op}${diffSummary}`;
        }
      }
      return `${actor} performed ${op || 'unknown operation'}`;
    }
  }
}

/**
 * Determine event type category for an exchange event based on its Operation.
 */
// Per-case email-subject (and file-name) reveal control. On All Events it is ONE button
// in the table toolbar; the Message Trace and Related Recipients tabs keep their own.
// Clicking reveals/hides every subject in the case; the reveal is recorded once in the
// activity log (handler in CaseDetail). `size="toolbar"` matches the toolbar buttons.
function SubjectRevealButton({ revealSubjects, onToggle, className = '', size = 'inline' }) {
  if (!onToggle) return null;
  const sizing = size === 'toolbar' ? 'text-xs px-2.5 py-1.5 gap-1.5' : 'text-[11px] px-2 py-0.5 gap-1';
  return (
    <button
      type="button"
      data-testid="subject-reveal"
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      className={`inline-flex items-center ${sizing} rounded border transition-colors shrink-0 ${
        revealSubjects
          ? 'bg-amber-900/40 border-amber-600 text-amber-300'
          : 'bg-amber-950/30 border-amber-700/60 text-amber-300/90 hover:bg-amber-900/30 hover:text-amber-200'
      } ${className}`}
      title={revealSubjects
        ? 'Re-mask email subjects for this case'
        : 'Email subjects can contain PHI/PII and are hidden. Click to reveal them for this case — the reveal is recorded in the activity log.'}
    >
      {revealSubjects
        ? <EyeSlashIcon className={size === 'toolbar' ? 'h-4 w-4' : 'h-3 w-3'} />
        : <EyeIcon className={size === 'toolbar' ? 'h-4 w-4' : 'h-3 w-3'} />}
      {revealSubjects ? 'Hide subjects' : 'Reveal subjects'}
    </button>
  );
}

function getExchangeCategory(operation) {
  const fileOps = [
    'FileAccessed', 'FilePreviewed', 'FileDownloaded', 'FileModified',
    'FileModifiedExtended', 'FileUploaded', 'FileMoved', 'FileRenamed',
    'FileDeleted', 'FileRecycled', 'FileCopied', 'FileCheckedOut',
    'FileCheckedIn',
  ];
  const listOps = ['ListViewed', 'ListUpdated', 'ListColumnCreated', 'ListColumnUpdated'];

  if (fileOps.includes(operation)) return 'SharePoint';
  if (listOps.includes(operation)) return 'SharePointList';
  return 'Exchange';
}

/**
 * Get VPN/Proxy flags for an event, cross-referencing IP data for exchange events.
 */
function getVPNProxyFlags(event, ipLookup) {
  if (event._eventType === 'SignIn') {
    return {
      isVPN: event.IsVPN || event.RiskFactors?.IsVPN || false,
      isProxy: event.IsProxy || event.RiskFactors?.IsProxy || false,
      isTOR: event.IsTOR || event.RiskFactors?.IsTOR || false,
      isCompromised: event.IsCompromised || event.RiskFactors?.IsCompromised || false,
      isAnonymous: event.IsAnonymous || event.RiskFactors?.IsAnonymous || false,
    };
  }
  // For exchange events, look up from IP data
  const ipInfo = ipLookup[event.IPAddress];
  if (ipInfo) {
    return {
      isVPN: ipInfo.IsVPN || false,
      isProxy: ipInfo.IsProxy || false,
      isTOR: ipInfo.IsTOR || false,
      isCompromised: ipInfo.IsCompromised || false,
      isAnonymous: ipInfo.IsAnonymous || false,
    };
  }
  return { isVPN: false, isProxy: false, isTOR: false, isCompromised: false, isAnonymous: false };
}

/**
 * Get country for an event, cross-referencing IP data for exchange events.
 */
function getCountry(event, ipLookup) {
  if (event._eventType === 'SignIn') {
    return event.Location?.Country || event.IP_Country || '';
  }
  const ipInfo = ipLookup[event.IPAddress];
  if (ipInfo?.Country) return ipInfo.Country;
  // Fallback to inferred IP for events with no actual IP
  if (!event.IPAddress && event.InferredIP) {
    const inferredInfo = ipLookup[event.InferredIP];
    return inferredInfo?.Country || '';
  }
  return '';
}

// SharePoint file operations set
const FILE_OPERATIONS = new Set([
  'FileAccessed', 'FilePreviewed', 'FileDownloaded', 'FileModified',
  'FileModifiedExtended', 'FileUploaded', 'FileMoved', 'FileRenamed',
  'FileDeleted', 'FileRecycled', 'FileCopied', 'FileCheckedOut', 'FileCheckedIn',
]);

// SharePoint list operations set
const LIST_OPERATIONS = new Set([
  'ListViewed', 'ListUpdated', 'ListColumnCreated', 'ListColumnUpdated',
]);

// ── Sort helpers ─────────────────────────────────────────────────────────────

function getStatusClass(status) {
  switch (status?.toLowerCase()) {
    case 'success':
    case 'succeeded':
      return 'text-green-400';
    case 'failure':
    case 'failed':
      return 'text-red-400';
    case 'interrupted':
      return 'text-amber-400';
    default:
      return 'text-gray-400';
  }
}

function getTypeBadge(eventType) {
  switch (eventType) {
    case 'SignIn':
      return { label: 'Sign-In', classes: 'bg-blue-900/60 text-blue-300 border-blue-700/50' };
    case 'Exchange':
      return { label: 'Exchange', classes: 'bg-purple-900/60 text-purple-300 border-purple-700/50' };
    case 'SharePoint':
      return { label: 'SharePoint', classes: 'bg-emerald-900/60 text-emerald-300 border-emerald-700/50' };
    case 'SharePointList':
      return { label: 'SP List', classes: 'bg-teal-900/60 text-teal-300 border-teal-700/50' };
    case 'DirectoryAudit':
      return { label: 'Dir Audit', classes: 'bg-indigo-900/60 text-indigo-300 border-indigo-700/50' };
    case 'MessageTrace':
      return { label: 'Msg Trace', classes: 'bg-amber-900/60 text-amber-300 border-amber-700/50' };
    case 'UrlClick':
      return { label: 'URL Click', classes: 'bg-rose-900/60 text-rose-300 border-rose-700/50' };
    case 'RiskDetection':
      return { label: 'Risk Det.', classes: 'bg-red-900/60 text-red-300 border-red-700/50' };
    case 'RiskyUser':
      return { label: 'Risky User', classes: 'bg-red-800/60 text-red-200 border-red-600/50' };
    case 'DLP':
      return { label: 'DLP', classes: 'bg-orange-900/60 text-orange-300 border-orange-700/50' };
    case 'General':
      return { label: 'General', classes: 'bg-gray-800/60 text-gray-300 border-gray-600/50' };
    case 'AzureActivity':
      return { label: 'Azure', classes: 'bg-sky-900/60 text-sky-300 border-sky-700/50' };
    case 'AlertEvidence':
      return { label: '\u26A0 Evidence', classes: 'bg-red-900/40 text-red-300 border-red-700/30 italic' };
    case 'SecurityAlert':
      return { label: 'Alert', classes: 'bg-red-900/80 text-red-200 border-red-600/50' };
    case 'SecurityIncident':
      return { label: 'Incident', classes: 'bg-rose-900/80 text-rose-200 border-rose-600/50' };
    // An alert the PORTAL raised (DetectionAlerts), as opposed to 'Alert' above, which is
    // a Microsoft Defender alert from the run blob.
    case CASE_ALERT_EVENT_TYPE:
      return { label: CASE_ALERT_LABEL, classes: 'bg-fuchsia-900/70 text-fuchsia-200 border-fuchsia-600/50' };
    case 'SecureScore':
      return { label: 'Score', classes: 'bg-lime-900/60 text-lime-300 border-lime-700/50' };
    // A non-interactive sign-in IS token use -- an existing token redeemed without the
    // user authenticating again. Labelled explicitly because it used to fall through to
    // the default badge and print the raw tag 'NonInteractiveSignIn'. The same label is
    // used in the reports (storyGenerator typeBadge) so the two read alike.
    case 'NonInteractiveSignIn':
      return { label: 'Token Use', classes: 'bg-cyan-900/60 text-cyan-300 border-cyan-700/50' };
    default:
      return { label: eventType, classes: 'bg-gray-700/60 text-gray-400 border-gray-600/50' };
  }
}

// ── Attacker Window Panel ────────────────────────────────────────────────────

function AttackerWindowPanel({
  attackerWindow,
  compromiseScope = 'access_confirmed',
  postWindowFilteredCount,
  hidePostWindow,
  onTogglePostWindow,
  selectedTz,
  // Pre-compromise session filter (added v1.53.0). compromisedSessions is a
  // Map<sessionId, ISO compromise-start ts>. Empty when no IPs classified Malicious.
  compromisedSessions,
  hidePreCompromise,
  onTogglePreCompromise,
  preCompromiseFilteredCount,
  // Safe-session classification + bulk list (added v1.53.2)
  sessionClassifications = {},
  safeSessionSet = new Set(),
  onCycleSessionStatus,
  otherSessionsSummary = [],
}) {
  const [expanded, setExpanded] = useState(false);
  const aw = attackerWindow;
  // v1.182.0 — under the "no attacker access found" attestation the window is a window of
  // EXPOSURE (the captured session could have been used), not confirmed attacker access.
  const noAccess = compromiseScope === 'credentials_captured_no_access_found';

  return (
    <div className="mb-4 border border-purple-700/40 rounded-lg bg-purple-900/10 overflow-hidden">
      {/* Collapsed bar */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-purple-900/20 transition-colors"
      >
        <ClockIcon className="h-4 w-4 text-purple-400 flex-shrink-0" />
        <span className="text-xs font-medium text-purple-300">{noAccess ? 'Exposure Window' : 'Attacker Window'}</span>
        <span className="text-xs text-gray-400">
          {/* "Successful" would contradict "refused every attempt" in the same sentence. */}
          {!aw.noTokenIssued && (
            <>{aw.successfulAttackerSignInCount} successful sign-in{aw.successfulAttackerSignInCount !== 1 ? 's' : ''}</>
          )}
          {!aw.noTokenIssued && aw.attackerSignInCount > aw.successfulAttackerSignInCount && (
            <span className="text-gray-500"> ({aw.attackerSignInCount - aw.successfulAttackerSignInCount} failed)</span>
          )}
          {aw.noTokenIssued && !aw.anchoredByPhishingKit ? (
            // v1.194.7: the same fact for ANY window, not only a kit-anchored one. The relay
            // addresses classified malicious used to switch this off, and the panel then counted
            // the kit's MFA steps as "successful sign-ins" (attackerAccess.js).
            <span className="text-amber-300">
              {aw.attackerAccess?.attemptCount ?? 0} sign-in attempt{(aw.attackerAccess?.attemptCount ?? 0) !== 1 ? 's' : ''} from {aw.maliciousIPs.length} malicious IP{aw.maliciousIPs.length !== 1 ? 's' : ''}; every attempt was refused (no token issued)
            </span>
          ) : aw.anchoredByPhishingKit && aw.noTokenIssued ? (
            // v1.194.2: every kit row is an MFA step of an attempt Conditional Access REFUSED, and
            // no sign-in from the kit's addresses issued a token. "Successful sign-ins" and a token
            // lifetime would both claim access that never happened (IR-20261013-levig-001).
            <span className="text-amber-300">
              Anchored on {aw.successfulPhishingKitLegCount} phishing-kit sign-in step{aw.successfulPhishingKitLegCount !== 1 ? 's' : ''}; Conditional Access refused every attempt (no token issued)
            </span>
          ) : aw.anchoredByPhishingKit ? (
            // v1.181.0: the window is anchored on the phishing-kit landing leg(s) — say so rather
            // than printing "from 0 malicious IPs".
            <span className="text-amber-300">
              {' '}anchored on {aw.successfulPhishingKitLegCount} phishing-kit sign-in{aw.successfulPhishingKitLegCount !== 1 ? 's' : ''} (no IP classified malicious)
            </span>
          ) : (
            <>{' '}from {aw.maliciousIPs.length} malicious IP{aw.maliciousIPs.length !== 1 ? 's' : ''}</>
          )}
        </span>
        <span className="text-xs text-gray-500 mx-1">|</span>
        <span className="text-xs text-gray-400">
          Cutoff: <span className="text-purple-300">{formatTimestamp(aw.globalCutoff, selectedTz)}</span>
        </span>
        <span className="text-xs text-gray-500 mx-1">|</span>
        <span className="text-xs text-gray-400">
          {aw.noTokenIssued
            ? <span title={`The cutoff still allows a ${aw.tokenLifetimeUsed}-minute token lifetime after the last attempt, so later activity stays in view. No token was issued.`}>assumes {aw.tokenLifetimeUsed} min token (none issued)</span>
            : <>{aw.tokenLifetimeUsed} min token lifetime</>}
        </span>
        {aw.multiEpisode && (
          <>
            <span className="text-xs text-gray-500 mx-1">|</span>
            <span className="text-xs text-amber-300">{aw.episodeCount} episodes (not continuous)</span>
          </>
        )}
        {postWindowFilteredCount > 0 && (
          <>
            <span className="text-xs text-gray-500 mx-1">|</span>
            <span
              role="button"
              onClick={(e) => { e.stopPropagation(); onTogglePostWindow && onTogglePostWindow(); }}
              className={`text-xs px-1.5 py-0.5 rounded-full cursor-pointer hover:opacity-80 transition-opacity ${
                hidePostWindow
                  ? 'bg-purple-800/60 text-purple-200'
                  : 'bg-gray-700 text-gray-400'
              }`}
              title={hidePostWindow ? 'Show post-window events' : 'Hide post-window events'}
            >
              {postWindowFilteredCount} post-window
            </span>
          </>
        )}
        {preCompromiseFilteredCount > 0 && (
          <>
            <span className="text-xs text-gray-500 mx-1">|</span>
            <span
              role="button"
              onClick={(e) => { e.stopPropagation(); onTogglePreCompromise && onTogglePreCompromise(); }}
              className={`text-xs px-1.5 py-0.5 rounded-full cursor-pointer hover:opacity-80 transition-opacity ${
                hidePreCompromise
                  ? 'bg-purple-800/60 text-purple-200'
                  : 'bg-gray-700 text-gray-400'
              }`}
              title={hidePreCompromise
                ? 'Show pre-compromise session activity (legitimate prior use of compromised sessions)'
                : 'Hide pre-compromise session activity'}
            >
              {preCompromiseFilteredCount} pre-compromise
            </span>
          </>
        )}
        <ChevronDownIcon className={`h-3.5 w-3.5 text-gray-500 ml-auto flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-purple-800/30">
          {noAccess && (
            <div className="mb-3 text-xs text-amber-300 bg-amber-900/10 border border-amber-700/30 rounded px-2.5 py-1.5">
              Recorded as <strong>credentials/session captured, no attacker access found</strong>. This is the
              window during which the captured session could have been used; the reviewed activity shows no
              attacker access to the account.
            </div>
          )}
          {aw.multiEpisode && (
            <div className="mb-3 text-xs">
              <span className="text-gray-500 block mb-1">Compromise episodes ({aw.episodeCount}) — activity was NOT continuous between them</span>
              <div className="space-y-1">
                {aw.episodes.map((ep, i) => {
                  const phrase = ep.containmentType === 'account_disabled' ? 'account disabled'
                    : ep.containmentType === 'password_reset' ? 'password reset'
                      : 'sessions revoked';
                  return (
                    <div key={i} className="text-gray-300">
                      <span className="text-gray-500">{i + 1}.</span>{' '}
                      {formatTimestamp(ep.start, selectedTz)}
                      {ep.contained
                        ? <span className="text-gray-400"> — {phrase} {formatTimestamp(ep.containedAt, selectedTz)}</span>
                        : <span className="text-amber-300"> — no containment action recorded</span>}
                    </div>
                  );
                })}
              </div>
              <div className="text-gray-500 mt-1">The window dates and cutoff below span all episodes.</div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-xs">
            {/* Malicious IPs */}
            <div>
              <span className="text-gray-500 block mb-1">Malicious IPs</span>
              <div className="flex flex-wrap gap-1.5">
                {aw.maliciousIPs.length === 0 && aw.anchoredByPhishingKit && (
                  // v1.194.2: no longer claims the kit sits on the victim's own address -- on
                  // IR-20261013-levig-001 its addresses were the attacker's proxies. Which it is
                  // is the analyst's call, from the IP Addresses tab.
                  <span className="text-amber-300 text-xs">
                    {aw.noTokenIssued
                      ? 'None classified — the window is anchored on the phishing-kit sign-in steps. Conditional Access refused every attempt and no token was issued, so this window shows when the kit was active, not attacker access to the account. Classify the kit’s addresses on the IP Addresses tab.'
                      : 'None classified — the window is anchored on the phishing-kit sign-in. Classify its address on the IP Addresses tab (it can be the victim’s own, relayed through the kit, or the kit’s own).'}
                  </span>
                )}
                {aw.maliciousIPs.map(ip => (
                  <span key={ip} className="font-mono text-red-400 bg-red-900/30 px-1.5 py-0.5 rounded border border-red-700/30">
                    {ip}
                  </span>
                ))}
              </div>
            </div>

            {/* Cutoff method */}
            <div>
              <span className="text-gray-500 block mb-1">Cutoff Method</span>
              <span className="text-gray-300">{aw.cutoffMethod}</span>
              {aw.tokenLifetimeUsed !== 90 && (
                <span className="text-purple-400 ml-2">(non-default token lifetime)</span>
              )}
            </div>

            {/* Attacker activity window */}
            {aw.firstAttackerActivity && (
              <div>
                <span className="text-gray-500 block mb-1">First Attacker Activity</span>
                <span className="text-gray-300">{formatTimestamp(aw.firstAttackerActivity, selectedTz)}</span>
                {aw.firstAttackerIP && <span className="text-red-400 ml-2 font-mono text-xs">{aw.firstAttackerIP}</span>}
              </div>
            )}
            {aw.lastAttackerActivity && (
              <div>
                <span className="text-gray-500 block mb-1">Last Attacker Activity</span>
                <span className="text-gray-300">{formatTimestamp(aw.lastAttackerActivity, selectedTz)}</span>
                {aw.lastAttackerIP && <span className="text-red-400 ml-2 font-mono text-xs">{aw.lastAttackerIP}</span>}
              </div>
            )}

            {/* Remediation events */}
            {aw.accountDisabledAt && (
              <div>
                <span className="text-gray-500 block mb-1">Account Disabled</span>
                <span className="text-green-400">{formatTimestamp(aw.accountDisabledAt, selectedTz)}</span>
                {aw.accountDisabledBy && <span className="text-gray-500 ml-2">by {aw.accountDisabledBy}</span>}
              </div>
            )}
            {aw.tokensRevokedAt && (
              <div>
                <span className="text-gray-500 block mb-1">Tokens Revoked</span>
                <span className="text-green-400">{formatTimestamp(aw.tokensRevokedAt, selectedTz)}</span>
                {aw.tokensRevokedBy && <span className="text-gray-500 ml-2">by {aw.tokensRevokedBy}</span>}
              </div>
            )}
            {aw.passwordResetAt && (
              <div>
                <span className="text-gray-500 block mb-1">Password Reset</span>
                <span className="text-green-400">{formatTimestamp(aw.passwordResetAt, selectedTz)}</span>
                {aw.passwordResetBy && <span className="text-gray-500 ml-2">by {aw.passwordResetBy}</span>}
              </div>
            )}

            {/* Global cutoff */}
            <div>
              <span className="text-gray-500 block mb-1">Global Cutoff</span>
              <span className="text-purple-300 font-medium">{formatTimestamp(aw.globalCutoff, selectedTz)}</span>
            </div>
          </div>

          {/* Compromised sessions — sessions that were touched by a malicious IP */}
          {compromisedSessions && compromisedSessions.size > 0 && (
            <div className="mt-3 pt-3 border-t border-purple-800/30">
              <span className="text-gray-500 block mb-1.5 text-xs">
                Compromised Sessions ({compromisedSessions.size})
              </span>
              <div className="text-[11px] text-gray-400 mb-1.5">
                Sign-in <code className="text-gray-300">SessionId</code> or
                {' '}<code className="text-gray-300">AppAccessContext.AADSessionId</code> values that
                were touched by a malicious IP. Pre-compromise events under these sessions are legitimate prior session use; post-compromise events could be the attacker using a stolen token even if the IP looks clean.
              </div>
              <div className="space-y-0.5 font-mono text-xs">
                {[...compromisedSessions.entries()]
                  .sort((a, b) => (a[1] || '').localeCompare(b[1] || ''))
                  .map(([sid, ts]) => (
                    <div key={sid} className="flex items-baseline gap-3">
                      <span className="text-purple-300 break-all">{sid}</span>
                      <span className="text-gray-500 flex-shrink-0">
                        first malicious touch: <span className="text-gray-300">{formatTimestamp(ts, selectedTz)}</span>
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Other Sessions Seen — bulk classification list (v1.53.2) */}
          {Array.isArray(otherSessionsSummary) && otherSessionsSummary.length > 0 && (
            <div className="mt-3 pt-3 border-t border-purple-800/30">
              <span className="text-gray-500 block mb-1.5 text-xs">
                Other Sessions Seen ({otherSessionsSummary.length})
              </span>
              <div className="text-[11px] text-gray-400 mb-1.5">
                Distinct session ids in the case that were NOT touched by a malicious IP. Click the circle to mark a session as Safe — events under safe sessions are hidden via the "Safe sessions hidden" toolbar toggle. The full list, including compromised sessions, is on the AAD Session IDs tab.
              </div>
              <div className="space-y-0.5 font-mono text-xs">
                {otherSessionsSummary.map((row) => {
                  const isSafe = row.status === 'safe';
                  return (
                    /* Status circle LEADS the row, matching every classification
                       table in the portal (v1.139.0). */
                    <div key={row.sessionId} className="flex items-center gap-2">
                      <StatusCircle
                        tone={sessionStatusTone(row.status)}
                        onClick={onCycleSessionStatus ? () => onCycleSessionStatus(row.sessionId) : undefined}
                        title={isSafe ? 'Safe — click to clear' : 'Default — click to mark this session Safe'}
                        className="flex-shrink-0"
                      />
                      <span className="text-gray-300 break-all flex-1">{row.sessionId}</span>
                      <span className="text-gray-500 flex-shrink-0 text-[11px]">{row.eventCount} {row.eventCount === 1 ? 'event' : 'events'}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────

export default function TimelineView({
  signInEvents = [],
  exchangeEvents = [],
  directoryAuditEvents = [],
  sharePointEvents = [],
  messageTraceEvents = [],
  urlClickEvents = [],
  rawUrlClickEvents,
  // Directory-audit events BEFORE the filter pills, used only to build the
  // correlation index. A sibling hidden by a pill must still be disclosed, or the
  // badge undercounts and reads as a smaller action than the one that happened.
  rawDirectoryAuditEvents,
  dlpEvents = [],
  generalEvents = [],
  azureActivityEvents = [],
  riskDetectionEvents = [],
  riskyUserEvents = [],
  securityAlertEvents = [],
  securityIncidentEvents = [],
  // Collapsed non-interactive sign-in activity. Defaults to [] so an OLDER run blob
  // (written before the runbook read this feed) renders exactly as before rather than
  // crashing -- but note absent must be read as "not collected", never "none happened".
  nonInteractiveSignInEvents = [],
  // Portal detection alerts about the case user (utils/caseAlerts.js), already shaped as
  // timeline rows. All Events only: they come from DetectionAlerts, not the run blob.
  caseAlertEvents = [],
  ipData = [],
  apps = [],
  knownApps = [],
  tenantId,
  onToggleKnownApp,
  knownAppSaving = false,
  isAdmin = false,
  hideKnownApps = true,
  hideMsSubstrate = false,
  hideOutlookNoise = false,
  revealSubjects = false,
  onToggleRevealSubjects,
  safeIPSet = new Set(),
  ipClassifications = {},
  summary = {},
  caseData = {},
  selectedTz = 'ET',
  // Evidence for judging the case user's own Teams attendance. CaseDetail builds it from the
  // UNFILTERED results so a pill cannot shrink the baseline: { baseline, signIns, nonInteractive }.
  meetingContext = null,
  attackerWindow = null,
  // Recovered-leg attribution from CaseDetail (v1.188.0) — see LegAttributionContext.
  legAttribution = null,
  // v1.182.0 — 'credentials_captured_no_access_found' relabels the panel to an Exposure
  // Window, because on this attestation the window is opportunity, not confirmed access.
  compromiseScope = 'access_confirmed',
  hidePostWindow = false,
  onTogglePostWindow,
  postWindowFilteredCount = 0,
  // Pre-compromise session filter (v1.53.0)
  compromisedSessions = new Map(),
  hidePreCompromise = true,
  onTogglePreCompromise,
  preCompromiseCount = 0,
  // Safe-session classifications (v1.53.2)
  sessionClassifications = {},
  safeSessionSet = new Set(),
  onCycleSessionStatus,
  otherSessionsSummary = [],
  dismissedEventIds = new Set(),
  onDismissEvent,
  // The analyst's attacker-activity marks (v1.189.0): Map<eventId,{reason,by,at}> + setter.
  attackerEventMarks = null,
  onSetAttackerMark,
  // Which of this person's addresses the run searched (v1.135.0); null on results
  // blobs from before multi-UPN resolution existed.
  identityResolution = null,
  urlClassifications = {},
  onCycleUrlStatus,
  onSetIPStatus,
  phishingFlags = [],
  onTogglePhishingFlag,
  relatedPhishingMessages = [],
  userBaseline = null,
  ipComments = {},
  // Per-type sub-tab dataset (RAW + always-on baseline only). The toolbar toggles
  // are applied upstream to the per-type props above (which build the filtered "All
  // Events" combinedEvents); the dedicated sub-tabs read from this unfiltered set so
  // they show their full records. Falls back to the filtered props when absent.
  subTabEvents = null,
}) {
  const [activeSubTab, setActiveSubTab] = useState('all');
  const [filterQuery, setFilterQuery] = useState('');
  const [sortColumn, setSortColumn] = useState('EventTimeUTC');
  const [sortDirection, setSortDirection] = useState('asc');
  const [expandedRow, setExpandedRow] = useState(null);

  // Build IP lookup for cross-referencing
  const ipLookup = useMemo(() => {
    const lookup = {};
    ipData.forEach(ip => { lookup[ip.IPAddress] = ip; });
    return lookup;
  }, [ipData]);

  // Which events are attributable to the attacker. Built from the analyst's own malicious
  // classifications plus the sessions those addresses were seen in -- see
  // utils/attackerAttribution.js for why a session is corroboration and not proof.
  const attackerCtx = useMemo(() => buildAttackerContext({
    SignInEvents: signInEvents,
    NonInteractiveSignInEvents: nonInteractiveSignInEvents,
    ExchangeEvents: exchangeEvents,
    SharePointEvents: sharePointEvents,
  }, ipClassifications, attackerEventMarks, attackerSessionMarks(sessionClassifications)),
  [signInEvents, nonInteractiveSignInEvents, exchangeEvents, sharePointEvents, ipClassifications, attackerEventMarks, sessionClassifications]);
  const analystMarkCtx = useMemo(
    () => ({
      marks: attackerEventMarks instanceof Map ? attackerEventMarks : new Map(),
      onSet: onSetAttackerMark,
      // Safe is refused on a row whose own address is classified Malicious (v1.189.8).
      isMaliciousRow: (evt) => {
        const ip = evt?.IPAddress || evt?.InferredIP || '';
        return Boolean(ip) && ipClassifications?.[ip] === 'malicious';
      },
    }),
    [attackerEventMarks, onSetAttackerMark, ipClassifications],
  );

  // Build combined event list
  const combinedEvents = useMemo(() => {
    // Tagging only. Every roll-up happens in ONE place, at the bottom of this memo, so
    // that the reports can apply the identical transform at the identical point --
    // see utils/allEventsRollups.js. Sign-ins become ACCESS PERIODS there (341 rows on
    // the reference case say the same thing 341 times; six periods narrate the incident),
    // and utils/accessPeriods.js records the four groupings that were tried and rejected.
    const signIns = signInEvents.map(evt => ({
      ...evt,
      _eventType: 'SignIn',
      _category: 'SignIn',
    }));

    // Operations hidden from timeline — data stays in blob for historical reference.
    // See HiddenOperations.jsx for the full list and rationale.
    const hiddenExchangeOps = new Set(['Send', 'SendAs', 'SendOnBehalf', 'MessageReadReceiptReceived', 'ReceiveEmail', 'MipLabel']);
    const hiddenSharePointOps = new Set(['FileSyncUploadedFull', 'PagePrefetched']);
    const hiddenDlpOps = new Set(['MipLabel']);
    const exchanges = exchangeEvents
      .filter(evt => !hiddenExchangeOps.has(evt.Operation))
      .map(evt => {
        const cat = getExchangeCategory(evt.Operation);
        return {
          ...evt,
          _eventType: cat === 'SharePoint' || cat === 'SharePointList' ? cat : 'Exchange',
          _category: cat,
        };
      });

    // Tagging only -- rolled into ACTIONS at the bottom of this memo. Microsoft records
    // one thing a person did as several rows, and v1.152.0's correlationId badge could not
    // see them whole: a B2B invite arrives as three rows under TWO correlation ids, and 8
    // of the reference case's actions span more than one. See utils/directoryActions.js.
    // 🔴 `isAutoMfaUpdate` applied here as of v1.171.0. It was NOT, and All Events was the
    // only surface of three that kept these rows: the Directory Audit sub-tab drops them
    // (DirectoryAuditView's own processedEvents) and so does the report stream
    // (eventUtils combineAndTagEvents). So the report matched one screen surface and not
    // the other -- and All Events, the view the analyst signs off on and the one
    // `reviewedEventCount` is derived from, was the odd one out.
    //
    // These are Azure MFA StrongAuthenticationService maintenance writes: initiated by a
    // service principal, both old and new values populated, device name unchanged. An
    // attacker cannot forge `initiatedBy.app`, and the analyst can still read them on the
    // workload's own tab, so nothing becomes unreachable.
    const dirAudit = directoryAuditEvents
      .filter(evt => !isAutoMfaUpdate(evt))
      .map(evt => ({
        ...evt,
        _eventType: 'DirectoryAudit',
        _category: 'DirectoryAudit',
      }));

    const spEvents = sharePointEvents.filter(evt => !hiddenSharePointOps.has(evt.Operation)).map(evt => {
      const cat = getExchangeCategory(evt.Operation);
      return {
        ...evt,
        _eventType: cat === 'SharePoint' || cat === 'SharePointList' ? cat : 'SharePoint',
        _category: cat,
      };
    });

    // Selection via the SHARED rule (utils/eventUtils selectTimelineMessageTrace), so the
    // reports choose exactly these rows too -- v1.156.0 unified the roll-ups and left this
    // one behind, which is how `reviewedEventCount` came to count 83 inbound messages no
    // view of the case displays.
    //
    // Collapsed to one row per message: message trace is stored per RECIPIENT, so a
    // single send to three addresses rendered as three identical-looking rows. Every
    // constituent id rides on `_collapsedIds` -- dismissal acts on all of them.
    const msgTraces = collapseMessageTrace(
      selectTimelineMessageTrace(messageTraceEvents, phishingFlags)
    );

    // Same shared rule: clicks that reached the URL, narrowed to the confirmed ones once
    // the analyst has confirmed any. Confirming one IS the statement that the others are
    // not the story, so the reports must select identically.
    const urlClicks = selectTimelineUrlClicks(urlClickEvents, urlClassifications);

    const dlps = dlpEvents.filter(evt => !hiddenDlpOps.has(evt.Operation)).map(evt => ({
      ...evt,
      _eventType: 'DLP',
      _category: 'DLP',
    }));

    const hiddenGeneralOps = new Set(['MessageReadReceiptReceived']);
    const generals = generalEvents.filter(evt => !hiddenGeneralOps.has(evt.Operation)).map(evt => ({
      ...evt,
      _eventType: 'General',
      _category: 'General',
    }));

    const azureActs = azureActivityEvents.map(evt => ({
      ...evt,
      _eventType: 'AzureActivity',
      _category: 'AzureActivity',
    }));

    const riskDets = riskDetectionEvents.map(evt => ({
      ...evt,
      _eventType: 'RiskDetection',
      _category: 'RiskDetection',
      Operation: evt.RiskEventType || 'RiskDetection',
    }));

    const riskyUsers = riskyUserEvents.map(evt => ({
      ...evt,
      _eventType: 'RiskyUser',
      _category: 'RiskyUser',
      Operation: `Risk: ${evt.RiskLevel || 'Unknown'}`,
    }));

    // Only include user-related alerts/incidents in the combined "All Events" view
    // Alerts with non-sign-in evidence (e.g., URL clicks) produce TWO rows:
    //   1. Activity row at the user action time (AlertEvidence type)
    //   2. Alert row at the MSFT alert creation time (SecurityAlert type)
    const dedupedAlerts = deduplicateAlerts(securityAlertEvents.filter(e => e.IsUserRelated));
    const secAlerts = [];
    for (const evt of dedupedAlerts) {
      const activityTime = evt.FirstActivityDateTime || '';
      const url = getSecurityAlertUrl(evt);
      const hasNonSignInEvidence = !!url;

      // Activity row — at the time the user action happened
      if (hasNonSignInEvidence && activityTime) {
        secAlerts.push({
          ...evt,
          EventTimeUTC: activityTime,
          EventId: `${evt.EventId}_activity`,
          _eventType: 'AlertEvidence', _category: 'AlertEvidence',
          Operation: evt.Title || 'Alert',
          _alertUrl: url,
          _alertTitle: evt.Title || '',
          _alertSeverity: evt.Severity || '',
        });
      }

      // Alert row — at the time Microsoft generated the alert
      secAlerts.push({
        ...evt,
        _eventType: 'SecurityAlert',
        _category: 'SecurityAlert',
        Operation: evt.Title || 'Alert',
      });
    }

    // Non-interactive sign-in activity (token refresh / SSO reuse / PRT replay).
    //
    // Tagging only -- rolled into access periods at the bottom of this memo. ROLLED, never
    // filtered out: the feed arrives at (session, IP, hour) grain so one idle laptop is
    // ~25 rows a day and the reference case carried 2,836, but 37 of that case's 59
    // addresses exist ONLY in this feed, 3 of them classified malicious, and 380 rows
    // carry the 50173 revoke signal. Dropping the feed would drop the containment read.
    //
    // `Operation` is the row's own label in the source feed. The Application column shows
    // the applications instead (_niAppLabel, stamped by the roll-up), because that literal
    // word printed identically on all 2,836 rows and made the feed look content-free.
    const nonInteractives = nonInteractiveSignInEvents.map(evt => ({
      ...evt,
      _eventType: 'NonInteractiveSignIn',
      _category: 'NonInteractiveSignIn',
      Operation: 'TokenRefresh',
    }));

    const secIncidents = securityIncidentEvents.filter(evt => evt.IsUserRelated).map(evt => ({
      ...evt,
      _eventType: 'SecurityIncident',
      _category: 'SecurityIncident',
      Operation: evt.DisplayName || 'Incident',
    }));

    // ONE shared roll-up, applied at the same point the reports apply it, so the screen
    // an analyst signs off on and the report that leaves the building cannot drift.
    // See utils/allEventsRollups.js -- nothing above this line rolls anything.
    //
    // Portal detection alerts are appended AFTER the roll-up: they are not part of the
    // report contract above (the reports never carry them -- see utils/caseAlerts.js),
    // and no roll-up applies to them.
    return [
      ...applyAllEventsRollups([
        ...signIns, ...exchanges, ...dirAudit, ...spEvents, ...msgTraces, ...urlClicks,
        ...dlps, ...generals, ...azureActs, ...riskDets, ...riskyUsers,
        ...secAlerts, ...secIncidents, ...nonInteractives,
      ]),
      ...caseAlertEvents,
    ];
  }, [signInEvents, exchangeEvents, directoryAuditEvents, sharePointEvents, messageTraceEvents, urlClickEvents,
      dlpEvents, generalEvents, azureActivityEvents, riskDetectionEvents, riskyUserEvents,
      securityAlertEvents, securityIncidentEvents, nonInteractiveSignInEvents, phishingFlags, urlClassifications,
      caseAlertEvents]);

  // Resolve the per-type arrays the SUB-TABS render from. Prefer the unfiltered
  // `subTabEvents` set (toolbar toggles scope to All Events only); fall back to the
  // filtered per-type props when it isn't supplied. Empty arrays from subTabEvents
  // are intentionally kept (?? guards only null/undefined). "All Events" itself keeps
  // using `combinedEvents` (built from the filtered props) — untouched.
  const stEv = useMemo(() => {
    const s = subTabEvents || {};
    return {
      signIn: s.SignInEvents ?? signInEvents,
      directory: s.DirectoryAuditEvents ?? directoryAuditEvents,
      msgTrace: s.MessageTraceEvents ?? messageTraceEvents,
      urlClick: s.UrlClickEvents ?? rawUrlClickEvents ?? urlClickEvents,
      sharePoint: s.SharePointEvents ?? sharePointEvents,
      riskDetection: s.RiskDetectionEvents ?? riskDetectionEvents,
      riskyUser: s.RiskyUserEvents ?? riskyUserEvents,
      dlp: s.DlpEvents ?? dlpEvents,
      general: s.GeneralEvents ?? generalEvents,
      azureActivity: s.AzureActivityEvents ?? azureActivityEvents,
      securityAlert: s.SecurityAlertEvents ?? securityAlertEvents,
      securityIncident: s.SecurityIncidentEvents ?? securityIncidentEvents,
    };
  }, [subTabEvents, signInEvents, directoryAuditEvents, messageTraceEvents, rawUrlClickEvents, urlClickEvents,
      sharePointEvents, riskDetectionEvents, riskyUserEvents, dlpEvents, generalEvents, azureActivityEvents,
      securityAlertEvents, securityIncidentEvents]);

  // One-action grouping for directory-audit rows. Built from the UNFILTERED set when
  // the caller supplies it, falling back to the filtered prop so any other consumer of
  // this component still gets grouping (just of what it can see).

  // Deduplicate security alerts for the Security Alerts sub-tab (from the sub-tab set)
  const dedupedSecurityAlertEvents = useMemo(
    () => deduplicateAlerts(stEv.securityAlert),
    [stEv]
  );

  // Sub-tab counts — per-type counts reflect what each sub-tab renders (the
  // unfiltered `stEv` set); `all` stays the filtered All Events count.
  const subTabCounts = useMemo(() => ({
    all: combinedEvents.length,
    signins: stEv.signIn.length,
    apps: apps.length,
    directory: stEv.directory.length,
    msgtrace: stEv.msgTrace.length,
    phishing: relatedPhishingMessages.length,
    urlclicks: stEv.urlClick.length,
    spfiles: stEv.sharePoint.filter(e => FILE_OPERATIONS.has(e.Operation)).length,
    splists: stEv.sharePoint.filter(e => LIST_OPERATIONS.has(e.Operation)).length,
    riskdetection: stEv.riskDetection.length,
    riskyuser: stEv.riskyUser.length,
    dlp: stEv.dlp.length,
    general: stEv.general.length,
    azureactivity: stEv.azureActivity.length,
    securityalerts: dedupedSecurityAlertEvents.length,
    securityincidents: stEv.securityIncident.length,
  }), [combinedEvents, stEv, apps, relatedPhishingMessages, dedupedSecurityAlertEvents]);

  // Total event counts for summary
  const totalSignIn = signInEvents.length;
  const totalExchange = exchangeEvents.length;
  const totalIPs = ipData.length;

  const hasAnyData = totalSignIn > 0 || totalExchange > 0 || directoryAuditEvents.length > 0 ||
    sharePointEvents.length > 0 || messageTraceEvents.length > 0 || urlClickEvents.length > 0 ||
    dlpEvents.length > 0 || generalEvents.length > 0 || azureActivityEvents.length > 0 ||
    riskDetectionEvents.length > 0 || riskyUserEvents.length > 0 ||
    securityAlertEvents.length > 0 || securityIncidentEvents.length > 0;

  if (!hasAnyData) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-500">No events found.</p>
      </div>
    );
  }

  return (
    <IdentityContext.Provider value={identityResolution}>
    <IpCommentsContext.Provider value={ipComments}>
    <LegAttributionContext.Provider value={legAttribution}>
      <AttackerContext.Provider value={attackerCtx}>
      <AnalystMarkContext.Provider value={analystMarkCtx}>
    <div>
      {/* Summary Header */}
      <div className="mb-4 px-1">
        <p className="text-sm text-gray-400">
          {(caseData.startDate || caseData.endDate) && (
            <span>
              <span className="text-gray-500">Investigation period:</span>{' '}
              <span className="text-gray-300">
                {formatDateRange(
                  caseData.resultsStartDate || caseData.startDate,
                  caseData.resultsEndDate || caseData.endDate
                )}
              </span>
              <span className="text-gray-600 mx-2">|</span>
            </span>
          )}
          <span className="text-gray-300">{totalSignIn}</span>
          <span className="text-gray-500"> sign-in events</span>
          <span className="text-gray-600 mx-2">|</span>
          <span className="text-gray-300">{totalExchange}</span>
          <span className="text-gray-500"> exchange events</span>
          {messageTraceEvents.length > 0 && (
            <>
              <span className="text-gray-600 mx-2">|</span>
              <span className="text-gray-300">{messageTraceEvents.length}</span>
              <span className="text-gray-500"> message trace</span>
            </>
          )}
          {urlClickEvents.length > 0 && (
            <>
              <span className="text-gray-600 mx-2">|</span>
              <span className="text-gray-300">{urlClickEvents.length}</span>
              <span className="text-gray-500"> URL clicks</span>
            </>
          )}
          <span className="text-gray-600 mx-2">|</span>
          <span className="text-gray-300">{totalIPs}</span>
          <span className="text-gray-500"> unique IPs</span>
        </p>
      </div>

      {/* Attacker Window Panel */}
      {attackerWindow && !attackerWindow.isIndeterminate && <AttackerWindowPanel
        attackerWindow={attackerWindow}
        compromiseScope={compromiseScope}
        postWindowFilteredCount={postWindowFilteredCount}
        hidePostWindow={hidePostWindow}
        onTogglePostWindow={onTogglePostWindow}
        selectedTz={selectedTz}
        compromisedSessions={compromisedSessions}
        hidePreCompromise={hidePreCompromise}
        onTogglePreCompromise={onTogglePreCompromise}
        preCompromiseFilteredCount={preCompromiseCount}
        sessionClassifications={sessionClassifications}
        safeSessionSet={safeSessionSet}
        onCycleSessionStatus={onCycleSessionStatus}
        otherSessionsSummary={otherSessionsSummary}
      />}

      {/* Sub-tab Navigation */}
      <div className="flex flex-wrap gap-1 mb-5 border-b border-gray-800 pb-3">
        {SUB_TABS.filter(tab => tab.id !== 'phishing' || phishingFlags.length > 0 || relatedPhishingMessages.length > 0).map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveSubTab(tab.id);
              setFilterQuery('');
              setExpandedRow(null);
            }}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors flex items-center gap-1.5 ${
              activeSubTab === tab.id
                ? tab.id === 'phishing' ? 'bg-red-900/60 text-red-300 border-red-700/50' : 'bg-blue-900/60 text-blue-300 border-blue-700/50'
                : tab.id === 'phishing' ? 'bg-red-900/30 text-red-400 border-red-800/50 hover:text-red-300 hover:border-red-700' : 'bg-gray-800/50 text-gray-500 border-gray-700/50 hover:text-gray-300 hover:border-gray-600'
            }`}
          >
            {tab.label}
            {subTabCounts[tab.id] > 0 && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                activeSubTab === tab.id
                  ? tab.id === 'phishing' ? 'bg-red-800/80 text-red-200' : 'bg-blue-800/80 text-blue-200'
                  : 'bg-gray-700 text-gray-400'
              }`}>
                {subTabCounts[tab.id]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Sub-tab Content */}
      {activeSubTab === 'all' && (
        <AllEventsView
          events={combinedEvents}
          ipLookup={ipLookup}
          knownApps={knownApps}
          apps={apps}
          hideKnownApps={hideKnownApps}
          hideMsSubstrate={hideMsSubstrate}
          hideOutlookNoise={hideOutlookNoise}
          revealSubjects={revealSubjects}
          onToggleRevealSubjects={onToggleRevealSubjects}
          safeIPSet={safeIPSet}
          ipClassifications={ipClassifications}
          filterQuery={filterQuery}
          setFilterQuery={setFilterQuery}
          sortColumn={sortColumn}
          setSortColumn={setSortColumn}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          expandedRow={expandedRow}
          setExpandedRow={setExpandedRow}
          selectedTz={selectedTz}
          dismissedEventIds={dismissedEventIds}
          onDismissEvent={onDismissEvent}
          urlClassifications={urlClassifications}
          compromisedSessions={compromisedSessions}
          sessionClassifications={sessionClassifications}
          onCycleSessionStatus={onCycleSessionStatus}
          onSetIPStatus={onSetIPStatus}
          meetingContext={meetingContext}
        />
      )}
      {activeSubTab === 'signins' && (
        <SignInLogsView
          events={stEv.signIn}
          ipLookup={ipLookup}
          safeIPSet={safeIPSet}
          ipClassifications={ipClassifications}
          filterQuery={filterQuery}
          setFilterQuery={setFilterQuery}
          sortColumn={sortColumn}
          setSortColumn={setSortColumn}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          expandedRow={expandedRow}
          setExpandedRow={setExpandedRow}
          selectedTz={selectedTz}
          compromisedSessions={compromisedSessions}
          sessionClassifications={sessionClassifications}
          onCycleSessionStatus={onCycleSessionStatus}
        />
      )}
      {activeSubTab === 'apps' && (
        <EnterpriseAppsTab
          apps={apps}
          knownApps={knownApps}
          tenantId={tenantId}
          onToggleKnownApp={onToggleKnownApp}
          isSaving={knownAppSaving}
          isAdmin={isAdmin}
          hideKnownApps={hideKnownApps}
        />
      )}
      {activeSubTab === 'directory' && (
        <DirectoryAuditView
          events={stEv.directory}
          safeIPSet={safeIPSet}
          ipClassifications={ipClassifications}
          filterQuery={filterQuery}
          setFilterQuery={setFilterQuery}
          sortColumn={sortColumn}
          setSortColumn={setSortColumn}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          expandedRow={expandedRow}
          setExpandedRow={setExpandedRow}
          selectedTz={selectedTz}
        />
      )}
      {activeSubTab === 'msgtrace' && (
        <MessageTraceView
          events={stEv.msgTrace}
          safeIPSet={safeIPSet}
          ipClassifications={ipClassifications}
          filterQuery={filterQuery}
          setFilterQuery={setFilterQuery}
          sortColumn={sortColumn}
          setSortColumn={setSortColumn}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          expandedRow={expandedRow}
          setExpandedRow={setExpandedRow}
          selectedTz={selectedTz}
          phishingFlags={phishingFlags}
          onTogglePhishingFlag={onTogglePhishingFlag}
          revealSubjects={revealSubjects}
          onToggleRevealSubjects={onToggleRevealSubjects}
          onFindUrlClicks={(nmid) => {
            setActiveSubTab('urlclicks');
            setFilterQuery(nmid);
            setExpandedRow(null);
          }}
        />
      )}
      {activeSubTab === 'phishing' && (
        <RelatedPhishingView
          phishingFlags={phishingFlags}
          relatedPhishingMessages={relatedPhishingMessages}
          selectedTz={selectedTz}
          ipClassifications={ipClassifications}
          revealSubjects={revealSubjects}
          onToggleRevealSubjects={onToggleRevealSubjects}
        />
      )}
      {activeSubTab === 'urlclicks' && (
        <UrlClicksView
          events={stEv.urlClick}
          ipLookup={ipLookup}
          safeIPSet={safeIPSet}
          ipClassifications={ipClassifications}
          filterQuery={filterQuery}
          setFilterQuery={setFilterQuery}
          sortColumn={sortColumn}
          setSortColumn={setSortColumn}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          expandedRow={expandedRow}
          setExpandedRow={setExpandedRow}
          selectedTz={selectedTz}
          urlClassifications={urlClassifications}
          onCycleUrlStatus={onCycleUrlStatus}
          onFindMessageTrace={(nmid) => {
            setActiveSubTab('msgtrace');
            setFilterQuery(nmid);
            setExpandedRow(null);
          }}
        />
      )}
      {activeSubTab === 'spfiles' && (
        <SharePointFilesView
          exchangeEvents={stEv.sharePoint}
          ipLookup={ipLookup}
          selectedTz={selectedTz}
        />
      )}
      {activeSubTab === 'splists' && (
        <SharePointListsView
          exchangeEvents={stEv.sharePoint}
          ipLookup={ipLookup}
          selectedTz={selectedTz}
        />
      )}
      {activeSubTab === 'riskdetection' && (
        <GenericEventsView
          events={stEv.riskDetection}
          columns={[
            { key: 'EventTimeUTC', label: `Time (${getTzLabel(selectedTz)})`, format: ts => formatTimestampShort(ts, selectedTz) },
            { key: 'RiskEventType', label: 'Risk Type' },
            { key: 'RiskLevel', label: 'Level', badge: true, badgeColors: { high: 'bg-red-600', medium: 'bg-amber-600', low: 'bg-green-600', hidden: 'bg-gray-600', none: 'bg-gray-700' } },
            { key: 'RiskState', label: 'State' },
            { key: 'IPAddress', label: 'IP Address' },
            { key: 'Activity', label: 'Activity' },
            { key: 'Source', label: 'Source' },
            { key: 'Location_Country', label: 'Country' },
          ]}
          safeIPSet={safeIPSet}
          ipClassifications={ipClassifications}
          filterQuery={filterQuery}
          setFilterQuery={setFilterQuery}
          eventType="RiskDetection"
        />
      )}
      {activeSubTab === 'riskyuser' && (
        <GenericEventsView
          events={stEv.riskyUser}
          columns={[
            { key: 'EventTimeUTC', label: `Time (${getTzLabel(selectedTz)})`, format: ts => formatTimestampShort(ts, selectedTz) },
            { key: 'UserPrincipalName', label: 'User' },
            { key: 'RiskLevel', label: 'MSFT Risk Level', badge: true, badgeColors: { high: 'bg-red-600', medium: 'bg-amber-600', low: 'bg-green-600', hidden: 'bg-gray-600', none: 'bg-gray-700' } },
            { key: 'RiskState', label: 'State' },
            { key: 'RiskDetail', label: 'Detail' },
          ]}
          filterQuery={filterQuery}
          setFilterQuery={setFilterQuery}
          eventType="RiskyUser"
        />
      )}
      {activeSubTab === 'dlp' && (
        <GenericEventsView
          events={stEv.dlp}
          columns={[
            { key: 'EventTimeUTC', label: `Time (${getTzLabel(selectedTz)})`, format: ts => formatTimestampShort(ts, selectedTz) },
            { key: 'Operation', label: 'Operation' },
            { key: 'PolicyName', label: 'Policy' },
            { key: 'PolicyAction', label: 'Action' },
            { key: 'SensitiveInfoTypes', label: 'Sensitive Types' },
            { key: 'IPAddress', label: 'IP Address' },
            { key: 'Workload', label: 'Workload' },
          ]}
          safeIPSet={safeIPSet}
          ipClassifications={ipClassifications}
          filterQuery={filterQuery}
          setFilterQuery={setFilterQuery}
          eventType="DLP"
        />
      )}
      {activeSubTab === 'general' && (
        <GenericEventsView
          events={stEv.general}
          columns={[
            { key: 'EventTimeUTC', label: `Time (${getTzLabel(selectedTz)})`, format: ts => formatTimestampShort(ts, selectedTz) },
            { key: 'Operation', label: 'Operation' },
            { key: 'Workload', label: 'Workload' },
            { key: 'RecordType', label: 'Record Type' },
            { key: 'ResultStatus', label: 'Status' },
            { key: 'IPAddress', label: 'IP Address' },
          ]}
          safeIPSet={safeIPSet}
          ipClassifications={ipClassifications}
          filterQuery={filterQuery}
          setFilterQuery={setFilterQuery}
          eventType="General"
        />
      )}
      {activeSubTab === 'azureactivity' && (
        <GenericEventsView
          events={stEv.azureActivity}
          columns={[
            { key: 'EventTimeUTC', label: `Time (${getTzLabel(selectedTz)})`, format: ts => formatTimestampShort(ts, selectedTz) },
            { key: 'Operation', label: 'Operation' },
            { key: 'ResourceType', label: 'Resource Type' },
            { key: 'ResultStatus', label: 'Status' },
            { key: 'IPAddress', label: 'IP Address' },
            { key: 'Category', label: 'Category' },
          ]}
          safeIPSet={safeIPSet}
          ipClassifications={ipClassifications}
          filterQuery={filterQuery}
          setFilterQuery={setFilterQuery}
          eventType="AzureActivity"
        />
      )}
      {activeSubTab === 'securityalerts' && (
        <GenericEventsView
          events={dedupedSecurityAlertEvents}
          columns={[
            { key: 'EventTimeUTC', label: `Alert Time (${getTzLabel(selectedTz)})`, width: 150, format: ts => formatTimestampShort(ts, selectedTz) },
            { key: '_activityTime', label: `Activity Time (${getTzLabel(selectedTz)})`, width: 150, format: ts => ts ? formatTimestampShort(ts, selectedTz) : '\u2014' },
            { key: 'Title', label: 'Title', width: 380 },
            { key: 'Severity', label: 'Severity', width: 80, badge: true, badgeColors: { high: 'bg-red-600', medium: 'bg-amber-600', low: 'bg-green-600', informational: 'bg-blue-600' } },
            { key: '_evidence', label: 'Evidence', width: 400, format: v => v ? <span title={v} className="cursor-help">{v}</span> : '\u2014' },
            { key: 'Category', label: 'Category', width: 140 },
            { key: 'MitreTechniques', label: 'MITRE', width: 160 },
          ]}
          filterQuery={filterQuery}
          setFilterQuery={setFilterQuery}
          safeIPSet={safeIPSet}
          ipClassifications={ipClassifications}
          eventType="SecurityAlert"
          enrichEvents={events => events.map(evt => ({
            ...evt,
            _activityTime: evt.FirstActivityDateTime || evt.firstActivityDateTime || evt.RawLog?.firstActivityDateTime || '',
            // EventTimeUTC = alert creation time (from createdDateTime in runbook)
            _evidence: getSecurityAlertSummary(evt),
          }))}
        />
      )}
      {activeSubTab === 'securityincidents' && (
        <GenericEventsView
          events={stEv.securityIncident}
          columns={[
            { key: 'EventTimeUTC', label: `Time (${getTzLabel(selectedTz)})`, format: ts => formatTimestampShort(ts, selectedTz) },
            { key: 'DisplayName', label: 'Incident Name' },
            { key: 'Severity', label: 'Severity', badge: true, badgeColors: { high: 'bg-red-600', medium: 'bg-amber-600', low: 'bg-green-600', informational: 'bg-blue-600' } },
            { key: 'Status', label: 'Status' },
            { key: 'Classification', label: 'Classification' },
            { key: 'Determination', label: 'Determination' },
            { key: 'AlertCount', label: 'Alerts' },
            { key: 'IsUserRelated', label: 'User Related', format: v => v ? 'Yes' : 'No' },
          ]}
          filterQuery={filterQuery}
          setFilterQuery={setFilterQuery}
          eventType="SecurityIncident"
        />
      )}
    </div>
      </AnalystMarkContext.Provider>
      </AttackerContext.Provider>
    </LegAttributionContext.Provider>
    </IpCommentsContext.Provider>
    </IdentityContext.Provider>
  );
}

// ── Helper: format date range ────────────────────────────────────────────────

// ── Column Filter Select ─────────────────────────────────────────────────────

function ColumnFilterSelect({ columnId, options, value, onChange, extraOptions }) {
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(columnId, e.target.value)}
      onClick={(e) => e.stopPropagation()}
      className={`w-full bg-gray-800 border rounded px-1 py-1 text-xs focus:outline-none focus:border-blue-600 cursor-pointer ${
        value ? 'border-blue-600 text-blue-300' : 'border-gray-700 text-gray-400'
      }`}
    >
      <option value="">All</option>
      {extraOptions && extraOptions.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
      {options.map(opt => (
        <option key={opt} value={opt}>{opt || '(empty)'}</option>
      ))}
    </select>
  );
}

// ── "All Events" Combined Timeline ──────────────────────────────────────────

function getAllEventsColumns(tz) {
  return [
    { id: 'EventTimeUTC', label: `Timestamp (${getTzLabel(tz)})`, sortable: true, filterable: false },
    { id: '_description', label: 'Description', sortable: true, filterable: false },
    { id: '_eventType', label: 'Type', sortable: true, filterable: true },
    { id: '_appName', label: 'Application', sortable: true, filterable: true },
    { id: 'IPAddress', label: 'Client IP', sortable: true, filterable: true },
    // Replaced VPN/Proxy (v1.138.0). The threat flags remain on the Sign-In Logs
    // view, in the expanded row and in IP Intelligence; the session id is the
    // column that lets an analyst tie a row to an attacker-owned session, which
    // nothing else in this table exposes.
    { id: '_sessionId', label: 'AAD Session ID', sortable: true, filterable: true },
    { id: '_country', label: 'Country', sortable: true, filterable: true },
    { id: 'RiskScore', label: 'Risk', sortable: true, filterable: false },
  ];
}

// VPN/Proxy extra filter options for <select> dropdowns
const VPN_PROXY_EXTRA_OPTIONS = [{ value: '__any_threat__', label: 'Any threat' }];

// Classify an IP for display coloring.
// Returns: 'safe', 'suspect', 'malicious', 'ms-infra', 'ms-compute', or 'default'.
// Checks explicit ipClassifications first, then safeIPSet, then AzureServiceTier.
// When an event is provided and the IP is MS-Infra but the event is an unresolved
// proxy (SharePoint/Exchange with no successful session IP resolution), returns
// 'default' instead of 'ms-infra' — the real source IP is unknown.
function classifyIP(ip, safeIPSet, ipLookup, ipClassifications, event) {
  // Explicit classification takes priority
  if (ipClassifications) {
    const explicit = ipClassifications[ip];
    if (explicit && explicit !== 'default') return explicit;
  }
  if (safeIPSet && safeIPSet.has(ip)) return 'safe';
  const info = ipLookup && ipLookup[ip];
  if (info) {
    const tier = info.AzureServiceTier || info.azureServiceTier || '';
    if (tier === 'MS-Backend' || tier === 'MS-Shared-Infra') {
      // For proxy event types (SharePoint, Exchange), only show ms-infra if the
      // real user IP was successfully resolved via session IP map. If unresolved,
      // the real source is unknown — show default so analysts don't assume safety.
      if (event) {
        const et = event._eventType || '';
        const isProxyType = et === 'SharePoint' || et === 'SharePointList' || et === 'Exchange';
        if (isProxyType) {
          const method = event.IPResolutionMethod || '';
          const resolved = method === 'SessionIPMap' || method === 'SessionIPMap-MultiIP'
            // TimeWindowCorrelation and single UserIPCorrelation are GUESSES (utils/guessedIp.js):
            // the source is unknown, so the recorded Microsoft address must not read as safe.
            || method === 'UserIPCorrelation-MultiIP';
          if (!resolved) return 'default';
        }
      }
      return 'ms-infra';
    }
    if (tier === 'User-Compute') return 'ms-compute';
  }
  return 'default';
}

function resolvedIPClassName(classification) {
  switch (classification) {
    case 'safe':
      return 'text-green-400';
    case 'ms-infra':
      return 'text-blue-400';
    case 'suspect':
      return 'text-amber-400';
    case 'malicious':
      return 'text-red-400';
    default:
      return 'text-gray-200';
  }
}

function resolvedIPLabel(classification) {
  if (classification === 'ms-infra') return 'MS-Infra';
  if (classification === 'ms-compute') return 'Azure-VM';
  return null;
}

// Partition resolved IPs into interesting vs hidden (MS-Infra / Safe) for large multi-IP lists
function partitionResolvedIPs(resolvedList, safeIPSet, ipLookup, ipClassifications) {
  const interesting = [];
  const hiddenMsInfra = [];
  const hiddenSafe = [];
  for (const rip of resolvedList) {
    const cls = classifyIP(rip, safeIPSet, ipLookup, ipClassifications);
    if (cls === 'ms-infra') hiddenMsInfra.push(rip);
    else if (cls === 'safe') hiddenSafe.push(rip);
    else interesting.push(rip);
  }
  return { interesting, hiddenMsInfra, hiddenSafe };
}

// Resolved IP cell — shows indicator when IP was resolved from a service proxy
function ResolvedIPCell({ event, safeIPSet, ipLookup, ipClassifications }) {
  const ip = event.IPAddress || '\u2014';
  const classification = event.IPAddress ? classifyIP(event.IPAddress, safeIPSet, ipLookup, ipClassifications, event) : 'default';
  const isSafe = classification === 'safe' || classification === 'ms-infra';
  const method = event.IPResolutionMethod || '';

  // Service-initiated directory audit events (e.g., EvoSTS consent processing):
  // the IP is Microsoft's service IP, NOT attributable to the user or attacker.
  if (event.IsServiceInitiated && event._eventType === 'DirectoryAudit') {
    const tooltip = `Microsoft service IP (unattributable) \u2014 this action was processed server-side by ${serviceInitiatorLabel(event)}. ` +
      `The IP belongs to Microsoft\u2019s infrastructure, not to the user or any attacker. ` +
      `Investigate WHAT was done (app/permissions), not WHERE it came from.`;
    return (
      <span className="text-gray-500 cursor-help" title={tooltip}>
        {ip} <span className="text-purple-400 text-[9px] font-medium ml-0.5">{'\u24E2'}</span>
      </span>
    );
  }

  // Message trace events: IP is the sending mail server, not the sender's device
  if (event._eventType === 'MessageTrace' && event.IPAddress) {
    const tooltip = 'Sending mail server IP \u2014 this is the IP of the mail server that handed the message to Exchange Online, not the sender\u2019s device IP.';
    return (
      <span className={`cursor-help ${resolvedIPClassName(classification)}`} title={tooltip}>
        {ip} <span className="text-gray-500 text-[9px] font-medium ml-0.5" title={tooltip}>{'\u2709'}</span><IpCommentNote ip={event.IPAddress} />
      </span>
    );
  }

  if (method === 'SessionIPMap') {
    // Provenance (v1.150.0). 'InCaseSessionCorrelation' / 'InCaseSessionConfirmed' mean the
    // resolution came from this case's OWN sign-in + non-interactive events, because the
    // pipeline's session IP map had no row for the session. The two are different claims:
    //   Correlation -- a Microsoft proxy IP was SUBSTITUTED with the user's real address.
    //   Confirmed   -- the address already on the record was VALIDATED. A backend-service
    //                  event is withheld because a SERVICE performed the action, not because
    //                  its IP is Microsoft's, so its recorded IP is often already the real
    //                  one (54 of 169 on the reference case).
    // Calling a confirmation "resolved from service IP" would describe those 54 wrongly, so
    // the copy and the glyph both branch.
    const src = event.IPResolutionSource || '';
    const confirmed = src === 'InCaseSessionConfirmed';
    const tooltip = confirmed
      ? 'Address confirmed \u2014 this event was recorded by a background service, so its IP was held back '
        + 'until it could be checked. This session\u2019s sign-in activity within the case shows only this '
        + 'one address, and it matches the one on the record.'
      : `Resolved from service IP${event.ServiceAppsSeen ? ` (${event.ServiceAppsSeen})` : ''}`
        + (src === 'InCaseSessionCorrelation'
          ? ' \u2014 matched on this session\u2019s sign-in activity within this case'
          : '');
    return (
      <span className={resolvedIPClassName(classification)} title={tooltip}>
        {ip} <span className="text-cyan-400 text-[10px]" title={tooltip}>{confirmed ? '\u2713' : '\u21E8'}</span><IpCommentNote ip={event.IPAddress} />
      </span>
    );
  }

  if (method === 'SessionIPMap-MultiIP') {
    const allIPs = event.AllResolvedIPs || '';
    const resolvedList = allIPs ? allIPs.split(',').map(s => s.trim()).filter(Boolean) : [];
    const serviceApp = event.ServiceAppsSeen || '';
    const inCase = event.IPResolutionSource === 'InCaseSessionCorrelation';
    const multiIPTooltip = (inCase ? 'Candidates matched on this session\u2019s sign-in activity within this case.\n\n' : '') +
      `Multiple IPs detected in this session. All ${resolvedList.length} IPs were seen using the same authentication session. ` +
      `Any one (or more) of these IPs may have triggered this event \u2014 the exact source cannot be determined. ` +
      `In a compromise scenario, both the legitimate user and an attacker can share the same session token.` +
      (serviceApp ? `\n\nService app: ${serviceApp}` : '') +
      (event.OriginalIPAddress ? `\nOriginal service IP: ${event.OriginalIPAddress}` : '');
    if (resolvedList.length > 0) {
      // For large multi-IP lists, hide MS-Infra and Safe IPs with a compact notation
      if (resolvedList.length > 10) {
        const { interesting, hiddenMsInfra, hiddenSafe } = partitionResolvedIPs(resolvedList, safeIPSet, ipLookup, ipClassifications);
        const totalHidden = hiddenMsInfra.length + hiddenSafe.length;
        const hiddenParts = [];
        if (hiddenMsInfra.length > 0) hiddenParts.push(`${hiddenMsInfra.length} MS-Infra`);
        if (hiddenSafe.length > 0) hiddenParts.push(`${hiddenSafe.length} Safe`);
        const hiddenNote = hiddenParts.length > 0 ? `+ ${hiddenParts.join(', ')} hidden` : '';
        const headerText = totalHidden > 0
          ? `\u26A0 ${interesting.length} IPs (${totalHidden} hidden) \u24D8`
          : `\u26A0 ${resolvedList.length} IPs in session \u24D8`;
        const displayIPs = interesting.length > 0 ? interesting : [];
        return (
          <div className="flex flex-col leading-tight">
            <span className="text-amber-400 text-[9px] cursor-help mb-0.5" title={multiIPTooltip}>
              {headerText}
            </span>
            {displayIPs.map((rip) => {
              const cls = classifyIP(rip, safeIPSet, ipLookup, ipClassifications);
              const label = resolvedIPLabel(cls);
              return (
                <span key={rip}>
                  <span className={resolvedIPClassName(cls)}>{rip}</span>
                  {label && <span className="text-[9px] text-gray-500 ml-1">{label}</span>}
                  <IpCommentNote ip={rip} />
                </span>
              );
            })}
            {interesting.length === 0 && (
              <span className="text-gray-400 text-[10px]">{event.OriginalIPAddress || ip}</span>
            )}
            {hiddenNote && <span className="text-gray-500 text-[9px] mt-0.5">{hiddenNote}</span>}
          </div>
        );
      }
      return (
        <div className="flex flex-col leading-tight">
          <span className="text-amber-400 text-[9px] cursor-help mb-0.5" title={multiIPTooltip}>
            {'\u26A0'} {resolvedList.length} IPs in session {'\u24D8'}
          </span>
          {resolvedList.map((rip) => {
            const cls = classifyIP(rip, safeIPSet, ipLookup, ipClassifications);
            const label = resolvedIPLabel(cls);
            return (
              <span key={rip}>
                <span className={resolvedIPClassName(cls)}>{rip}</span>
                {label && <span className="text-[9px] text-gray-500 ml-1">{label}</span>}
                <IpCommentNote ip={rip} />
              </span>
            );
          })}
        </div>
      );
    }
    return (
      <span className="text-gray-300 cursor-help" title={multiIPTooltip}>
        {ip} <span className="text-amber-400 text-[10px]">{'\u21C4'}</span>
      </span>
    );
  }

  // A GUESS, not an observation (utils/guessedIp.js). The recorded address is the headline;
  // the guess sits under it as "possibly", in grey, and drives no colour, no attribution and
  // no IP count. Origin: IR-20261013-levig-001, where a vendor's mailbox scan was shown on the
  // AiTM attacker's IP.
  if (isGuessedIpMethod(method) && event.GuessedIP) {
    const tooltip = `Recorded address: ${event.IPAddress || 'none'} (Microsoft service). `
      + `This event carries no session, so its true source is unknown. ${event.GuessedIP} is only a guess, `
      + `taken from ${guessedIpBasis(event)}. It is not evidence that the event came from that address.`;
    return (
      <div className="flex flex-col leading-tight" title={tooltip}>
        <span className={resolvedIPClassName(classification)}>{ip}<IpCommentNote ip={event.IPAddress} /></span>
        <span className="text-gray-500 italic text-[10px] cursor-help">possibly {event.GuessedIP}</span>
      </div>
    );
  }

  // App-only read by an app that never signed in as the user: say WHO read, not where.
  const reader = appOnlyReader(event);
  if (reader) {
    const tooltip = `Read by ${reader} on its own authority (no user session). The address is Microsoft's; `
      + `the app never signed in as this user, so no user or attacker IP is attributed to this event.`;
    return (
      <div className="flex flex-col leading-tight" title={tooltip}>
        <span className={resolvedIPClassName(classification)}>{ip}<IpCommentNote ip={event.IPAddress} /></span>
        <span className="text-gray-500 text-[10px] cursor-help">app-only {'\u00B7'} {reader}</span>
      </div>
    );
  }

  if (method === 'UserIPCorrelation-MultiIP') {
    const allIPs = event.AllResolvedIPs || '';
    const resolvedList = allIPs ? allIPs.split(',').map(s => s.trim()).filter(Boolean) : [];
    const multiIPTooltip = `Backend service event (${event.ClientInfo || event.ServiceAppsSeen || 'backend service'}) \u2014 ` +
      `the user had ${resolvedList.length} IPs during the investigation period. ` +
      `Any of these IPs could be the source. Cannot determine which one triggered this backend action.` +
      (event.OriginalIPAddress ? `\nOriginal backend IP: ${event.OriginalIPAddress}` : '');
    if (resolvedList.length > 0) {
      // For large multi-IP lists, hide MS-Infra and Safe IPs with a compact notation
      if (resolvedList.length > 10) {
        const { interesting, hiddenMsInfra, hiddenSafe } = partitionResolvedIPs(resolvedList, safeIPSet, ipLookup, ipClassifications);
        const totalHidden = hiddenMsInfra.length + hiddenSafe.length;
        const hiddenParts = [];
        if (hiddenMsInfra.length > 0) hiddenParts.push(`${hiddenMsInfra.length} MS-Infra`);
        if (hiddenSafe.length > 0) hiddenParts.push(`${hiddenSafe.length} Safe`);
        const hiddenNote = hiddenParts.length > 0 ? `+ ${hiddenParts.join(', ')} hidden` : '';
        const headerText = totalHidden > 0
          ? `\u26A0 ${interesting.length} IPs (${totalHidden} hidden) \u24D8`
          : `\u26A0 ${resolvedList.length} user IPs \u24D8`;
        const displayIPs = interesting.length > 0 ? interesting : [];
        return (
          <div className="flex flex-col leading-tight">
            <span className="text-amber-400 text-[9px] cursor-help mb-0.5" title={multiIPTooltip}>
              {headerText}
            </span>
            {displayIPs.map((rip) => {
              const cls = classifyIP(rip, safeIPSet, ipLookup, ipClassifications);
              const label = resolvedIPLabel(cls);
              return (
                <span key={rip}>
                  <span className={resolvedIPClassName(cls)}>{rip}</span>
                  {label && <span className="text-[9px] text-gray-500 ml-1">{label}</span>}
                  <IpCommentNote ip={rip} />
                </span>
              );
            })}
            {interesting.length === 0 && (
              <span className="text-gray-400 text-[10px]">{event.OriginalIPAddress || ip}</span>
            )}
            {hiddenNote && <span className="text-gray-500 text-[9px] mt-0.5">{hiddenNote}</span>}
          </div>
        );
      }
      return (
        <div className="flex flex-col leading-tight">
          <span className="text-amber-400 text-[9px] cursor-help mb-0.5" title={multiIPTooltip}>
            {'\u26A0'} {resolvedList.length} user IPs {'\u24D8'}
          </span>
          {resolvedList.map((rip) => {
            const cls = classifyIP(rip, safeIPSet, ipLookup, ipClassifications);
            const label = resolvedIPLabel(cls);
            return (
              <span key={rip}>
                <span className={resolvedIPClassName(cls)}>{rip}</span>
                {label && <span className="text-[9px] text-gray-500 ml-1">{label}</span>}
                <IpCommentNote ip={rip} />
              </span>
            );
          })}
        </div>
      );
    }
    return (
      <span className="text-gray-300 cursor-help" title={multiIPTooltip}>
        {ip} <span className="text-amber-400 text-[10px]">{'\u21C4'}</span>
      </span>
    );
  }


  // Inferred IP — event had no actual ClientIP, resolved via temporal proximity to another event
  if (!event.IPAddress && event.InferredIP) {
    const inferredClassification = classifyIP(event.InferredIP, safeIPSet, ipLookup, ipClassifications);
    const conf = event.InferredIPConfidence || 'low';
    const confSymbol = conf === 'high' ? '\u2713' : conf === 'medium' ? '~' : '?';
    const confColor = conf === 'high' ? 'text-green-400' : conf === 'medium' ? 'text-amber-400' : 'text-gray-500';
    const delta = event.InferredIPTimeDelta != null ? `${event.InferredIPTimeDelta}s` : '';
    const paired = event.InferredIPPairedLogType || '';
    const tooltip = `Inferred IP (${conf} confidence${delta ? `, ${delta} delta` : ''}${paired ? `, from ${paired} event` : ''}) \u2014 ` +
      `this event had no ClientIP; IP was inferred from the nearest event by the same user.`;
    return (
      <span className={`italic ${resolvedIPClassName(inferredClassification)}`} title={tooltip}>
        {event.InferredIP} <span className={`text-[9px] font-medium ml-0.5 ${confColor}`}>{confSymbol}</span><IpCommentNote ip={event.InferredIP} />
      </span>
    );
  }

  return <span className={resolvedIPClassName(classification)}>{ip}<IpCommentNote ip={event.IPAddress} /></span>;
}

function AllEventsView({
  events, ipLookup,
  knownApps = [], apps = [],
  hideKnownApps = true,
  hideMsSubstrate = false,
  hideOutlookNoise = false,
  revealSubjects = false,
  onToggleRevealSubjects,
  safeIPSet = new Set(),
  ipClassifications = {},
  filterQuery, setFilterQuery,
  sortColumn, setSortColumn,
  sortDirection, setSortDirection,
  expandedRow, setExpandedRow,
  selectedTz = 'ET',
  dismissedEventIds = new Set(),
  onDismissEvent,
  urlClassifications = {},
  compromisedSessions = new Map(),
  sessionClassifications = {},
  onCycleSessionStatus,
  onSetIPStatus,
  meetingContext = null,
}) {
  const ALL_EVENTS_COLUMNS = useMemo(() => getAllEventsColumns(selectedTz), [selectedTz]);
  const [rawLogEvent, setRawLogEvent] = useState(null);
  // Column filter state: { columnId: selected value string }
  const [columnFilters, setColumnFilters] = useState({});

  // Build set of known app IDs (from live API + blob enterprise apps)
  const knownAppIdSet = useMemo(() => {
    const set = new Set();
    knownApps.forEach(ka => set.add(ka.appId));
    apps.forEach(a => { if (a.isKnown || a.isKnownApp) set.add(a.appId || a.AppId); });
    return set;
  }, [knownApps, apps]);

  const handleSort = (colId) => {
    if (sortColumn === colId) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(colId);
      setSortDirection('asc');
    }
  };

  const handleColumnFilterChange = useCallback((columnId, value) => {
    setColumnFilters(prev => {
      const next = { ...prev };
      if (value) { next[columnId] = value; } else { delete next[columnId]; }
      return next;
    });
    setExpandedRow(null);
  }, [setExpandedRow]);

  // Column resize — real-time direct DOM resize
  // Widths map to getAllEventsColumns order: Timestamp, Description, Type, Application, Client IP, AAD Session ID, Country, Risk
  const [colWidths, setColWidths] = useState([200, 440, 116, 200, 340, 300, 120, 160]);
  const thRefs = useRef([]);
  const tableRef = useRef(null);

  const startResize = useCallback((e, colIdx) => {
    e.preventDefault();
    const th = thRefs.current[colIdx];
    const table = tableRef.current;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    const startTableWidth = table ? table.offsetWidth : 0;
    let lastWidth = startWidth;

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (moveEvt) => {
      lastWidth = Math.max(50, startWidth + moveEvt.clientX - startX);
      th.style.width = `${lastWidth}px`;
      if (table) table.style.width = `${startTableWidth + (lastWidth - startWidth)}px`;
    };

    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setColWidths(prev => { const n = [...prev]; n[colIdx] = lastWidth; return n; });
      if (table) table.style.width = '';
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Enrich events with computed fields
  const enrichedEvents = useMemo(() => {
    const targetUpn = events.find(e => e.UserPrincipalName)?.UserPrincipalName || '';
    return events.map(evt => {
      let desc = generateDescription(evt, revealSubjects);
      if (targetUpn) {
        const upnLower = targetUpn.toLowerCase();
        const descLower = desc.toLowerCase();
        // Exchange/Directory/SharePoint: "{upn} verb..." → "verb..." (case-insensitive)
        if (descLower.startsWith(upnLower + ' ')) {
          desc = desc.slice(targetUpn.length + 1);
        } else {
          // Sign-in: "SignIn for {upn} on ..." → "SignIn on ..." (case-insensitive)
          const idx = descLower.indexOf(` for ${upnLower}`);
          if (idx !== -1) {
            desc = desc.slice(0, idx) + desc.slice(idx + ` for ${targetUpn}`.length);
          }
        }
      }
      return {
        ...evt,
        _description: desc,
        _country: getCountry(evt, ipLookup),
        // Sign-in SessionId and Exchange/SharePoint AADSessionId are one
        // namespace here, matching compromisedSessions and the AAD Session IDs tab.
        _sessionId: evt.SessionId || evt.AADSessionId || '',
        // KEEP even though the VPN/Proxy column was removed in v1.138.0 — this is
        // read by mustShowDespiteImplicitTrust (eventUtils) to force-show
        // threat-flagged events that the known-app / MS-substrate filters would
        // otherwise hide, and by storyGenerator for reports. Not display-only.
        _vpnProxyFlags: getVPNProxyFlags(evt, ipLookup),
        _riskScore: evt._eventType === 'SignIn'
          ? (evt.RiskScore ?? evt.Risk_Total ?? null)
          : (evt.IPAddress && ipLookup[evt.IPAddress]?.RiskLevel != null
              ? ipLookup[evt.IPAddress].RiskLevel
              : (!evt.IPAddress && evt.InferredIP && ipLookup[evt.InferredIP]?.RiskLevel != null
                  ? ipLookup[evt.InferredIP].RiskLevel
                  : null)),
        // ONE label function, shared with the reports (utils/allEventsRollups.js): a
        // non-interactive row shows its app list rather than the literal "TokenRefresh",
        // and a rolled period shows every app it spans rather than its first one.
        _appName: rowAppLabel(evt),
        _appId: evt.ApplicationId || evt.ClientAppId || null,
        // Directory audit: expose the detail (e.g. "User registered Passkey")
        // + descriptive operation label so the expanded row's Detail field shows.
        ...(evt._eventType === 'DirectoryAudit'
          ? (() => {
              const d = getDirAuditDetail(evt);
              return { _detail: d, _displayOp: getDisplayOperation({ ...evt, _detail: d }) };
            })()
          : {}),
      };
    });
  }, [events, ipLookup, revealSubjects]);

  // Build unique values for filterable columns (from enriched but unfiltered data)
  const columnOptions = useMemo(() => {
    const opts = {};

    // Type
    const types = new Set();
    enrichedEvents.forEach(evt => { if (evt._eventType) types.add(evt._eventType); });
    opts['_eventType'] = [...types].sort();

    // Application / Operation
    const appNames = new Set();
    enrichedEvents.forEach(evt => { if (evt._appName) appNames.add(evt._appName); });
    opts['_appName'] = [...appNames].sort();

    // Client IP
    const ips = new Set();
    enrichedEvents.forEach(evt => { if (evt.IPAddress) ips.add(evt.IPAddress); });
    opts['IPAddress'] = [...ips].sort();

    // AAD Session ID — '(none)' is a real, useful choice here: a third of events
    // carry no session at all and those are the ones that can't be attributed.
    const sids = new Set();
    enrichedEvents.forEach(evt => { sids.add(evt._sessionId || '(none)'); });
    opts['_sessionId'] = [...sids].sort();

    // Country
    const countries = new Set();
    enrichedEvents.forEach(evt => {
      countries.add(evt._country || '(empty)');
    });
    opts['_country'] = [...countries].sort();

    return opts;
  }, [enrichedEvents]);

  // Count known app events for display
  const knownAppEventCount = useMemo(() => {
    if (knownAppIdSet.size === 0) return 0;
    return enrichedEvents.filter(evt => evt._appId && knownAppIdSet.has(evt._appId)).length;
  }, [enrichedEvents, knownAppIdSet]);

  const processedEvents = useMemo(() => {
    let result = enrichedEvents;

    // Filter out known app events if toggle is on — but preserve events from
    // IPs with threat indicators (VPN/proxy/TOR/compromised/anonymous) or manually
    // classified as suspect/malicious, so attacker activity using legitimate apps
    // (e.g., Outlook) is never hidden. The exception logic is centralized in
    // mustShowDespiteImplicitTrust so the timeline and report generator never diverge.
    if (hideKnownApps && knownAppIdSet.size > 0) {
      result = result.filter(evt => {
        // A portal alert is the detection engine's verdict, not routine app activity.
        if (evt._eventType === CASE_ALERT_EVENT_TYPE) return true;
        if (!evt._appId || !knownAppIdSet.has(evt._appId)) return true;
        return mustShowDespiteImplicitTrust(evt, { ipClassifications, ipLookup, compromisedSessions });
      });
    }

    // Hide MS-substrate background reads (only the MS-Substrate class) unless a threat
    // signal forces visibility. Substrate is sessionless by construction, so the
    // must-show check falls through to the IP/threat-flag checks.
    if (hideMsSubstrate) {
      result = result.filter(evt =>
        evt.AppTrustClass !== 'MS-Substrate' || mustShowDespiteImplicitTrust(evt, { ipClassifications, ipLookup, compromisedSessions })
      );
    }

    // Hide noisy Outlook background operations (OutlookItemPrioritized, etc.) when
    // the toggle is on. These carry no user/attacker signal — a pure operation match.
    if (hideOutlookNoise) {
      result = result.filter(evt => !OUTLOOK_NOISE_OPS.has(evt.Operation));
    }

    // Apply column filters (single-select)
    if (columnFilters['_eventType']) {
      result = result.filter(evt => evt._eventType === columnFilters['_eventType']);
    }
    if (columnFilters['_appName']) {
      result = result.filter(evt => evt._appName === columnFilters['_appName']);
    }
    if (columnFilters['IPAddress']) {
      result = result.filter(evt => evt.IPAddress === columnFilters['IPAddress']);
    }
    if (columnFilters['_sessionId']) {
      result = result.filter(evt => (evt._sessionId || '(none)') === columnFilters['_sessionId']);
    }
    if (columnFilters['_country']) {
      result = result.filter(evt => (evt._country || '(empty)') === columnFilters['_country']);
    }

    // Apply text filter
    if (filterQuery.trim()) {
      const q = filterQuery.toLowerCase();
      result = result.filter(evt =>
        (evt.EventTimeUTC && evt.EventTimeUTC.toLowerCase().includes(q)) ||
        (evt.UserPrincipalName && evt.UserPrincipalName.toLowerCase().includes(q)) ||
        (evt.IPAddress && evt.IPAddress.toLowerCase().includes(q)) ||
        (evt.InferredIP && evt.InferredIP.toLowerCase().includes(q)) ||
        (evt.OriginalIPAddress && evt.OriginalIPAddress.toLowerCase().includes(q)) ||
        (evt.GuessedIP && evt.GuessedIP.toLowerCase().includes(q)) ||
        (evt.AllResolvedIPs && evt.AllResolvedIPs.toLowerCase().includes(q)) ||
        (evt._description && evt._description.toLowerCase().includes(q)) ||
        (evt.ApplicationName && evt.ApplicationName.toLowerCase().includes(q)) ||
        (evt.Operation && evt.Operation.toLowerCase().includes(q)) ||
        (evt._appId && evt._appId.toLowerCase().includes(q)) ||
        (evt.ClientAppName && evt.ClientAppName.toLowerCase().includes(q)) ||
        // The app shown as "app-only · <app>" in the IP cell (utils/guessedIp.js).
        (evt.CallingAppName && evt.CallingAppName.toLowerCase().includes(q)) ||
        // Paste a session id from the AAD Session IDs tab straight into the
        // filter box to pull up everything that rode that session.
        (evt._sessionId && evt._sessionId.toLowerCase().includes(q))
      );
    }

    // Sort
    result = [...result].sort((a, b) => {
      let aVal, bVal;
      if (sortColumn === '_sessionId') {
        // Sessionless events sort last in either direction — they are the rows
        // that can't be attributed, not the "lowest" ones.
        aVal = a._sessionId || '￿'; bVal = b._sessionId || '￿';
      } else if (sortColumn === '_country') {
        aVal = a._country; bVal = b._country;
      } else if (sortColumn === 'RiskScore') {
        aVal = a._riskScore; bVal = b._riskScore;
      } else {
        aVal = a[sortColumn]; bVal = b[sortColumn];
      }

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      let cmp;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        cmp = aVal - bVal;
      } else {
        cmp = String(aVal).localeCompare(String(bVal));
      }
      return sortDirection === 'desc' ? -cmp : cmp;
    });

    return result;
  }, [enrichedEvents, columnFilters, filterQuery, sortColumn, sortDirection, hideKnownApps, hideMsSubstrate, hideOutlookNoise, knownAppIdSet, ipClassifications, ipLookup, compromisedSessions]);

  useEffect(() => { setCurrentPage(1); }, [processedEvents]);

  // Count active column filters for display
  const activeFilterCount = Object.keys(columnFilters).length;
  const totalPages = Math.max(1, Math.ceil(processedEvents.length / pageSize));
  const pagedEvents = processedEvents.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input
            type="text"
            placeholder="Filter events (user, IP, description, app...)"
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            className="input-field pl-9"
          />
        </div>
        <div className="flex items-center gap-3">
          {activeFilterCount > 0 && (
            <button
              onClick={() => setColumnFilters({})}
              className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
            >
              Clear all filters
            </button>
          )}
          <span className="text-xs text-gray-500 flex-shrink-0">
            {processedEvents.length === events.length
              ? `${events.length} event${events.length !== 1 ? 's' : ''}`
              : `Showing ${processedEvents.length} of ${events.length} events (${events.length - processedEvents.length} hidden by filter)`
            }
          </span>
          {/* Table-wide view actions: one place, beside the search, for controls that
              act on the whole timeline rather than on one row. */}
          <div className="flex items-center gap-2 pl-3 border-l border-gray-700" data-testid="all-events-actions">
            <SubjectRevealButton revealSubjects={revealSubjects} onToggle={onToggleRevealSubjects} size="toolbar" />
            <MeetingBulkHideButton events={events} meetingContext={meetingContext} dismissedEventIds={dismissedEventIds} onDismissEvent={onDismissEvent} />
          </div>
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table
            ref={tableRef}
            className="text-sm"
            style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0) + 32 + (onDismissEvent ? 28 : 0)}px` }}
          >
            <thead>
              <tr className="border-b border-gray-800">
                <th className="pl-3 pr-1 py-3" style={{ width: '28px' }} />
                <th className="px-2 py-3" style={{ width: '28px' }} />
                {onDismissEvent && <th className="px-2 py-3" style={{ width: '28px' }} />}
                {ALL_EVENTS_COLUMNS.map((col, colIdx) => (
                  <th
                    key={col.id}
                    ref={(el) => { thRefs.current[colIdx] = el; }}
                    className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap relative select-none"
                    style={{ width: `${colWidths[colIdx]}px` }}
                  >
                    <div className="flex items-center gap-1 pr-2 overflow-hidden">
                      <span
                        onClick={col.sortable ? () => handleSort(col.id) : undefined}
                        className={`truncate ${col.sortable ? 'cursor-pointer hover:text-gray-300' : ''}`}
                      >
                        {col.label}
                      </span>
                      {col.sortable && sortColumn === col.id && (
                        sortDirection === 'asc'
                          ? <ChevronUpIcon className="h-3 w-3 text-blue-400 flex-shrink-0" />
                          : <ChevronDownIcon className="h-3 w-3 text-blue-400 flex-shrink-0" />
                      )}
                    </div>
                    {/* Resize handle */}
                    <div
                      className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                      onMouseDown={(e) => startResize(e, colIdx)}
                    >
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
              </tr>
              {/* Filter row with <select> dropdowns */}
              <tr className="border-b border-gray-700 bg-gray-900/50">
                <th className="pl-3 pr-1 py-1.5" />
                <th className="px-2 py-1.5" />
                {onDismissEvent && <th className="px-2 py-1.5" />}
                {ALL_EVENTS_COLUMNS.map((col) => (
                  <th key={`filter-${col.id}`} className="px-3 py-1.5">
                    {col.filterable ? (
                      <ColumnFilterSelect
                        columnId={col.id}
                        options={columnOptions[col.id] || []}
                        value={columnFilters[col.id] || ''}
                        onChange={handleColumnFilterChange}
                      />
                    ) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {pagedEvents.map((event, index) => {
                const isExpanded = expandedRow === index;
                const badge = getTypeBadge(event._eventType);
                const dismissIds = event._collapsedIds && event._collapsedIds.length ? event._collapsedIds : [event.EventId];
                const isDismissed = dismissIds.every((id) => dismissedEventIds.has(id));
                return (
                  <React.Fragment key={event.EventId || index}>
                    <tr
                      onClick={() => setExpandedRow(isExpanded ? null : index)}
                      className={`hover:bg-gray-800/50 cursor-pointer transition-colors ${isDismissed ? 'opacity-40' : ''}`}
                    >
                      <td className="pl-3 pr-1 py-3" style={{ width: '28px' }}>
                        <ChevronRightIcon
                          className={`h-3.5 w-3.5 text-gray-600 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        />
                      </td>
                      <td className="px-2 py-3" style={{ width: '28px' }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); setRawLogEvent(event); }}
                          className="p-0.5 text-gray-600 hover:text-blue-400 transition-colors"
                          title="View raw log"
                        >
                          <CodeBracketIcon className="h-3.5 w-3.5" />
                        </button>
                      </td>
                      {onDismissEvent && (
                        <td className="px-2 py-3 text-center" style={{ width: '28px' }}>
                          <button
                            onClick={(e) => { e.stopPropagation(); onDismissEvent(dismissIds); }}
                            className={`p-0.5 transition-colors ${isDismissed ? 'text-orange-400 hover:text-gray-300' : 'text-gray-400 hover:text-orange-400'}`}
                            title={isDismissed ? 'Restore this event' : 'Dismiss this event'}
                          >
                            {isDismissed ? <EyeIcon className="h-3.5 w-3.5" /> : <EyeSlashIcon className="h-3.5 w-3.5" />}
                          </button>
                        </td>
                      )}
                      <td className="px-4 py-3 text-gray-300 whitespace-nowrap font-mono text-xs overflow-hidden">
                        {formatTimestamp(event.EventTimeUTC, selectedTz)}
                      </td>
                      <td className={`px-4 py-3 text-xs overflow-hidden ${event._eventType === 'UrlClick' ? getUrlStatusTextClass(urlClassifications[event.EventId] || 'default') : 'text-gray-300'}`}>
                        <span className="line-clamp-2" title={event._description}>
                          {event._description}
                          {(event._eventType === 'SecurityAlert' || event._eventType === 'AlertEvidence' || event._eventType === 'RiskDetection') && event.ResultStatus && (
                            <span className={`ml-2 inline-flex items-center px-1.5 py-0 rounded text-[9px] font-semibold border ${
                              event.ResultStatus === 'Success'
                                ? 'bg-green-900/60 text-green-300 border-green-700/50'
                                : event.ResultStatus === 'Failed'
                                  ? 'bg-red-900/60 text-red-300 border-red-700/50'
                                  : 'bg-gray-800 text-gray-400 border-gray-700'
                            }`}>
                              Sign-in: {event.ResultStatus}
                            </span>
                          )}
                          <RecoveredLegBadge event={event} />
                          {/^https?:\/\//i.test(String(event.ApplicationId || '')) && (
                            <span
                              className="ml-2 inline-flex items-center px-1.5 py-0 rounded text-[9px] font-semibold border bg-red-900/50 text-red-300 border-red-700/50"
                              title="The sign-in application is a URL, not a normal application ID — the hallmark of an adversary-in-the-middle phishing kit (e.g. a spoofed *.microsoftonline.com.* landing domain). Treat as high-attention regardless of risk score; verify the source IP and assume token theft."
                            >
                              phishing-kit app
                            </span>
                          )}
                          <AliasIdentityBadge event={event} />
                          <CorrelatedActionBadge event={event} />
                          <AttackerBadge event={event} />
                          {isPostCompromiseSessionEvent(event, compromisedSessions) && (
                            <span
                              className="ml-2 inline-flex items-center px-1.5 py-0 rounded text-[9px] font-semibold border bg-purple-900/40 text-purple-300 border-purple-800/50"
                              title="This event happened on a session that was touched by a malicious IP after the first compromise — could be the attacker using a stolen token even when the source IP looks legitimate."
                            >
                              session-compromised
                            </span>
                          )}
                          {(() => {
                            const sid = event.SessionId || event.AADSessionId;
                            if (!sid) return null;
                            if (compromisedSessions && compromisedSessions.has(sid)) return null;
                            if (!sessionClassifications || sessionClassifications[sid] !== 'safe') return null;
                            return (
                              <span
                                className="ml-2 inline-flex items-center px-1.5 py-0 rounded text-[9px] font-semibold border bg-green-900/40 text-green-300 border-green-800/50"
                                title="Session classified as Safe by the analyst. Toggle 'Safe sessions hidden' to hide all events under this session."
                              >
                                session-safe
                              </span>
                            );
                          })()}
                        </span>
                      </td>
                      <td className="px-4 py-3 overflow-hidden">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${badge.classes}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs overflow-hidden">
                        <span className={`truncate block ${event._appId && knownAppIdSet.has(event._appId) ? 'text-green-400' : 'text-gray-300'}`} title={event._appName}>{event._appName || '\u2014'}</span>
                        {event.AppTrustClass === 'MS-Substrate' && (
                          <span className="mt-1 inline-block" title="Microsoft substrate (background) read \u2014 self-issued resource token, sessionless, read-only. Toggle 'MS substrate hidden' to hide these.">
                            <Badge tone="neutral" dot>MS substrate</Badge>
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap font-mono text-xs overflow-hidden group">
                        <ResolvedIPCell event={event} safeIPSet={safeIPSet} ipLookup={ipLookup} ipClassifications={ipClassifications} />
                        <CopyButton text={event.IPAddress || event.InferredIP} />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap overflow-hidden group">
                        {event._sessionId ? (
                          <>
                            <span
                              className={`font-mono text-[11px] ${
                                compromisedSessions && compromisedSessions.has(event._sessionId)
                                  ? 'text-red-300'
                                  : (sessionClassifications && sessionClassifications[event._sessionId] === 'safe'
                                      ? 'text-green-300'
                                      : 'text-gray-400')
                              }`}
                              title={event._sessionId}
                            >
                              {event._sessionId}
                            </span>
                            <CopyButton text={event._sessionId} />
                          </>
                        ) : (
                          <span className="text-gray-700 text-xs" title="This event carries no session id \u2014 it cannot be attributed to a session">&mdash;</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-400 whitespace-nowrap text-xs overflow-hidden">
                        {event._country || '\u2014'}
                      </td>
                      <td className="px-4 py-3 overflow-hidden">
                        <div className="flex items-center gap-1 flex-wrap">
                          {event._riskScore != null ? (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRiskBgClass(event._riskScore)}`}>
                              {event._riskScore}
                            </span>
                          ) : (
                            <span className="text-gray-700">{'\u2014'}</span>
                          )}
                          <AnomalyBadges event={event} />
                        </div>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="bg-gray-800/30">
                        <td colSpan={ALL_EVENTS_COLUMNS.length + (onDismissEvent ? 3 : 2)} className="px-6 py-4">
                          {event._meeting
                            ? <ExpandedMeetingDetail event={event} selectedTz={selectedTz} ipClassifications={ipClassifications} safeIPSet={safeIPSet} meetingContext={meetingContext} dismissedEventIds={dismissedEventIds} onDismissEvent={onDismissEvent} />
                            : event._eventType === CASE_ALERT_EVENT_TYPE
                            ? <ExpandedCaseAlertDetail event={event} selectedTz={selectedTz} />
                            : event._eventType === 'SignIn'
                            ? <ExpandedSignInDetail event={event} safeIPSet={safeIPSet} ipLookup={ipLookup} ipClassifications={ipClassifications} sessionClassifications={sessionClassifications} compromisedSessions={compromisedSessions} onCycleSessionStatus={onCycleSessionStatus} onSetIPStatus={onSetIPStatus} />
                            : <ExpandedEventDetail event={event} ipLookup={ipLookup} selectedTz={selectedTz} safeIPSet={safeIPSet} ipClassifications={ipClassifications} revealSubjects={revealSubjects} sessionClassifications={sessionClassifications} compromisedSessions={compromisedSessions} onCycleSessionStatus={onCycleSessionStatus} onSetIPStatus={onSetIPStatus} />
                          }
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}

              {processedEvents.length === 0 && (
                <tr>
                  <td colSpan={ALL_EVENTS_COLUMNS.length + (onDismissEvent ? 3 : 2)} className="px-6 py-12 text-center">
                    <p className="text-gray-500">
                      {filterQuery ? 'No events match the current filter.' : 'No events found.'}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={processedEvents.length}
          onPageChange={(p) => { setCurrentPage(p); setExpandedRow(null); }}
          onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); setExpandedRow(null); }}
        />
      </div>
      {rawLogEvent && <RawLogModal event={rawLogEvent} onClose={() => setRawLogEvent(null)} />}
    </div>
  );
}

// ── "Sign-In Logs" Detailed View ────────────────────────────────────────────

function getSignInColumns(tz) {
  return [
    { id: 'EventTimeUTC', label: `Timestamp (${getTzLabel(tz)})`, sortable: true, filterable: false },
    { id: 'ApplicationName', label: 'Application', sortable: true, filterable: true },
    { id: 'IPAddress', label: 'IP Address', sortable: true, filterable: true },
    { id: '_vpnProxy', label: 'VPN/Proxy/TOR', sortable: false, filterable: true },
    { id: 'IP_Country', label: 'Country', sortable: true, filterable: true },
    { id: 'DeviceOS', label: 'OS / Browser', sortable: true, filterable: true },
    { id: 'DeviceHostname', label: 'Device', sortable: true, filterable: true },
    { id: 'ResultStatus', label: 'Status', sortable: true, filterable: true },
    { id: 'RiskScore', label: 'Risk', sortable: true, filterable: true },
  ];
}

function SignInLogsView({
  events,
  ipLookup,
  safeIPSet = new Set(),
  ipClassifications = {},
  filterQuery, setFilterQuery,
  sortColumn, setSortColumn,
  sortDirection, setSortDirection,
  expandedRow, setExpandedRow,
  selectedTz = 'ET',
  compromisedSessions = new Map(),
  sessionClassifications = {},
  onCycleSessionStatus,
}) {
  const SIGNIN_COLUMNS = useMemo(() => getSignInColumns(selectedTz), [selectedTz]);
  const handleSort = (colId) => {
    if (sortColumn === colId) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(colId);
      setSortDirection('asc');
    }
  };

  // Column filter state: { columnId: selected value string }
  const [columnFilters, setColumnFilters] = useState({});

  const handleColumnFilterChange = useCallback((columnId, value) => {
    setColumnFilters(prev => {
      const next = { ...prev };
      if (value) { next[columnId] = value; } else { delete next[columnId]; }
      return next;
    });
    setExpandedRow(null);
  }, [setExpandedRow]);

  const columnOptions = useMemo(() => {
    const opts = {};
    const apps = new Set();
    const ips = new Set();
    const vpnLabels = new Set();
    const countries = new Set();
    const oses = new Set();
    const hostnames = new Set();
    const statuses = new Set();
    const riskLabelSet = new Set();
    events.forEach(evt => {
      if (evt.ApplicationName) apps.add(evt.ApplicationName);
      if (evt.IPAddress) ips.add(evt.IPAddress);
      const isVPN = evt.IsVPN || evt.RiskFactors?.IsVPN || false;
      const isProxy = evt.IsProxy || evt.RiskFactors?.IsProxy || false;
      const isTOR = evt.IsTOR || evt.RiskFactors?.IsTOR || false;
      const isCompromised = evt.IsCompromised || evt.RiskFactors?.IsCompromised || false;
      const isAnonymous = evt.IsAnonymous || evt.RiskFactors?.IsAnonymous || false;
      if (isVPN) vpnLabels.add('VPN');
      if (isProxy) vpnLabels.add('Proxy');
      if (isTOR) vpnLabels.add('TOR');
      if (isCompromised) vpnLabels.add('Compromised');
      if (isAnonymous) vpnLabels.add('Anonymous');
      if (!isVPN && !isProxy && !isTOR && !isCompromised && !isAnonymous) vpnLabels.add('None');
      const country = evt.IP_Country || evt.Location?.Country;
      if (country) countries.add(country);
      if (evt.DeviceOS) oses.add(evt.DeviceOS);
      if (evt.DeviceHostname) hostnames.add(evt.DeviceHostname);
      if (evt.ResultStatus) statuses.add(evt.ResultStatus);
      riskLabelSet.add(getRiskLabel(evt.RiskScore ?? evt.Risk_Total ?? null));
    });
    opts['ApplicationName'] = [...apps].sort();
    opts['IPAddress'] = [...ips].sort();
    opts['_vpnProxy'] = [...vpnLabels].sort();
    opts['IP_Country'] = [...countries].sort();
    opts['DeviceOS'] = [...oses].sort();
    opts['DeviceHostname'] = [...hostnames].sort();
    opts['ResultStatus'] = [...statuses].sort();
    opts['RiskScore'] = ['High', 'Medium', 'Low', 'Trusted', 'Unknown'].filter(l => riskLabelSet.has(l));
    return opts;
  }, [events]);

  // Column resize
  const [colWidths, setColWidths] = useState([170, 180, 130, 120, 120, 160, 150, 100, 100]);
  const thRefs = useRef([]);
  const tableRef = useRef(null);

  const startResize = useCallback((e, colIdx) => {
    e.preventDefault();
    const th = thRefs.current[colIdx];
    const table = tableRef.current;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    const startTableWidth = table ? table.offsetWidth : 0;
    let lastWidth = startWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (moveEvt) => {
      lastWidth = Math.max(50, startWidth + moveEvt.clientX - startX);
      th.style.width = `${lastWidth}px`;
      if (table) table.style.width = `${startTableWidth + (lastWidth - startWidth)}px`;
    };
    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setColWidths(prev => { const n = [...prev]; n[colIdx] = lastWidth; return n; });
      if (table) table.style.width = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const processedEvents = useMemo(() => {
    let result = [...events];

    // Apply column filters (single-select)
    if (columnFilters['ApplicationName']) {
      result = result.filter(evt => evt.ApplicationName === columnFilters['ApplicationName']);
    }
    if (columnFilters['IPAddress']) {
      result = result.filter(evt => evt.IPAddress === columnFilters['IPAddress']);
    }
    if (columnFilters['_vpnProxy']) {
      const v = columnFilters['_vpnProxy'];
      result = result.filter(evt => {
        const isVPN = evt.IsVPN || evt.RiskFactors?.IsVPN || false;
        const isProxy = evt.IsProxy || evt.RiskFactors?.IsProxy || false;
        const isTOR = evt.IsTOR || evt.RiskFactors?.IsTOR || false;
        const isCompromised = evt.IsCompromised || evt.RiskFactors?.IsCompromised || false;
        const isAnonymous = evt.IsAnonymous || evt.RiskFactors?.IsAnonymous || false;
        if (v === '__any_threat__') return isVPN || isProxy || isTOR || isCompromised || isAnonymous;
        if (v === 'VPN') return isVPN;
        if (v === 'Proxy') return isProxy;
        if (v === 'TOR') return isTOR;
        if (v === 'Compromised') return isCompromised;
        if (v === 'Anonymous') return isAnonymous;
        if (v === 'None') return !isVPN && !isProxy && !isTOR && !isCompromised && !isAnonymous;
        return true;
      });
    }
    if (columnFilters['IP_Country']) {
      result = result.filter(evt => (evt.IP_Country || evt.Location?.Country || '') === columnFilters['IP_Country']);
    }
    if (columnFilters['DeviceOS']) {
      result = result.filter(evt => evt.DeviceOS === columnFilters['DeviceOS']);
    }
    if (columnFilters['DeviceHostname']) {
      result = result.filter(evt => evt.DeviceHostname === columnFilters['DeviceHostname']);
    }
    if (columnFilters['ResultStatus']) {
      result = result.filter(evt => evt.ResultStatus === columnFilters['ResultStatus']);
    }
    if (columnFilters['RiskScore']) {
      result = result.filter(evt => {
        const score = evt.RiskScore ?? evt.Risk_Total ?? null;
        return getRiskLabel(score) === columnFilters['RiskScore'];
      });
    }

    if (filterQuery.trim()) {
      const q = filterQuery.toLowerCase();
      result = result.filter(evt =>
        (evt.EventTimeUTC && evt.EventTimeUTC.toLowerCase().includes(q)) ||
        (evt.UserPrincipalName && evt.UserPrincipalName.toLowerCase().includes(q)) ||
        (evt.ApplicationName && evt.ApplicationName.toLowerCase().includes(q)) ||
        (evt.IPAddress && evt.IPAddress.toLowerCase().includes(q)) ||
        (evt.OriginalIPAddress && evt.OriginalIPAddress.toLowerCase().includes(q)) ||
        (evt.GuessedIP && evt.GuessedIP.toLowerCase().includes(q)) ||
        (evt.AllResolvedIPs && evt.AllResolvedIPs.toLowerCase().includes(q)) ||
        (evt.DeviceOS && evt.DeviceOS.toLowerCase().includes(q)) ||
        (evt.DeviceHostname && evt.DeviceHostname.toLowerCase().includes(q)) ||
        (evt.ResultStatus && evt.ResultStatus.toLowerCase().includes(q))
      );
    }

    result.sort((a, b) => {
      let aVal, bVal;
      if (sortColumn === 'IP_Country') {
        aVal = a.IP_Country || a.Location?.Country || '';
        bVal = b.IP_Country || b.Location?.Country || '';
      } else if (sortColumn === 'RiskScore') {
        aVal = a.RiskScore ?? a.Risk_Total ?? 0;
        bVal = b.RiskScore ?? b.Risk_Total ?? 0;
      } else {
        aVal = a[sortColumn]; bVal = b[sortColumn];
      }

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      let cmp;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        cmp = aVal - bVal;
      } else {
        cmp = String(aVal).localeCompare(String(bVal));
      }
      return sortDirection === 'desc' ? -cmp : cmp;
    });

    return result;
  }, [events, columnFilters, filterQuery, sortColumn, sortDirection]);

  useEffect(() => { setCurrentPage(1); }, [processedEvents]);

  const activeFilterCount = Object.keys(columnFilters).length;
  const totalPages = Math.max(1, Math.ceil(processedEvents.length / pageSize));
  const pagedEvents = processedEvents.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  if (events.length === 0) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-500">No sign-in events found.</p>
      </div>
    );
  }

  return (
    <div>
      <FilterBar
        filterQuery={filterQuery}
        setFilterQuery={setFilterQuery}
        shownCount={processedEvents.length}
        totalCount={events.length}
        placeholder="Filter sign-in events..."
        activeFilterCount={activeFilterCount}
        onClearFilters={() => setColumnFilters({})}
        note={events.some(evt => evt.IsCorrelationEvent) ? 'Includes lookback sign-ins for IP correlation' : null}
      />

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table
            ref={tableRef}
            className="text-sm"
            style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0) + 32}px` }}
          >
            <thead>
              <tr className="border-b border-gray-800">
                <th className="px-3 py-3" style={{ width: '32px' }} />
                {SIGNIN_COLUMNS.map((col, colIdx) => (
                  <th
                    key={col.id}
                    ref={(el) => { thRefs.current[colIdx] = el; }}
                    className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap relative select-none"
                    style={{ width: `${colWidths[colIdx]}px` }}
                  >
                    <div className="flex items-center gap-1 pr-2 overflow-hidden">
                      <span
                        onClick={col.sortable ? () => handleSort(col.id) : undefined}
                        className={`truncate ${col.sortable ? 'cursor-pointer hover:text-gray-300' : ''}`}
                      >
                        {col.label}
                      </span>
                      {col.sortable && sortColumn === col.id && (
                        sortDirection === 'asc'
                          ? <ChevronUpIcon className="h-3 w-3 text-blue-400 flex-shrink-0" />
                          : <ChevronDownIcon className="h-3 w-3 text-blue-400 flex-shrink-0" />
                      )}
                    </div>
                    <div
                      className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                      onMouseDown={(e) => startResize(e, colIdx)}
                    >
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
              </tr>
              {/* Filter row with <select> dropdowns */}
              <tr className="border-b border-gray-700 bg-gray-900/50">
                <th className="px-3 py-1.5" />
                {SIGNIN_COLUMNS.map((col) => (
                  <th key={`filter-${col.id}`} className="px-3 py-1.5">
                    {col.filterable ? (
                      <ColumnFilterSelect
                        columnId={col.id}
                        options={columnOptions[col.id] || []}
                        value={columnFilters[col.id] || ''}
                        onChange={handleColumnFilterChange}
                        extraOptions={col.id === '_vpnProxy' ? VPN_PROXY_EXTRA_OPTIONS : undefined}
                      />
                    ) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {pagedEvents.map((event, index) => {
                const isExpanded = expandedRow === index;
                const country = event.IP_Country || event.Location?.Country || '';
                const riskScore = event.RiskScore ?? event.Risk_Total ?? null;
                const browser = parseBrowser(event.UserAgent);
                const vpnFlags = {
                  isVPN: event.IsVPN || event.RiskFactors?.IsVPN || false,
                  isProxy: event.IsProxy || event.RiskFactors?.IsProxy || false,
                  isTOR: event.IsTOR || event.RiskFactors?.IsTOR || false,
                  isCompromised: event.IsCompromised || event.RiskFactors?.IsCompromised || false,
                  isAnonymous: event.IsAnonymous || event.RiskFactors?.IsAnonymous || false,
                };

                return (
                  <React.Fragment key={event.EventId || index}>
                    <tr
                      onClick={() => setExpandedRow(isExpanded ? null : index)}
                      className="hover:bg-gray-800/50 cursor-pointer transition-colors"
                    >
                      <td className="px-3 py-3">
                        <ChevronRightIcon
                          className={`h-3.5 w-3.5 text-gray-600 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        />
                      </td>
                      <td className="px-4 py-3 text-gray-300 whitespace-nowrap font-mono text-xs overflow-hidden">
                        {formatTimestamp(event.EventTimeUTC, selectedTz)}
                      </td>
                      <td className="px-4 py-3 text-gray-300 whitespace-nowrap text-xs overflow-hidden">
                        <span className="truncate block" title={event.ApplicationName}>{event.ApplicationName || '\u2014'}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap font-mono text-xs overflow-hidden group">
                        <ResolvedIPCell event={event} safeIPSet={safeIPSet} ipLookup={ipLookup} ipClassifications={ipClassifications} />
                        <CopyButton text={event.IPAddress || event.InferredIP} />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap overflow-hidden">
                        <VPNProxyBadges flags={vpnFlags} />
                      </td>
                      <td className="px-4 py-3 text-gray-400 whitespace-nowrap text-xs overflow-hidden">
                        {country || '\u2014'}
                      </td>
                      <td className="px-4 py-3 text-gray-400 whitespace-nowrap text-xs overflow-hidden">
                        {event.DeviceOS || 'Unknown'} / {browser}
                      </td>
                      <td className="px-4 py-3 text-gray-400 whitespace-nowrap text-xs overflow-hidden">
                        {event.DeviceHostname || '\u2014'}
                      </td>
                      <td className="px-4 py-3 overflow-hidden">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`text-xs font-medium ${getStatusClass(event.ResultStatus)}`}>
                            {event.ResultStatus || '\u2014'}
                          </span>
                          {isPostCompromiseSessionEvent(event, compromisedSessions) && (
                            <span
                              className="inline-flex items-center px-1.5 py-0 rounded text-[9px] font-semibold border bg-purple-900/40 text-purple-300 border-purple-800/50"
                              title="This sign-in is on a session that was touched by a malicious IP at or after the first compromise."
                            >
                              session-compromised
                            </span>
                          )}
                          {(() => {
                            const sid = event.SessionId;
                            if (!sid) return null;
                            if (compromisedSessions && compromisedSessions.has(sid)) return null;
                            if (!sessionClassifications || sessionClassifications[sid] !== 'safe') return null;
                            return (
                              <span
                                className="inline-flex items-center px-1.5 py-0 rounded text-[9px] font-semibold border bg-green-900/40 text-green-300 border-green-800/50"
                                title="Session classified as Safe by the analyst."
                              >
                                session-safe
                              </span>
                            );
                          })()}
                          <RecoveredLegBadge event={event} className="" />
                          {/^https?:\/\//i.test(String(event.ApplicationId || '')) && (
                            <span
                              className="ml-2 inline-flex items-center px-1.5 py-0 rounded text-[9px] font-semibold border bg-red-900/50 text-red-300 border-red-700/50"
                              title="The sign-in application is a URL, not a normal application ID — the hallmark of an adversary-in-the-middle phishing kit (e.g. a spoofed *.microsoftonline.com.* landing domain). Treat as high-attention regardless of risk score; verify the source IP and assume token theft."
                            >
                              phishing-kit app
                            </span>
                          )}
                          <AliasIdentityBadge event={event} className="" />
                        </div>
                      </td>
                      <td className="px-4 py-3 overflow-hidden">
                        <div className="flex items-center gap-1 flex-wrap">
                          {riskScore != null ? (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRiskBgClass(riskScore)}`}>
                              {riskScore} - {getRiskLabel(riskScore)}
                            </span>
                          ) : (
                            <span className="text-gray-600">{'\u2014'}</span>
                          )}
                          <AnomalyBadges event={event} />
                        </div>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="bg-gray-800/30">
                        <td colSpan={SIGNIN_COLUMNS.length + 1} className="px-6 py-4">
                          <ExpandedSignInDetail event={event} safeIPSet={safeIPSet} ipLookup={ipLookup} ipClassifications={ipClassifications} sessionClassifications={sessionClassifications} compromisedSessions={compromisedSessions} onCycleSessionStatus={onCycleSessionStatus} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}

            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={processedEvents.length}
          onPageChange={(p) => { setCurrentPage(p); setExpandedRow(null); }}
          onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); setExpandedRow(null); }}
        />
      </div>
    </div>
  );
}

// ── SharePoint Files View ───────────────────────────────────────────────────

const SP_FILES_COLUMNS = [
  { id: 'fileName', label: 'File Name', filterable: false },
  { id: 'sitePath', label: 'Site / Path', filterable: true },
  { id: 'operations', label: 'Operations', filterable: true },
  { id: 'accessCount', label: 'Count', filterable: false },
  { id: 'ips', label: 'IPs', filterable: false },
  { id: 'firstAccessed', label: 'First', filterable: false },
  { id: 'lastAccessed', label: 'Last', filterable: false },
];

function SharePointFilesView({ exchangeEvents, ipLookup, selectedTz = 'ET' }) {
  const SP_FILES_WIDTHS_INIT = [200, 250, 200, 80, 80, 170, 170];
  const [colWidths, setColWidths] = useState(SP_FILES_WIDTHS_INIT);
  const thRefs = useRef([]);
  const tableRef = useRef(null);

  const startResize = useCallback((e, colIdx) => {
    e.preventDefault();
    const th = thRefs.current[colIdx];
    const table = tableRef.current;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    const startTableWidth = table ? table.offsetWidth : 0;
    let lastWidth = startWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (moveEvt) => {
      lastWidth = Math.max(50, startWidth + moveEvt.clientX - startX);
      th.style.width = `${lastWidth}px`;
      if (table) table.style.width = `${startTableWidth + (lastWidth - startWidth)}px`;
    };
    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setColWidths(prev => { const n = [...prev]; n[colIdx] = lastWidth; return n; });
      if (table) table.style.width = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const fileData = useMemo(() => {
    const fileEvents = exchangeEvents.filter(e => FILE_OPERATIONS.has(e.Operation));

    // Deduplicate by file name
    const fileMap = {};
    fileEvents.forEach(evt => {
      const folders = parseFolders(evt.Folders);
      const fileName = folders?.FileName || 'Unknown file';
      const sitePath = folders?.Path || '';
      const key = `${sitePath}|${fileName}`;

      if (!fileMap[key]) {
        fileMap[key] = {
          fileName,
          sitePath,
          operations: new Set(),
          accessCount: 0,
          ips: new Set(),
          firstAccessed: evt.EventTimeUTC,
          lastAccessed: evt.EventTimeUTC,
        };
      }
      const entry = fileMap[key];
      entry.operations.add(evt.Operation);
      entry.accessCount++;
      if (evt.IPAddress) entry.ips.add(evt.IPAddress);
      if (evt.EventTimeUTC < entry.firstAccessed) entry.firstAccessed = evt.EventTimeUTC;
      if (evt.EventTimeUTC > entry.lastAccessed) entry.lastAccessed = evt.EventTimeUTC;
    });

    return Object.values(fileMap).map(f => ({
      ...f,
      operations: [...f.operations],
      ips: [...f.ips],
    })).sort((a, b) => b.accessCount - a.accessCount);
  }, [exchangeEvents]);

  // Column filters: { columnId: selected value string }
  const [columnFilters, setColumnFilters] = useState({});

  const handleColumnFilterChange = useCallback((columnId, value) => {
    setColumnFilters(prev => {
      const next = { ...prev };
      if (value) { next[columnId] = value; } else { delete next[columnId]; }
      return next;
    });
  }, []);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const columnOptions = useMemo(() => {
    const sites = new Set();
    const allOps = new Set();
    fileData.forEach(row => {
      if (row.sitePath) sites.add(row.sitePath);
      row.operations.forEach(op => allOps.add(op));
    });
    return { sitePath: [...sites].sort(), operations: [...allOps].sort() };
  }, [fileData]);

  const filteredFileData = useMemo(() => {
    let result = fileData;
    if (columnFilters['sitePath']) {
      result = result.filter(row => row.sitePath === columnFilters['sitePath']);
    }
    if (columnFilters['operations']) {
      result = result.filter(row => row.operations.includes(columnFilters['operations']));
    }
    return result;
  }, [fileData, columnFilters]);

  const activeFilterCount = Object.keys(columnFilters).length;

  useEffect(() => { setCurrentPage(1); }, [filteredFileData]);
  const totalPages = Math.max(1, Math.ceil(filteredFileData.length / pageSize));
  const pagedFileData = filteredFileData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  if (fileData.length === 0) {
    return (
      <div className="card text-center py-16">
        <FolderIcon className="h-12 w-12 text-gray-700 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-400 mb-2">
          No SharePoint File Activity
        </h3>
        <p className="text-sm text-gray-600 max-w-md mx-auto">
          File access events (FileAccessed, FilePreviewed, etc.) will appear here when found in the investigation data.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-gray-500">
          {filteredFileData.length === fileData.length
            ? `${fileData.length} unique file${fileData.length !== 1 ? 's' : ''} accessed`
            : `Showing ${filteredFileData.length} of ${fileData.length} files`
          }
        </p>
        {activeFilterCount > 0 && (
          <button onClick={() => setColumnFilters({})} className="text-xs text-blue-400 hover:text-blue-300 transition-colors">
            Clear column filters
          </button>
        )}
      </div>
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table
            ref={tableRef}
            className="text-sm"
            style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0)}px` }}
          >
            <thead>
              <tr className="border-b border-gray-800">
                {SP_FILES_COLUMNS.map((col, colIdx) => (
                  <th
                    key={col.id}
                    ref={(el) => { thRefs.current[colIdx] = el; }}
                    className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap relative select-none"
                    style={{ width: `${colWidths[colIdx]}px` }}
                  >
                    <div className="flex items-center gap-1 pr-2 overflow-hidden">
                      <span className="truncate">{col.label}</span>
                    </div>
                    <div
                      className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                      onMouseDown={(e) => startResize(e, colIdx)}
                    >
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
              </tr>
              {/* Filter row */}
              <tr className="border-b border-gray-700 bg-gray-900/50">
                {SP_FILES_COLUMNS.map((col) => (
                  <th key={`filter-${col.id}`} className="px-3 py-1.5">
                    {col.filterable ? (
                      <ColumnFilterSelect
                        columnId={col.id}
                        options={columnOptions[col.id] || []}
                        value={columnFilters[col.id] || ''}
                        onChange={handleColumnFilterChange}
                      />
                    ) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {pagedFileData.map((file, i) => (
                <tr key={i} className="hover:bg-gray-800/50 transition-colors">
                  <td className="px-4 py-3 text-gray-200 text-xs font-medium overflow-hidden">
                    <span className="truncate block" title={file.fileName}>{file.fileName}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs overflow-hidden" title={file.sitePath}>
                    <span className="truncate block">{file.sitePath || '\u2014'}</span>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div className="flex flex-wrap gap-1">
                      {file.operations.map(op => (
                        <span key={op} className="px-1.5 py-0.5 rounded bg-gray-800 text-gray-400 text-[10px]">
                          {op}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center text-gray-300 font-mono text-xs">
                    {file.accessCount}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="font-mono text-xs text-gray-400" title={file.ips.join(', ')}>
                      {file.ips.length}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                    {formatTimestampShort(file.firstAccessed, selectedTz)}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                    {formatTimestampShort(file.lastAccessed, selectedTz)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={filteredFileData.length}
          onPageChange={setCurrentPage}
          onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
        />
      </div>
    </div>
  );
}

// ── SharePoint Lists View ───────────────────────────────────────────────────

const SP_LISTS_COLUMNS = [
  { id: 'listName', label: 'List Name', filterable: false },
  { id: 'path', label: 'Path / URL', filterable: true },
  { id: 'operations', label: 'Operations', filterable: true },
  { id: 'viewCount', label: 'Views', filterable: false },
  { id: 'ips', label: 'IPs', filterable: false },
  { id: 'firstViewed', label: 'First', filterable: false },
  { id: 'lastViewed', label: 'Last', filterable: false },
];

function SharePointListsView({ exchangeEvents, ipLookup, selectedTz = 'ET' }) {
  const SP_LISTS_WIDTHS_INIT = [200, 300, 200, 80, 80, 170, 170];
  const [colWidths, setColWidths] = useState(SP_LISTS_WIDTHS_INIT);
  const thRefs = useRef([]);
  const tableRef = useRef(null);

  const startResize = useCallback((e, colIdx) => {
    e.preventDefault();
    const th = thRefs.current[colIdx];
    const table = tableRef.current;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    const startTableWidth = table ? table.offsetWidth : 0;
    let lastWidth = startWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (moveEvt) => {
      lastWidth = Math.max(50, startWidth + moveEvt.clientX - startX);
      th.style.width = `${lastWidth}px`;
      if (table) table.style.width = `${startTableWidth + (lastWidth - startWidth)}px`;
    };
    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setColWidths(prev => { const n = [...prev]; n[colIdx] = lastWidth; return n; });
      if (table) table.style.width = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const listData = useMemo(() => {
    const listEvents = exchangeEvents.filter(e => LIST_OPERATIONS.has(e.Operation));

    const listMap = {};
    listEvents.forEach(evt => {
      const folders = parseFolders(evt.Folders);
      const path = folders?.Path || 'Unknown list';
      const listName = path.split('/').pop() || path;
      const key = path;

      if (!listMap[key]) {
        listMap[key] = {
          listName,
          path,
          operations: new Set(),
          viewCount: 0,
          ips: new Set(),
          firstViewed: evt.EventTimeUTC,
          lastViewed: evt.EventTimeUTC,
        };
      }
      const entry = listMap[key];
      entry.operations.add(evt.Operation);
      entry.viewCount++;
      if (evt.IPAddress) entry.ips.add(evt.IPAddress);
      if (evt.EventTimeUTC < entry.firstViewed) entry.firstViewed = evt.EventTimeUTC;
      if (evt.EventTimeUTC > entry.lastViewed) entry.lastViewed = evt.EventTimeUTC;
    });

    return Object.values(listMap).map(l => ({
      ...l,
      operations: [...l.operations],
      ips: [...l.ips],
    })).sort((a, b) => b.viewCount - a.viewCount);
  }, [exchangeEvents]);

  // Column filters: { columnId: selected value string }
  const [columnFilters, setColumnFilters] = useState({});

  const handleColumnFilterChange = useCallback((columnId, value) => {
    setColumnFilters(prev => {
      const next = { ...prev };
      if (value) { next[columnId] = value; } else { delete next[columnId]; }
      return next;
    });
  }, []);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const columnOptions = useMemo(() => {
    const paths = new Set();
    const allOps = new Set();
    listData.forEach(row => {
      if (row.path) paths.add(row.path);
      row.operations.forEach(op => allOps.add(op));
    });
    return { path: [...paths].sort(), operations: [...allOps].sort() };
  }, [listData]);

  const filteredListData = useMemo(() => {
    let result = listData;
    if (columnFilters['path']) {
      result = result.filter(row => row.path === columnFilters['path']);
    }
    if (columnFilters['operations']) {
      result = result.filter(row => row.operations.includes(columnFilters['operations']));
    }
    return result;
  }, [listData, columnFilters]);

  const activeFilterCount = Object.keys(columnFilters).length;

  useEffect(() => { setCurrentPage(1); }, [filteredListData]);
  const totalPages = Math.max(1, Math.ceil(filteredListData.length / pageSize));
  const pagedListData = filteredListData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  if (listData.length === 0) {
    return (
      <div className="card text-center py-16">
        <ListBulletIcon className="h-12 w-12 text-gray-700 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-400 mb-2">
          No SharePoint List Activity
        </h3>
        <p className="text-sm text-gray-600 max-w-md mx-auto">
          List view events will appear here when found in the investigation data.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-gray-500">
          {filteredListData.length === listData.length
            ? `${listData.length} unique list${listData.length !== 1 ? 's' : ''} accessed`
            : `Showing ${filteredListData.length} of ${listData.length} lists`
          }
        </p>
        {activeFilterCount > 0 && (
          <button onClick={() => setColumnFilters({})} className="text-xs text-blue-400 hover:text-blue-300 transition-colors">
            Clear column filters
          </button>
        )}
      </div>
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table
            ref={tableRef}
            className="text-sm"
            style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0)}px` }}
          >
            <thead>
              <tr className="border-b border-gray-800">
                {SP_LISTS_COLUMNS.map((col, colIdx) => (
                  <th
                    key={col.id}
                    ref={(el) => { thRefs.current[colIdx] = el; }}
                    className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap relative select-none"
                    style={{ width: `${colWidths[colIdx]}px` }}
                  >
                    <div className="flex items-center gap-1 pr-2 overflow-hidden">
                      <span className="truncate">{col.label}</span>
                    </div>
                    <div
                      className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                      onMouseDown={(e) => startResize(e, colIdx)}
                    >
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
              </tr>
              {/* Filter row */}
              <tr className="border-b border-gray-700 bg-gray-900/50">
                {SP_LISTS_COLUMNS.map((col) => (
                  <th key={`filter-${col.id}`} className="px-3 py-1.5">
                    {col.filterable ? (
                      <ColumnFilterSelect
                        columnId={col.id}
                        options={columnOptions[col.id] || []}
                        value={columnFilters[col.id] || ''}
                        onChange={handleColumnFilterChange}
                      />
                    ) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {pagedListData.map((list, i) => (
                <tr key={i} className="hover:bg-gray-800/50 transition-colors">
                  <td className="px-4 py-3 text-gray-200 text-xs font-medium overflow-hidden">
                    <span className="truncate block" title={list.listName}>{list.listName}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs overflow-hidden" title={list.path}>
                    <span className="truncate block">{list.path || '\u2014'}</span>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div className="flex flex-wrap gap-1">
                      {list.operations.map(op => (
                        <span key={op} className="px-1.5 py-0.5 rounded bg-gray-800 text-gray-400 text-[10px]">
                          {op}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center text-gray-300 font-mono text-xs">
                    {list.viewCount}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="font-mono text-xs text-gray-400" title={list.ips.join(', ')}>
                      {list.ips.length}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                    {formatTimestampShort(list.firstViewed, selectedTz)}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                    {formatTimestampShort(list.lastViewed, selectedTz)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={filteredListData.length}
          onPageChange={setCurrentPage}
          onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
        />
      </div>
    </div>
  );
}

// ── Directory Audit View ─────────────────────────────────────────────────────

function getDirectoryColumns(tz) {
  return [
    { id: 'EventTimeUTC', label: `Timestamp (${getTzLabel(tz)})`, sortable: true, filterable: false },
    { id: 'Operation', label: 'Operation', sortable: true, filterable: true },
    { id: '_detail', label: 'Detail', sortable: true, filterable: false },
    { id: 'IPAddress', label: 'IP Address', sortable: true, filterable: true },
    { id: 'ResultStatus', label: 'Status', sortable: true, filterable: true },
  ];
}

function DirectoryAuditView({
  events,
  safeIPSet,
  ipClassifications = {},
  filterQuery, setFilterQuery,
  sortColumn, setSortColumn,
  sortDirection, setSortDirection,
  expandedRow, setExpandedRow,
  selectedTz = 'ET',
}) {
  const [rawLogEvent, setRawLogEvent] = useState(null);
  const DIRECTORY_COLUMNS = useMemo(() => getDirectoryColumns(selectedTz), [selectedTz]);
  const handleSort = (colId) => {
    if (sortColumn === colId) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(colId);
      setSortDirection('asc');
    }
  };

  // Column filter state: { columnId: selected value string }
  const [columnFilters, setColumnFilters] = useState({});

  const handleColumnFilterChange = useCallback((columnId, value) => {
    setColumnFilters(prev => {
      const next = { ...prev };
      if (value) { next[columnId] = value; } else { delete next[columnId]; }
      return next;
    });
    setExpandedRow(null);
  }, [setExpandedRow]);

  const columnOptions = useMemo(() => {
    const opts = {};
    const ops = new Set();
    const ips = new Set();
    const statuses = new Set();
    events.forEach(evt => {
      if (evt.Operation) ops.add(evt.Operation);
      if (evt.IPAddress) ips.add(evt.IPAddress);
      if (evt.ResultStatus) statuses.add(evt.ResultStatus);
    });
    opts['Operation'] = [...ops].sort();
    opts['IPAddress'] = [...ips].sort();
    opts['ResultStatus'] = [...statuses].sort();
    return opts;
  }, [events]);

  // Column resize
  const [colWidths, setColWidths] = useState([170, 250, 220, 130, 100]);
  const thRefs = useRef([]);
  const tableRef = useRef(null);

  const startResize = useCallback((e, colIdx) => {
    e.preventDefault();
    const th = thRefs.current[colIdx];
    const table = tableRef.current;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    const startTableWidth = table ? table.offsetWidth : 0;
    let lastWidth = startWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (moveEvt) => {
      lastWidth = Math.max(50, startWidth + moveEvt.clientX - startX);
      th.style.width = `${lastWidth}px`;
      if (table) table.style.width = `${startTableWidth + (lastWidth - startWidth)}px`;
    };
    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setColWidths(prev => { const n = [...prev]; n[colIdx] = lastWidth; return n; });
      if (table) table.style.width = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const processedEvents = useMemo(() => {
    // Enrich registration events with device names from nearby "Update user" events
    enrichDirAuditWithDeviceNames(events);
    // Filter out routine Azure MFA auto-updates (version bumps, timestamp refreshes)
    const filtered = events.filter(evt => !isAutoMfaUpdate(evt));
    let result = filtered.map(evt => {
      const _detail = getDirAuditDetail(evt);
      return { ...evt, _detail, _displayOp: getDisplayOperation({ ...evt, _detail }) };
    });

    // Apply column filters (single-select)
    if (columnFilters['Operation']) {
      result = result.filter(evt => evt.Operation === columnFilters['Operation']);
    }
    if (columnFilters['IPAddress']) {
      result = result.filter(evt => evt.IPAddress === columnFilters['IPAddress']);
    }
    if (columnFilters['ResultStatus']) {
      result = result.filter(evt => evt.ResultStatus === columnFilters['ResultStatus']);
    }

    if (filterQuery.trim()) {
      const q = filterQuery.toLowerCase();
      result = result.filter(evt =>
        (evt.EventTimeUTC && evt.EventTimeUTC.toLowerCase().includes(q)) ||
        (evt.UserPrincipalName && evt.UserPrincipalName.toLowerCase().includes(q)) ||
        (evt.Operation && evt.Operation.toLowerCase().includes(q)) ||
        (evt._displayOp && evt._displayOp.toLowerCase().includes(q)) ||
        (evt._detail && evt._detail.toLowerCase().includes(q)) ||
        (evt.IPAddress && evt.IPAddress.toLowerCase().includes(q)) ||
        (evt.OriginalIPAddress && evt.OriginalIPAddress.toLowerCase().includes(q)) ||
        (evt.GuessedIP && evt.GuessedIP.toLowerCase().includes(q)) ||
        (evt.AllResolvedIPs && evt.AllResolvedIPs.toLowerCase().includes(q)) ||
        (evt.ResultStatus && evt.ResultStatus.toLowerCase().includes(q))
      );
    }

    result.sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDirection === 'desc' ? -cmp : cmp;
    });

    return result;
  }, [events, columnFilters, filterQuery, sortColumn, sortDirection]);

  useEffect(() => { setCurrentPage(1); }, [processedEvents]);

  const activeFilterCount = Object.keys(columnFilters).length;
  const totalPages = Math.max(1, Math.ceil(processedEvents.length / pageSize));
  const pagedEvents = processedEvents.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  if (events.length === 0) {
    return (
      <div className="card text-center py-16">
        <ShieldExclamationIcon className="h-12 w-12 text-gray-700 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-400 mb-2">No Directory Audit Events</h3>
        <p className="text-sm text-gray-600 max-w-md mx-auto">
          AAD directory operations (password changes, role assignments, user updates) will appear here when found in the investigation data.
        </p>
      </div>
    );
  }

  return (
    <div>
      <FilterBar
        filterQuery={filterQuery}
        setFilterQuery={setFilterQuery}
        shownCount={processedEvents.length}
        totalCount={events.length}
        placeholder="Filter directory audit events..."
        activeFilterCount={activeFilterCount}
        onClearFilters={() => setColumnFilters({})}
      />

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table
            ref={tableRef}
            className="text-sm"
            style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0) + 32}px` }}
          >
            <thead>
              <tr className="border-b border-gray-800">
                <th className="px-3 py-3" style={{ width: '32px' }} />
                {DIRECTORY_COLUMNS.map((col, colIdx) => (
                  <th
                    key={col.id}
                    ref={(el) => { thRefs.current[colIdx] = el; }}
                    className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap relative select-none"
                    style={{ width: `${colWidths[colIdx]}px` }}
                  >
                    <div className="flex items-center gap-1 pr-2 overflow-hidden">
                      <span
                        onClick={col.sortable ? () => handleSort(col.id) : undefined}
                        className={`truncate ${col.sortable ? 'cursor-pointer hover:text-gray-300' : ''}`}
                      >
                        {col.label}
                      </span>
                      {col.sortable && sortColumn === col.id && (
                        sortDirection === 'asc'
                          ? <ChevronUpIcon className="h-3 w-3 text-blue-400 flex-shrink-0" />
                          : <ChevronDownIcon className="h-3 w-3 text-blue-400 flex-shrink-0" />
                      )}
                    </div>
                    <div
                      className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                      onMouseDown={(e) => startResize(e, colIdx)}
                    >
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
              </tr>
              {/* Filter row with <select> dropdowns */}
              <tr className="border-b border-gray-700 bg-gray-900/50">
                <th className="px-3 py-1.5" />
                {DIRECTORY_COLUMNS.map((col) => (
                  <th key={`filter-${col.id}`} className="px-3 py-1.5">
                    {col.filterable ? (
                      <ColumnFilterSelect
                        columnId={col.id}
                        options={columnOptions[col.id] || []}
                        value={columnFilters[col.id] || ''}
                        onChange={handleColumnFilterChange}
                      />
                    ) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {pagedEvents.map((event, index) => {
                const isExpanded = expandedRow === index;
                return (
                  <React.Fragment key={event.EventId || index}>
                    <tr
                      onClick={() => setExpandedRow(isExpanded ? null : index)}
                      className="hover:bg-gray-800/50 cursor-pointer transition-colors"
                    >
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1">
                          <ChevronRightIcon
                            className={`h-3.5 w-3.5 text-gray-600 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                          />
                          {event.RawLog && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setRawLogEvent(event); }}
                              className="p-0.5 text-gray-600 hover:text-blue-400 transition-colors"
                              title="View raw log"
                            >
                              <CodeBracketIcon className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-300 whitespace-nowrap font-mono text-xs overflow-hidden">
                        {formatTimestamp(event.EventTimeUTC, selectedTz)}
                      </td>
                      <td className="px-4 py-3 text-gray-300 text-xs overflow-hidden">
                        <span className="line-clamp-1" title={event.Operation}>{event._displayOp || event.Operation || '\u2014'}</span>
                        <CorrelatedActionBadge event={event} className="" />
                        <AttackerBadge event={event} />
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs overflow-hidden">
                        {event._detail ? (
                          <span className="line-clamp-1" title={event._detail}>{event._detail}</span>
                        ) : (
                          <span className="text-gray-700">{'\u2014'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap font-mono text-xs overflow-hidden">
                        {event.IsServiceInitiated ? (
                          <span className="text-gray-500 cursor-help" title={`Microsoft service IP (unattributable) \u2014 processed by ${serviceInitiatorLabel(event)}`}>
                            {event.IPAddress || '\u2014'} <span className="text-purple-400 text-[9px] font-medium ml-0.5">{'\u24E2'}</span>
                          </span>
                        ) : (
                          <span className={event.IPAddress ? resolvedIPClassName(classifyIP(event.IPAddress, safeIPSet, null, ipClassifications)) : 'text-gray-300'}>
                            {event.IPAddress || '\u2014'}<IpCommentNote ip={event.IPAddress} />
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 overflow-hidden">
                        <span className={`text-xs font-medium ${getStatusClass(event.ResultStatus)}`}>
                          {event.ResultStatus || '\u2014'}
                        </span>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="bg-gray-800/30">
                        <td colSpan={DIRECTORY_COLUMNS.length + 1} className="px-6 py-4">
                          <AnalystMarkControl event={event} />
                          <CorrelatedActionPanel event={event} selectedTz={selectedTz} />
      <AccessPeriodPanel event={event} selectedTz={selectedTz} />
                          {event.IsServiceInitiated && (
                            <div className="mb-3 px-3 py-2 bg-purple-900/20 border border-purple-800/30 rounded-lg text-xs text-purple-300">
                              <span className="font-medium">Microsoft Service IP (unattributable)</span> — This action was processed server-side by {serviceInitiatorLabel(event)}.
                              The IP address belongs to Microsoft's infrastructure, not to the user or any attacker.
                              Investigate <em>what</em> was done (target app, permissions granted), not <em>where</em> it came from.
                            </div>
                          )}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                            <DetailItem label="Timestamp (UTC)" value={event.EventTimeUTC ? format(parseISO(event.EventTimeUTC), 'MMM d, yyyy HH:mm:ss') : null} />
                            <DetailItem label="User" value={event.UserPrincipalName} />
                            <DetailItem label="Operation" value={event._displayOp || event.Operation} />
                            {event._displayOp && event._displayOp !== event.Operation && <DetailItem label="Raw Operation" value={event.Operation} />}
                            {event._detail && <DetailItem label="Detail" value={event._detail} />}
                            <div>
                              <span className="text-xs text-gray-600 block">IP Address</span>
                              {event.IsServiceInitiated ? (
                                <span className="text-sm break-all text-gray-500">
                                  {event.IPAddress || '\u2014'} <span className="text-purple-400 text-[9px]">{'\u24E2'}</span>
                                </span>
                              ) : (
                                <span className={`text-sm break-all ${event.IPAddress ? resolvedIPClassName(classifyIP(event.IPAddress, safeIPSet, null, ipClassifications)) : 'text-gray-300'}`}>{event.IPAddress || '\u2014'}<IpCommentNote ip={event.IPAddress} /></span>
                              )}
                            </div>
                            <DetailItem label="Status" value={event.ResultStatus} />
                            <DetailItem label="Workload" value={event.Workload} />
                            {event.ClientInfo && <DetailItem label="Client Info" value={event.ClientInfo} />}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}

            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={processedEvents.length}
          onPageChange={(p) => { setCurrentPage(p); setExpandedRow(null); }}
          onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); setExpandedRow(null); }}
        />
      </div>
      {rawLogEvent && <RawLogModal event={rawLogEvent} onClose={() => setRawLogEvent(null)} />}
    </div>
  );
}

// ── Message Trace View ──────────────────────────────────────────────────────

function getMsgTraceColumns(tz) {
  return [
    { id: '_phishFlag', label: 'Phish', sortable: false, filterable: false },
    { id: 'EventTimeUTC', label: `Timestamp (${getTzLabel(tz)})`, sortable: true, filterable: false },
    { id: 'Direction', label: 'Direction', sortable: true, filterable: true },
    { id: 'SenderAddress', label: 'Sender', sortable: true, filterable: false },
    { id: 'RecipientAddress', label: 'Recipient', sortable: true, filterable: false },
    { id: 'Subject', label: 'Subject', sortable: true, filterable: false },
    { id: 'Status', label: 'Status', sortable: true, filterable: true },
    { id: 'IPAddress', label: 'Sending Server IP', sortable: true, filterable: true, tooltip: 'IP of the mail server that handed the message to Exchange Online — not the sender\u2019s device IP' },
  ];
}

// Derive a message-trace event's Network Message ID — the join key to URL-click
// events (url_click.NetworkMessageId === the message's silver record Id). The
// runbook serializes message-trace rows with a COMPOSITE EventId (`{silverId}|{recipient}`)
// and does not emit a dedicated NetworkMessageId field, so recover the GUID from the
// EventId prefix (preferring explicit fields if a future runbook adds them).
function getMsgTraceNetworkId(evt) {
  if (!evt) return '';
  if (evt.NetworkMessageId) return evt.NetworkMessageId;
  if (evt.SilverRecordId) return evt.SilverRecordId;
  const id = evt.EventId || '';
  return id.includes('|') ? id.split('|')[0] : id;
}

function MessageTraceView({
  events,
  safeIPSet,
  ipClassifications = {},
  filterQuery, setFilterQuery,
  sortColumn, setSortColumn,
  sortDirection, setSortDirection,
  expandedRow, setExpandedRow,
  selectedTz = 'ET',
  phishingFlags = [],
  onTogglePhishingFlag,
  revealSubjects = false,
  onToggleRevealSubjects,
  onFindUrlClicks,
}) {
  const MSG_TRACE_COLUMNS = useMemo(() => getMsgTraceColumns(selectedTz), [selectedTz]);
  const handleSort = (colId) => {
    if (sortColumn === colId) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(colId);
      setSortDirection('asc');
    }
  };

  const [columnFilters, setColumnFilters] = useState({});

  const handleColumnFilterChange = useCallback((columnId, value) => {
    setColumnFilters(prev => {
      const next = { ...prev };
      if (value) { next[columnId] = value; } else { delete next[columnId]; }
      return next;
    });
    setExpandedRow(null);
  }, [setExpandedRow]);

  const columnOptions = useMemo(() => {
    const opts = {};
    const dirs = new Set();
    const statuses = new Set();
    const ips = new Set();
    // Built from the COLLAPSED rows so the Status dropdown offers 'mixed' when that is
    // what the rows say -- offering only 'delivered'/'failed' would make a mixed message
    // unreachable by the filter that appears to cover it.
    collapseMessageTrace(events).forEach(evt => {
      if (evt.Direction) dirs.add(evt.Direction);
      if (evt.Status) statuses.add(evt.Status);
      if (evt.IPAddress) ips.add(evt.IPAddress);
    });
    opts['Direction'] = [...dirs].sort();
    opts['Status'] = [...statuses].sort();
    opts['IPAddress'] = [...ips].sort();
    return opts;
  }, [events]);

  // Build a Set of flagged event IDs for O(1) lookup
  const flaggedEventIds = useMemo(() => new Set(phishingFlags.map(f => f.eventId)), [phishingFlags]);

  const [colWidths, setColWidths] = useState([60, 160, 80, 180, 180, 200, 110, 120]);
  const thRefs = useRef([]);
  const tableRef = useRef(null);

  const startResize = useCallback((e, colIdx) => {
    e.preventDefault();
    const th = thRefs.current[colIdx];
    const table = tableRef.current;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    const startTableWidth = table ? table.offsetWidth : 0;
    let lastWidth = startWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (moveEvt) => {
      lastWidth = Math.max(50, startWidth + moveEvt.clientX - startX);
      th.style.width = `${lastWidth}px`;
      if (table) table.style.width = `${startTableWidth + (lastWidth - startWidth)}px`;
    };
    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setColWidths(prev => { const n = [...prev]; n[colIdx] = lastWidth; return n; });
      if (table) table.style.width = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const processedEvents = useMemo(() => {
    // One row per MESSAGE. The feed is per-recipient, so a send to three addresses was
    // three rows differing only in the recipient cell; every recipient is still shown,
    // each with its own delivery status.
    let result = collapseMessageTrace(events);

    if (columnFilters['Direction']) {
      result = result.filter(evt => evt.Direction === columnFilters['Direction']);
    }
    if (columnFilters['Status']) {
      result = result.filter(evt => evt.Status === columnFilters['Status']);
    }
    if (columnFilters['IPAddress']) {
      result = result.filter(evt => evt.IPAddress === columnFilters['IPAddress']);
    }

    if (filterQuery.trim()) {
      const q = filterQuery.toLowerCase();
      result = result.filter(evt =>
        (evt.EventTimeUTC && evt.EventTimeUTC.toLowerCase().includes(q)) ||
        (evt.SenderAddress && evt.SenderAddress.toLowerCase().includes(q)) ||
        (evt.RecipientAddress && evt.RecipientAddress.toLowerCase().includes(q)) ||
        // Search EVERY recipient of a collapsed row: searching only the primary would
        // silently fail to find a message addressed to the person being looked for.
        (Array.isArray(evt._recipients) && evt._recipients.some(r => (r.address || '').toLowerCase().includes(q))) ||
        (evt.Subject && evt.Subject.toLowerCase().includes(q)) ||
        (evt.Status && evt.Status.toLowerCase().includes(q)) ||
        (evt.IPAddress && evt.IPAddress.toLowerCase().includes(q)) ||
        (evt.EventId && evt.EventId.toLowerCase().includes(q)) ||
        (evt.NetworkMessageId && evt.NetworkMessageId.toLowerCase().includes(q)) ||
        (evt.MessageId && evt.MessageId.toLowerCase().includes(q))
      );
    }

    result.sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDirection === 'desc' ? -cmp : cmp;
    });

    return result;
  }, [events, columnFilters, filterQuery, sortColumn, sortDirection]);

  useEffect(() => { setCurrentPage(1); }, [processedEvents]);

  const activeFilterCount = Object.keys(columnFilters).length;
  const totalPages = Math.max(1, Math.ceil(processedEvents.length / pageSize));
  const pagedEvents = processedEvents.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  if (events.length === 0) {
    return (
      <div className="card text-center py-16">
        <DocumentTextIcon className="h-12 w-12 text-gray-700 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-400 mb-2">No Message Trace Events</h3>
        <p className="text-sm text-gray-600 max-w-md mx-auto">
          Email message trace events (sent/received messages) will appear here when found in the investigation data.
        </p>
      </div>
    );
  }

  return (
    <div>
      <FilterBar
        filterQuery={filterQuery}
        setFilterQuery={setFilterQuery}
        shownCount={processedEvents.length}
        totalCount={events.length}
        placeholder="Filter message trace events..."
        activeFilterCount={activeFilterCount}
        onClearFilters={() => setColumnFilters({})}
      />

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table
            ref={tableRef}
            className="text-sm"
            style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0) + 32}px` }}
          >
            <thead>
              <tr className="border-b border-gray-800">
                <th className="px-3 py-3" style={{ width: '32px' }} />
                {MSG_TRACE_COLUMNS.map((col, colIdx) => (
                  <th
                    key={col.id}
                    ref={(el) => { thRefs.current[colIdx] = el; }}
                    className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap relative select-none"
                    style={{ width: `${colWidths[colIdx]}px` }}
                  >
                    <div className="flex items-center gap-1 pr-2 overflow-hidden">
                      <span
                        onClick={col.sortable ? () => handleSort(col.id) : undefined}
                        className={`truncate ${col.sortable ? 'cursor-pointer hover:text-gray-300' : ''}`}
                        title={col.tooltip || ''}
                      >
                        {col.label}
                      </span>
                      {col.sortable && sortColumn === col.id && (
                        sortDirection === 'asc'
                          ? <ChevronUpIcon className="h-3 w-3 text-blue-400 flex-shrink-0" />
                          : <ChevronDownIcon className="h-3 w-3 text-blue-400 flex-shrink-0" />
                      )}
                    </div>
                    <div
                      className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                      onMouseDown={(e) => startResize(e, colIdx)}
                    >
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
              </tr>
              <tr className="border-b border-gray-700 bg-gray-900/50">
                <th className="px-3 py-1.5" />
                {MSG_TRACE_COLUMNS.map((col) => (
                  <th key={`filter-${col.id}`} className="px-3 py-1.5">
                    {col.filterable ? (
                      <ColumnFilterSelect
                        columnId={col.id}
                        options={columnOptions[col.id] || []}
                        value={columnFilters[col.id] || ''}
                        onChange={handleColumnFilterChange}
                      />
                    ) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {pagedEvents.map((event, index) => {
                const isExpanded = expandedRow === index;
                return (
                  <React.Fragment key={event.EventId || index}>
                    <tr
                      onClick={() => setExpandedRow(isExpanded ? null : index)}
                      className="hover:bg-gray-800/50 cursor-pointer transition-colors"
                    >
                      <td className="px-3 py-3">
                        <ChevronRightIcon
                          className={`h-3.5 w-3.5 text-gray-600 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        />
                      </td>
                      <td className="px-2 py-3 text-center overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          // Checked across every constituent: a flag saved before the
                          // collapse landed sits on whichever recipient row the analyst
                          // clicked, which need not be the primary. Reading only the
                          // primary would show an existing flag as absent.
                          const groupIds = event._collapsedIds || [event.EventId];
                          const flaggedId = groupIds.find(id => flaggedEventIds.has(id));
                          const isFlagged = Boolean(flaggedId);
                          // Toggle the id that is ACTUALLY flagged, falling back to the
                          // primary when none is. Always sending the primary would add a
                          // SECOND flag for one message instead of clearing the first.
                          const flagTargetId = flaggedId || event.EventId;
                          return (
                            <button
                              onClick={() => onTogglePhishingFlag && onTogglePhishingFlag(flagTargetId, event.SenderAddress, event.Subject)}
                              className={`px-1.5 py-0.5 rounded text-[10px] font-medium border cursor-pointer transition-colors ${
                                isFlagged
                                  ? 'bg-red-900/60 text-red-300 border-red-700/50 hover:bg-red-800/60'
                                  : 'bg-gray-800/60 text-gray-600 border-gray-700/50 hover:text-gray-400 hover:border-gray-600/50'
                              }`}
                              title={isFlagged ? 'Unflag as phishing' : 'Flag as phishing entry point'}
                            >
                              {isFlagged ? 'Phish' : '\u2014'}
                            </button>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 text-gray-300 whitespace-nowrap font-mono text-xs overflow-hidden">
                        {formatTimestamp(event.EventTimeUTC, selectedTz)}
                      </td>
                      <td className="px-4 py-3 overflow-hidden">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${
                          event.Direction === 'Sent'
                            ? 'bg-sky-900/60 text-sky-300 border-sky-700/50'
                            : 'bg-violet-900/60 text-violet-300 border-violet-700/50'
                        }`}>
                          {event.Direction || '\u2014'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-300 text-xs overflow-hidden">
                        <span className="line-clamp-1" title={event.SenderAddress}>{event.SenderAddress || '\u2014'}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-300 text-xs overflow-hidden">
                        {event._collapsed ? (
                          <span className="line-clamp-1" title={`${recipientSummary(event, 50)}${event._recipientsPartial ? ` (of ${event._declaredRecipientCount} on the message)` : ''}`}>
                            {event._recipients[0].address}
                            <span className="ml-1 text-[10px] text-sky-400/80">{`+${event._recipientCount - 1} more`}</span>
                          </span>
                        ) : (
                        <span className="line-clamp-1" title={event.RecipientAddress}>{event.RecipientAddress || '\u2014'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-300 text-xs overflow-hidden">
                        <span className={`line-clamp-1${event.Subject && !revealSubjects ? ' text-gray-600 select-none tracking-widest' : ''}`} title={revealSubjects ? event.Subject : undefined}>{event.Subject ? maskSubject(event.Subject, revealSubjects) : '\u2014'}</span>
                      </td>
                      <td className="px-4 py-3 overflow-hidden">
                        <span className={`text-xs font-medium ${
                          event.Status === 'Delivered' ? 'text-green-400' :
                          event.Status === 'Failed' ? 'text-red-400' :
                          event.Status === 'FilteredAsSpam' ? 'text-amber-400' :
                          'text-gray-400'
                        }`}>
                          {event.Status || '\u2014'}
                        </span>
                      </td>
                      <td className={`px-4 py-3 whitespace-nowrap font-mono text-xs overflow-hidden ${event.IPAddress ? resolvedIPClassName(classifyIP(event.IPAddress, safeIPSet, null, ipClassifications)) : 'text-gray-300'}`}>
                        {event.IPAddress || '\u2014'}<IpCommentNote ip={event.IPAddress} />
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="bg-gray-800/30">
                        <td colSpan={MSG_TRACE_COLUMNS.length + 1} className="px-6 py-4">
                          {event.Subject && (
                            <div className="flex justify-end mb-2">
                              <SubjectRevealButton revealSubjects={revealSubjects} onToggle={onToggleRevealSubjects} />
                            </div>
                          )}
                          {event._collapsed && (
                            <div className="mb-4 px-3 py-2 bg-gray-900/40 border border-gray-700/50 rounded-lg">
                              <div className="flex items-baseline justify-between gap-3 mb-1.5">
                                <span className="text-[11px] font-medium text-gray-300">
                                  {`Recipients (${event._recipientCount}${event._recipientsPartial ? ` of ${event._declaredRecipientCount} on the message` : ''})`}
                                </span>
                                {event._statusMixed && (
                                  <span className="text-[10px] text-amber-300">delivery differed by recipient</span>
                                )}
                              </div>
                              {event._recipientsPartial && (
                                <p className="text-[10px] text-gray-500 mb-1.5">
                                  Microsoft recorded {event._declaredRecipientCount} recipients on this message. The
                                  investigation returns only the delivery records matching this mailbox's addresses,
                                  so the rest are not listed here.
                                </p>
                              )}
                              <ul className="space-y-0.5">
                                {event._recipients.map(r => (
                                  <li key={r.eventId} className="flex items-baseline gap-2 text-xs">
                                    <span className="text-gray-300 break-all">{r.address || '(no address)'}</span>
                                    <span className={`text-[10px] ${getStatusClass(r.status)}`}>{r.status || 'unknown'}</span>
                                    {r.size != null && <span className="text-[10px] text-gray-600">{`${Math.round(r.size / 1024)} KB`}</span>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                            <DetailItem label="Timestamp (UTC)" value={event.EventTimeUTC ? format(parseISO(event.EventTimeUTC), 'MMM d, yyyy HH:mm:ss') : null} />
                            <DetailItem label="Direction" value={event.Direction} />
                            <DetailItem label="Sender" value={event.SenderAddress} />
                            {!event._collapsed && <DetailItem label="Recipient" value={event.RecipientAddress} />}
                            <DetailItem label="Subject" value={event.Subject ? maskSubject(event.Subject, revealSubjects) : event.Subject} />
                            <DetailItem label="Status" value={event._collapsed ? recipientStatusSummary(event) : event.Status} />
                            <DetailItem label="Message ID" value={event.MessageId} />
                            <DetailItem label="Size" value={event.Size ? `${Math.round(event.Size / 1024)} KB` : null} />
                            <DetailItem label="From IP" value={event.IPAddress} />
                            {(() => {
                              const networkId = getMsgTraceNetworkId(event);
                              if (!networkId) return null;
                              return (
                                <div className="group flex items-baseline gap-1.5 md:col-span-2 flex-wrap" title="Join key to URL Clicks — copy or jump to the URL Clicks tab filtered by this ID">
                                  <span className="text-[11px] text-gray-500 whitespace-nowrap">Network Message ID</span>
                                  <span className="text-xs break-all font-mono text-gray-300">{networkId}</span>
                                  <CopyButton text={networkId} />
                                  {onFindUrlClicks && (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); onFindUrlClicks(networkId); }}
                                      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border border-blue-700/50 bg-blue-900/40 text-blue-300 hover:bg-blue-900/70 transition-colors"
                                      title="Switch to the URL Clicks tab and filter by this Network Message ID"
                                    >
                                      <CursorArrowRaysIcon className="h-3 w-3" />
                                      Find URL clicks
                                    </button>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={processedEvents.length}
          onPageChange={(p) => { setCurrentPage(p); setExpandedRow(null); }}
          onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); setExpandedRow(null); }}
        />
      </div>
    </div>
  );
}

// ── Related Phishing Recipients View ────────────────────────────────────────

function RelatedPhishingView({ phishingFlags = [], relatedPhishingMessages = [], selectedTz = 'ET', ipClassifications = {}, revealSubjects = false, onToggleRevealSubjects }) {
  // Group related messages by flagged sender
  const grouped = useMemo(() => {
    const g = {};
    relatedPhishingMessages.forEach(msg => {
      const key = (msg.FlaggedSenderAddress || msg.flaggedSenderAddress || '').toLowerCase();
      if (!g[key]) {
        g[key] = {
          sender: msg.FlaggedSenderAddress || msg.flaggedSenderAddress || '',
          subject: msg.FlaggedSubject || msg.flaggedSubject || '',
          messages: [],
        };
      }
      g[key].messages.push(msg);
    });
    return g;
  }, [relatedPhishingMessages]);

  const senderKeys = Object.keys(grouped);

  // Summary cards
  const uniqueRecipients = useMemo(() => {
    const set = new Set();
    relatedPhishingMessages.forEach(msg => {
      const r = (msg.RecipientAddress || '').toLowerCase();
      if (r) set.add(r);
    });
    return set.size;
  }, [relatedPhishingMessages]);

  const deliveredCount = relatedPhishingMessages.filter(m => m.Status === 'Delivered').length;
  const filteredCount = relatedPhishingMessages.filter(m => m.Status === 'FilteredAsSpam' || m.Status === 'Quarantined').length;

  const clickedRecipients = useMemo(() => {
    const set = new Set();
    relatedPhishingMessages.forEach(msg => {
      if ((msg.UrlClickCount || 0) > 0) {
        set.add((msg.RecipientAddress || '').toLowerCase());
      }
    });
    return set.size;
  }, [relatedPhishingMessages]);

  if (phishingFlags.length === 0) {
    return (
      <div className="card p-6 text-center">
        <p className="text-gray-500 text-sm">No messages flagged as phishing. Flag a message in the Message Trace tab to find other recipients.</p>
      </div>
    );
  }

  if (relatedPhishingMessages.length === 0) {
    return (
      <div className="card p-6">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h4 className="text-sm font-semibold text-red-400">Related Phishing Recipients</h4>
          {phishingFlags.some(f => f.subject) && (
            <SubjectRevealButton revealSubjects={revealSubjects} onToggle={onToggleRevealSubjects} />
          )}
        </div>
        <div className="mb-4 p-3 bg-gray-800/50 rounded border border-gray-700">
          <p className="text-xs text-gray-400 mb-2">
            <span className="text-red-400 font-semibold">{phishingFlags.length}</span> message(s) flagged as phishing:
          </p>
          {phishingFlags.map((flag, i) => (
            <div key={flag.eventId || i} className="text-xs text-gray-300 ml-2 mb-1">
              <span className="text-gray-500">From:</span>{' '}
              <span className="text-red-300 font-mono">{flag.senderAddress}</span>
              {flag.subject && (
                <span className="text-gray-500 ml-2">Subject: <span className="text-gray-400 italic">"{maskSubject(flag.subject, revealSubjects)}"</span></span>
              )}
            </div>
          ))}
        </div>
        <p className="text-xs text-amber-400">
          Click "Retrieve Logs" to re-run the investigation and find other recipients who received the same email.
        </p>
      </div>
    );
  }

  return (
    <div>
      {onToggleRevealSubjects && (
        <div className="flex justify-end mb-2">
          <SubjectRevealButton revealSubjects={revealSubjects} onToggle={onToggleRevealSubjects} />
        </div>
      )}
      {/* Summary cards */}
      <div className="grid grid-cols-5 gap-3 mb-4">
        <div className="card p-3">
          <p className="text-[10px] text-gray-500 uppercase font-semibold">Flagged Senders</p>
          <p className="text-lg font-bold text-red-400">{senderKeys.length}</p>
        </div>
        <div className="card p-3">
          <p className="text-[10px] text-gray-500 uppercase font-semibold">Related Messages</p>
          <p className="text-lg font-bold text-gray-200">{relatedPhishingMessages.length}</p>
        </div>
        <div className="card p-3">
          <p className="text-[10px] text-gray-500 uppercase font-semibold">Unique Recipients</p>
          <p className="text-lg font-bold text-amber-400">{uniqueRecipients}</p>
        </div>
        <div className="card p-3">
          <p className="text-[10px] text-gray-500 uppercase font-semibold">Delivered / Filtered</p>
          <p className="text-lg font-bold">
            <span className="text-green-400">{deliveredCount}</span>
            <span className="text-gray-600 mx-1">/</span>
            <span className="text-amber-400">{filteredCount}</span>
          </p>
        </div>
        <div className="card p-3">
          <p className="text-[10px] text-gray-500 uppercase font-semibold">Clicked URL</p>
          <p className={`text-lg font-bold ${clickedRecipients > 0 ? 'text-red-400' : 'text-gray-600'}`}>{clickedRecipients}</p>
        </div>
      </div>

      {/* Per-sender sections */}
      {senderKeys.map(key => {
        const group = grouped[key];
        return (
          <div key={key} className="card p-4 border border-red-900/30 mb-4">
            <div className="flex items-center gap-2 mb-3 text-xs">
              <span className="text-gray-500">From:</span>
              <span className="text-red-300 font-mono">{group.sender}</span>
              {group.subject && (
                <>
                  <span className="text-gray-600">|</span>
                  <span className="text-gray-500">Subject:</span>
                  <span className="text-gray-300 italic truncate max-w-md">"{maskSubject(group.subject, revealSubjects)}"</span>
                </>
              )}
              <span className="text-gray-600">|</span>
              <span className="text-gray-500">{group.messages.length} recipient(s)</span>
            </div>
            <div className="overflow-x-auto">
              <table className="text-xs w-full" style={{ tableLayout: 'fixed' }}>
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left text-[10px] font-semibold text-gray-500 uppercase px-3 py-2" style={{ width: '160px' }}>Timestamp</th>
                    <th className="text-left text-[10px] font-semibold text-gray-500 uppercase px-3 py-2" style={{ width: '200px' }}>Recipient</th>
                    <th className="text-left text-[10px] font-semibold text-gray-500 uppercase px-3 py-2" style={{ width: '200px' }}>Subject</th>
                    <th className="text-left text-[10px] font-semibold text-gray-500 uppercase px-3 py-2" style={{ width: '90px' }}>Status</th>
                    <th className="text-center text-[10px] font-semibold text-gray-500 uppercase px-3 py-2" style={{ width: '70px' }}>Clicked</th>
                    <th className="text-left text-[10px] font-semibold text-gray-500 uppercase px-3 py-2" style={{ width: '120px' }}>From IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800/50">
                  {group.messages.map((msg, idx) => {
                    const ip = msg.IPAddress || '';
                    const ipClass = ipClassifications[ip] || 'default';
                    const ipColor = ipClass === 'safe' ? 'text-green-400' : ipClass === 'suspect' ? 'text-amber-400' : ipClass === 'malicious' ? 'text-red-400' : 'text-gray-300';
                    return (
                      <tr key={msg.EventId || idx} className="hover:bg-gray-800/30">
                        <td className="px-3 py-2 text-gray-300 font-mono whitespace-nowrap">
                          {formatTimestamp(msg.EventTimeUTC, selectedTz)}
                        </td>
                        <td className="px-3 py-2 text-gray-300 truncate" title={msg.RecipientAddress}>
                          {msg.RecipientAddress || '\u2014'}
                        </td>
                        <td className="px-3 py-2 text-gray-300 truncate" title={revealSubjects ? msg.Subject : undefined}>
                          {msg.Subject ? maskSubject(msg.Subject, revealSubjects) : '\u2014'}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`text-xs font-medium ${
                            msg.Status === 'Delivered' ? 'text-green-400' :
                            msg.Status === 'Failed' ? 'text-red-400' :
                            msg.Status === 'FilteredAsSpam' || msg.Status === 'Quarantined' ? 'text-amber-400' :
                            'text-gray-400'
                          }`}>
                            {msg.Status || '\u2014'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          {(msg.UrlClickCount || 0) > 0 ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-900/50 text-red-300 border border-red-700/50">
                              {msg.UrlClickCount} {msg.UrlClickCount === 1 ? 'click' : 'clicks'}
                            </span>
                          ) : (
                            <span className="text-gray-700">{'\u2014'}</span>
                          )}
                        </td>
                        <td className={`px-3 py-2 font-mono ${ipColor}`}>
                          {ip || '\u2014'}<IpCommentNote ip={ip} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── URL Clicks View ─────────────────────────────────────────────────────────

function getUrlClickColumns(tz) {
  return [
    // Status leads (v1.139.0) — every classification control in the portal sits
    // in the first column of its table. Was a labelled pill in a "Class" column
    // sixth from the left.
    { id: '_urlClass', label: 'Status', sortable: true, filterable: true },
    { id: 'EventTimeUTC', label: `Timestamp (${getTzLabel(tz)})`, sortable: true, filterable: false },
    { id: 'ActionType', label: 'Action', sortable: true, filterable: true },
    { id: 'Url', label: 'URL', sortable: true, filterable: false },
    { id: 'IPAddress', label: 'Client IP', sortable: true, filterable: true },
    { id: 'ThreatType', label: 'Threat', sortable: true, filterable: true },
    { id: 'NetworkMessageId', label: 'Message ID', sortable: true, filterable: false },
  ];
}

// ── GenericEventsView ─── Data-driven table for new log types ───────────────

function GenericEventsView({ events, columns, safeIPSet, ipClassifications = {}, filterQuery, setFilterQuery, eventType, enrichEvents }) {
  const [sortCol, setSortCol] = useState('EventTimeUTC');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(0);
  const [rawLogEvent, setRawLogEvent] = useState(null);
  const pageSize = 50;

  // Column resize
  const defaultWidths = useMemo(() => columns.map(col => col.width || 150), [columns]);
  const [colWidths, setColWidths] = useState(defaultWidths);
  const thRefs = useRef([]);
  const tableRef = useRef(null);

  // Reset widths when columns change (different tab)
  useEffect(() => { setColWidths(columns.map(col => col.width || 150)); }, [columns]);

  const startResize = useCallback((e, colIdx) => {
    e.preventDefault();
    const th = thRefs.current[colIdx];
    const table = tableRef.current;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    const startTableWidth = table ? table.offsetWidth : 0;
    let lastWidth = startWidth;

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (moveEvt) => {
      lastWidth = Math.max(50, startWidth + moveEvt.clientX - startX);
      th.style.width = `${lastWidth}px`;
      if (table) table.style.width = `${startTableWidth + (lastWidth - startWidth)}px`;
    };

    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setColWidths(prev => { const n = [...prev]; n[colIdx] = lastWidth; return n; });
      if (table) table.style.width = '';
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const filtered = useMemo(() => {
    let rows = enrichEvents ? enrichEvents(events) : events;
    if (filterQuery) {
      const q = filterQuery.toLowerCase();
      rows = rows.filter(evt =>
        columns.some(col => {
          const val = evt[col.key];
          return val != null && String(val).toLowerCase().includes(q);
        }) || (evt.InferredIP && evt.InferredIP.toLowerCase().includes(q))
      );
    }
    rows = [...rows].sort((a, b) => {
      const aVal = a[sortCol] ?? '';
      const bVal = b[sortCol] ?? '';
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [events, filterQuery, sortCol, sortDir, columns, enrichEvents]);

  const pageCount = Math.ceil(filtered.length / pageSize);
  const pageEvents = filtered.slice(page * pageSize, (page + 1) * pageSize);

  if (events.length === 0) {
    return (
      <div className="card text-center py-10">
        <p className="text-gray-500">No {eventType} events found.</p>
      </div>
    );
  }

  const totalTableWidth = colWidths.reduce((a, b) => a + b, 0) + 28;

  return (
    <div>
      {/* Search */}
      <div className="mb-3 flex items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <MagnifyingGlassIcon className="h-4 w-4 text-gray-500 absolute left-2.5 top-2" />
          <input
            type="text"
            value={filterQuery}
            onChange={e => { setFilterQuery(e.target.value); setPage(0); }}
            placeholder={`Filter ${eventType} events...`}
            className="input-field pl-8 py-1.5 text-xs w-full"
          />
        </div>
        <span className="text-xs text-gray-500">{filtered.length} events</span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table
          ref={tableRef}
          className="text-xs"
          style={{ tableLayout: 'fixed', width: `${totalTableWidth}px` }}
        >
          <thead>
            <tr className="border-b border-gray-800">
              <th className="py-2 px-1" style={{ width: '28px' }} />
              {columns.map((col, colIdx) => (
                <th
                  key={col.key}
                  ref={(el) => { thRefs.current[colIdx] = el; }}
                  className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-2 py-2 whitespace-nowrap relative select-none"
                  style={{ width: `${colWidths[colIdx]}px` }}
                >
                  <div className="flex items-center gap-1 pr-2 overflow-hidden">
                    <span
                      onClick={() => {
                        if (sortCol === col.key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                        else { setSortCol(col.key); setSortDir('asc'); }
                      }}
                      className="truncate cursor-pointer hover:text-gray-300"
                    >
                      {col.label}
                    </span>
                    {sortCol === col.key && (
                      sortDir === 'asc'
                        ? <ChevronUpIcon className="h-3 w-3 text-blue-400 flex-shrink-0" />
                        : <ChevronDownIcon className="h-3 w-3 text-blue-400 flex-shrink-0" />
                    )}
                  </div>
                  {/* Resize handle */}
                  <div
                    className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                    onMouseDown={(e) => startResize(e, colIdx)}
                  >
                    <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageEvents.map((evt, idx) => (
              <tr key={evt.EventId || idx} className="border-b border-gray-800/50 hover:bg-gray-800/40">
                <td className="py-1.5 px-1" style={{ width: '28px' }}>
                  {evt.RawLog && (
                    <button
                      onClick={() => setRawLogEvent(evt)}
                      className="p-0.5 text-gray-600 hover:text-blue-400 transition-colors"
                      title="View raw log"
                    >
                      <CodeBracketIcon className="h-3.5 w-3.5" />
                    </button>
                  )}
                </td>
                {columns.map(col => {
                  const raw = evt[col.key];
                  let display;
                  if (col.format) {
                    display = col.format(raw);
                  } else if (col.badge && raw) {
                    const color = col.badgeColors?.[String(raw).toLowerCase()] || 'bg-gray-600';
                    display = (
                      <span className={`${color} text-white text-[10px] px-1.5 py-0.5 rounded-full font-medium`}>
                        {String(raw)}
                      </span>
                    );
                  } else {
                    display = raw != null ? String(raw) : '\u2014';
                  }
                  const isIPCol = col.key === 'IPAddress';
                  let ipColor = 'text-gray-300';
                  let ipDisplay = display;
                  if (isIPCol && raw) {
                    const ipStatus = classifyIP(raw, safeIPSet, null, ipClassifications, evt);
                    ipColor = resolvedIPClassName(ipStatus);
                  } else if (isIPCol && !raw && evt.InferredIP) {
                    const inferredStatus = classifyIP(evt.InferredIP, safeIPSet, null, ipClassifications);
                    ipColor = resolvedIPClassName(inferredStatus);
                    const conf = evt.InferredIPConfidence || 'low';
                    const confSymbol = conf === 'high' ? '\u2713' : conf === 'medium' ? '~' : '?';
                    const confColor = conf === 'high' ? 'text-green-400' : conf === 'medium' ? 'text-amber-400' : 'text-gray-500';
                    const delta = evt.InferredIPTimeDelta != null ? `${evt.InferredIPTimeDelta}s` : '';
                    const tooltip = `Inferred IP (${conf} confidence${delta ? `, ${delta} delta` : ''}) \u2014 no actual ClientIP on this event.`;
                    ipDisplay = (
                      <span className="italic cursor-help" title={tooltip}>
                        {evt.InferredIP} <span className={`text-[9px] font-medium ${confColor}`}>{confSymbol}</span>
                      </span>
                    );
                  }
                  const ipText = isIPCol ? (raw || evt.InferredIP || '') : '';
                  return (
                    <td key={col.key} className={`py-1.5 px-2 overflow-hidden text-ellipsis whitespace-nowrap ${isIPCol ? `${ipColor} group` : 'text-gray-300'}`}>
                      {isIPCol ? <>{ipDisplay}<CopyButton text={ipText} /><IpCommentNote ip={ipText} /></> : display}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pageCount > 1 && (
        <Pagination
          currentPage={page}
          totalPages={pageCount}
          onPageChange={setPage}
        />
      )}
      {rawLogEvent && <RawLogModal event={rawLogEvent} onClose={() => setRawLogEvent(null)} />}
    </div>
  );
}

function UrlClicksView({
  events,
  ipLookup,
  safeIPSet,
  ipClassifications = {},
  filterQuery, setFilterQuery,
  sortColumn, setSortColumn,
  sortDirection, setSortDirection,
  expandedRow, setExpandedRow,
  selectedTz = 'ET',
  urlClassifications = {},
  onCycleUrlStatus,
  onFindMessageTrace,
}) {
  const URL_CLICK_COLUMNS = useMemo(() => getUrlClickColumns(selectedTz), [selectedTz]);
  const handleSort = (colId) => {
    if (sortColumn === colId) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(colId);
      setSortDirection('asc');
    }
  };

  const [columnFilters, setColumnFilters] = useState({});

  const handleColumnFilterChange = useCallback((columnId, value) => {
    setColumnFilters(prev => {
      const next = { ...prev };
      if (value) { next[columnId] = value; } else { delete next[columnId]; }
      return next;
    });
    setExpandedRow(null);
  }, [setExpandedRow]);

  const columnOptions = useMemo(() => {
    const opts = {};
    const actions = new Set();
    const threats = new Set();
    const ips = new Set();
    const classes = new Set();
    events.forEach(evt => {
      if (evt.ActionType) actions.add(evt.ActionType);
      if (evt.ThreatType) threats.add(evt.ThreatType);
      if (evt.IPAddress) ips.add(evt.IPAddress);
      const cls = urlClassifications[evt.EventId] || 'default';
      classes.add(cls);
    });
    opts['ActionType'] = [...actions].sort();
    opts['ThreatType'] = [...threats].sort();
    opts['IPAddress'] = [...ips].sort();
    opts['_urlClass'] = [...classes].sort();
    return opts;
  }, [events, urlClassifications]);

  const [colWidths, setColWidths] = useState([160, 110, 280, 120, 90, 100, 160]);
  const thRefs = useRef([]);
  const tableRef = useRef(null);

  const startResize = useCallback((e, colIdx) => {
    e.preventDefault();
    const th = thRefs.current[colIdx];
    const table = tableRef.current;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    const startTableWidth = table ? table.offsetWidth : 0;
    let lastWidth = startWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (moveEvt) => {
      lastWidth = Math.max(50, startWidth + moveEvt.clientX - startX);
      th.style.width = `${lastWidth}px`;
      if (table) table.style.width = `${startTableWidth + (lastWidth - startWidth)}px`;
    };
    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setColWidths(prev => { const n = [...prev]; n[colIdx] = lastWidth; return n; });
      if (table) table.style.width = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const processedEvents = useMemo(() => {
    let result = [...events];

    if (columnFilters['ActionType']) {
      result = result.filter(evt => evt.ActionType === columnFilters['ActionType']);
    }
    if (columnFilters['ThreatType']) {
      result = result.filter(evt => evt.ThreatType === columnFilters['ThreatType']);
    }
    if (columnFilters['IPAddress']) {
      result = result.filter(evt => evt.IPAddress === columnFilters['IPAddress']);
    }
    if (columnFilters['_urlClass']) {
      result = result.filter(evt => (urlClassifications[evt.EventId] || 'default') === columnFilters['_urlClass']);
    }

    if (filterQuery.trim()) {
      const q = filterQuery.toLowerCase();
      result = result.filter(evt =>
        (evt.EventTimeUTC && evt.EventTimeUTC.toLowerCase().includes(q)) ||
        (evt.Url && evt.Url.toLowerCase().includes(q)) ||
        (evt.ActionType && evt.ActionType.toLowerCase().includes(q)) ||
        (evt.ThreatType && evt.ThreatType.toLowerCase().includes(q)) ||
        (evt.IPAddress && evt.IPAddress.toLowerCase().includes(q)) ||
        (evt.NetworkMessageId && evt.NetworkMessageId.toLowerCase().includes(q))
      );
    }

    result.sort((a, b) => {
      let aVal, bVal;
      if (sortColumn === '_urlClass') {
        aVal = urlClassifications[a.EventId] || 'default';
        bVal = urlClassifications[b.EventId] || 'default';
      } else {
        aVal = a[sortColumn];
        bVal = b[sortColumn];
      }
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDirection === 'desc' ? -cmp : cmp;
    });

    return result;
  }, [events, columnFilters, filterQuery, sortColumn, sortDirection, urlClassifications]);

  useEffect(() => { setCurrentPage(1); }, [processedEvents]);

  const activeFilterCount = Object.keys(columnFilters).length;
  const totalPages = Math.max(1, Math.ceil(processedEvents.length / pageSize));
  const pagedEvents = processedEvents.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  if (events.length === 0) {
    return (
      <div className="card text-center py-16">
        <LinkIcon className="h-12 w-12 text-gray-700 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-400 mb-2">No URL Click Events</h3>
        <p className="text-sm text-gray-600 max-w-md mx-auto">
          URL click events from Microsoft Defender Safe Links will appear here when found in the investigation data.
        </p>
      </div>
    );
  }

  return (
    <div>
      <FilterBar
        filterQuery={filterQuery}
        setFilterQuery={setFilterQuery}
        shownCount={processedEvents.length}
        totalCount={events.length}
        placeholder="Filter URL click events..."
        activeFilterCount={activeFilterCount}
        onClearFilters={() => setColumnFilters({})}
      />

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table
            ref={tableRef}
            className="text-sm"
            style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0) + 32}px` }}
          >
            <thead>
              <tr className="border-b border-gray-800">
                <th className="px-3 py-3" style={{ width: '32px' }} />
                {URL_CLICK_COLUMNS.map((col, colIdx) => (
                  <th
                    key={col.id}
                    ref={(el) => { thRefs.current[colIdx] = el; }}
                    className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap relative select-none"
                    style={{ width: `${colWidths[colIdx]}px` }}
                  >
                    <div className="flex items-center gap-1 pr-2 overflow-hidden">
                      <span
                        onClick={col.sortable ? () => handleSort(col.id) : undefined}
                        className={`truncate ${col.sortable ? 'cursor-pointer hover:text-gray-300' : ''}`}
                      >
                        {col.label}
                      </span>
                      {col.sortable && sortColumn === col.id && (
                        sortDirection === 'asc'
                          ? <ChevronUpIcon className="h-3 w-3 text-blue-400 flex-shrink-0" />
                          : <ChevronDownIcon className="h-3 w-3 text-blue-400 flex-shrink-0" />
                      )}
                    </div>
                    <div
                      className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                      onMouseDown={(e) => startResize(e, colIdx)}
                    >
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
              </tr>
              <tr className="border-b border-gray-700 bg-gray-900/50">
                <th className="px-3 py-1.5" />
                {URL_CLICK_COLUMNS.map((col) => (
                  <th key={`filter-${col.id}`} className="px-3 py-1.5">
                    {col.filterable ? (
                      <ColumnFilterSelect
                        columnId={col.id}
                        options={columnOptions[col.id] || []}
                        value={columnFilters[col.id] || ''}
                        onChange={handleColumnFilterChange}
                      />
                    ) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {pagedEvents.map((event, index) => {
                const isExpanded = expandedRow === index;
                const truncUrl = event.Url && event.Url.length > 60
                  ? event.Url.substring(0, 57) + '...'
                  : event.Url;
                return (
                  <React.Fragment key={event.EventId || index}>
                    <tr
                      onClick={() => setExpandedRow(isExpanded ? null : index)}
                      className="hover:bg-gray-800/50 cursor-pointer transition-colors"
                    >
                      <td className="px-3 py-3">
                        <ChevronRightIcon
                          className={`h-3.5 w-3.5 text-gray-600 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        />
                      </td>
                      {/* Status — leading column, portal-standard circle (v1.139.0) */}
                      <td className="px-4 py-3 overflow-hidden">
                        {(() => {
                          const cls = urlClassifications[event.EventId] || 'default';
                          const label = getUrlStatusLabel(cls);
                          return (
                            <StatusCircle
                              tone={urlStatusTone(cls)}
                              onClick={onCycleUrlStatus ? () => onCycleUrlStatus(event.EventId) : undefined}
                              title={cls === 'default'
                                ? 'Default — click to classify (confirmed → suspected → benign)'
                                : `${label} — click to change`}
                            />
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 text-gray-300 whitespace-nowrap font-mono text-xs overflow-hidden">
                        {formatTimestamp(event.EventTimeUTC, selectedTz)}
                      </td>
                      <td className="px-4 py-3 overflow-hidden">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${
                          event.ActionType === 'ClickBlocked'
                            ? 'bg-red-900/60 text-red-300 border-red-700/50'
                            : 'bg-green-900/60 text-green-300 border-green-700/50'
                        }`}>
                          {event.ActionType || '\u2014'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-300 text-xs overflow-hidden">
                        <span className="line-clamp-1 font-mono" title={event.Url}>{truncUrl || '\u2014'}</span>
                      </td>
                      <td className={`px-4 py-3 whitespace-nowrap font-mono text-xs overflow-hidden ${event.IPAddress ? resolvedIPClassName(classifyIP(event.IPAddress, safeIPSet, null, ipClassifications)) : 'text-gray-300'}`}>
                        {event.IPAddress || '\u2014'}<IpCommentNote ip={event.IPAddress} />
                      </td>
                      <td className="px-4 py-3 overflow-hidden">
                        {event.ThreatType ? (
                          <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${
                            event.ThreatType === 'Phish' ? 'bg-orange-900/60 text-orange-300 border-orange-700/50' :
                            event.ThreatType === 'Malware' ? 'bg-red-900/60 text-red-300 border-red-700/50' :
                            'bg-amber-900/60 text-amber-300 border-amber-700/50'
                          }`}>
                            {event.ThreatType}
                          </span>
                        ) : (
                          <span className="text-gray-700 text-xs">{'\u2014'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-300 text-xs overflow-hidden">
                        <span className="line-clamp-1 font-mono" title={event.NetworkMessageId}>{event.NetworkMessageId || '\u2014'}</span>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="bg-gray-800/30">
                        <td colSpan={URL_CLICK_COLUMNS.length + 1} className="px-6 py-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                            <DetailItem label="Timestamp (UTC)" value={event.EventTimeUTC ? format(parseISO(event.EventTimeUTC), 'MMM d, yyyy HH:mm:ss') : null} />
                            <DetailItem label="User" value={event.AccountUpn} />
                            <DetailItem label="Action" value={event.ActionType} />
                            <DetailItem label="Client IP" value={event.IPAddress} />
                            <DetailItem label="Threat Type" value={event.ThreatType || 'None'} />
                            <DetailItem label="Classification" value={getUrlStatusLabel(urlClassifications[event.EventId] || 'default') || 'Unclassified'} />
                            {event.NetworkMessageId ? (
                              <div className="group flex items-baseline gap-1.5 md:col-span-2 flex-wrap" title="Join key to Message Trace — copy or jump to the Message Trace tab filtered by this ID">
                                <span className="text-[11px] text-gray-500 whitespace-nowrap">Network Message ID</span>
                                <span className="text-xs break-all font-mono text-gray-300">{event.NetworkMessageId}</span>
                                <CopyButton text={event.NetworkMessageId} />
                                {onFindMessageTrace && (
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); onFindMessageTrace(event.NetworkMessageId); }}
                                    className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border border-blue-700/50 bg-blue-900/40 text-blue-300 hover:bg-blue-900/70 transition-colors"
                                    title="Switch to the Message Trace tab and filter by this Network Message ID"
                                  >
                                    <CursorArrowRaysIcon className="h-3 w-3" />
                                    Find message
                                  </button>
                                )}
                              </div>
                            ) : (
                              <DetailItem label="Network Message ID" value={event.NetworkMessageId} />
                            )}
                          </div>
                          <div className="mb-3">
                            <p className="text-xs font-medium text-gray-500 mb-1">Full URL</p>
                            <p className="text-xs text-gray-300 font-mono break-all bg-gray-900 border border-gray-700 rounded-lg p-2">
                              {event.Url || '\u2014'}
                            </p>
                          </div>
                          {(() => {
                            const chain = Array.isArray(event.UrlChain) ? event.UrlChain : (event.UrlChain ? [event.UrlChain] : []);
                            return chain.length > 1 ? (
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-1">URL Redirect Chain ({chain.length} hops)</p>
                              <div className="bg-gray-900 border border-gray-700 rounded-lg p-2 space-y-1">
                                {chain.map((url, i) => (
                                  <div key={i} className="flex items-start gap-2 text-xs">
                                    <span className="text-gray-600 flex-shrink-0">{i + 1}.</span>
                                    <span className="text-gray-300 font-mono break-all">{url}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            ) : null;
                          })()}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={processedEvents.length}
          onPageChange={(p) => { setCurrentPage(p); setExpandedRow(null); }}
          onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); setExpandedRow(null); }}
        />
      </div>
    </div>
  );
}

// ── Placeholder Tab ─────────────────────────────────────────────────────────

function PlaceholderTab({ icon: Icon, title, message }) {
  return (
    <div className="card text-center py-16">
      <Icon className="h-12 w-12 text-gray-700 mx-auto mb-4" />
      <h3 className="text-lg font-medium text-gray-400 mb-2">{title}</h3>
      <p className="text-sm text-gray-600 max-w-md mx-auto">{message}</p>
    </div>
  );
}

// ── Shared Sub-components ───────────────────────────────────────────────────

function FilterBar({ filterQuery, setFilterQuery, shownCount, totalCount, placeholder, activeFilterCount, onClearFilters, note }) {
  return (
    <div className="flex items-center justify-between gap-4 mb-4">
      <div className="flex items-center gap-3 flex-1">
        <div className="relative max-w-md flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input
            type="text"
            placeholder={placeholder}
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            className="input-field pl-9"
          />
        </div>
        {note && <span className="text-xs text-gray-600 italic">{note}</span>}
      </div>
      <div className="flex items-center gap-3">
        {activeFilterCount > 0 && onClearFilters && (
          <button onClick={onClearFilters} className="text-xs text-blue-400 hover:text-blue-300 transition-colors">
            Clear column filters
          </button>
        )}
        <span className="text-xs text-gray-500 flex-shrink-0">
          {shownCount} of {totalCount} event{totalCount !== 1 ? 's' : ''}
        </span>
      </div>
    </div>
  );
}

function VPNProxyBadges({ flags }) {
  if (!flags.isVPN && !flags.isProxy && !flags.isTOR && !flags.isCompromised && !flags.isAnonymous) {
    return <span className="text-gray-700 text-xs">-</span>;
  }
  return (
    <div className="flex gap-1 flex-wrap">
      {flags.isVPN && (
        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-900/60 text-amber-300 border border-amber-700/50">
          VPN
        </span>
      )}
      {flags.isProxy && (
        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-900/60 text-orange-300 border border-orange-700/50">
          Proxy
        </span>
      )}
      {flags.isTOR && (
        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-900/60 text-red-300 border border-red-700/50">
          TOR
        </span>
      )}
      {flags.isCompromised && (
        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-900/60 text-red-300 border border-red-700/50">
          Botnet
        </span>
      )}
      {flags.isAnonymous && (
        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-900/60 text-amber-300 border border-amber-700/50">
          Anon
        </span>
      )}
    </div>
  );
}

// ── Anomaly Badges (baseline deviation indicators) ──────────────────────

function AnomalyBadges({ event }) {
  if (!event) return null;
  // Only sign-in events have IsAbnormal* flags
  const isSignIn = event._eventType === 'SignIn' || event._eventType === 'signin';
  if (!isSignIn) return null;

  const flags = [];
  if (event.IsAbnormalIP) flags.push({ label: 'IP', title: 'IP not in user\'s 7-day baseline' });
  if (event.IsAbnormalOS) flags.push({ label: 'OS', title: 'OS not in user\'s 7-day baseline' });
  if (event.IsAbnormalCountry) flags.push({ label: 'Ctry', title: 'Country not in user\'s 7-day baseline' });
  if (event.IsAbnormalState) flags.push({ label: 'State', title: 'State not in user\'s 7-day baseline' });
  if (event.IsAbnormalCity) flags.push({ label: 'City', title: 'City not in user\'s 7-day baseline' });
  if (event.IsAbnormalTime) flags.push({ label: 'Time', title: 'Sign-in outside user\'s normal hours' });

  if (flags.length === 0) return null;

  return (
    <div className="flex gap-0.5 flex-wrap">
      {flags.map(f => (
        <span
          key={f.label}
          title={f.title}
          className="px-1 py-0 rounded text-[9px] font-medium bg-amber-900/30 text-amber-400/80 border border-amber-700/30"
        >
          {f.label}
        </span>
      ))}
    </div>
  );
}

// ── Raw Log Modal ─────────────────────────────────────────────────────────

function RawLogModal({ event, onClose }) {
  const [copied, setCopied] = useState(false);
  const rawData = event.RawLog || event;
  const jsonStr = useMemo(() => JSON.stringify(rawData, null, 2), [rawData]);
  const badge = getTypeBadge(event._eventType);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleCopy = () => {
    navigator.clipboard.writeText(jsonStr).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70" />
      <div
        className="relative bg-gray-900 border border-gray-700 rounded-lg shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <div className="flex items-center gap-3 min-w-0">
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${badge.classes}`}>
              {badge.label}
            </span>
            <span className="text-sm text-gray-300 truncate">
              {event.EventTimeUTC ? format(parseISO(event.EventTimeUTC), 'MMM d, yyyy HH:mm:ss') : 'Unknown time'}
            </span>
            <span className="text-xs text-gray-500 truncate">{event.Operation || ''}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 px-2.5 py-1 text-xs rounded border border-gray-600 text-gray-300 hover:bg-gray-800 transition-colors"
            >
              <ClipboardDocumentIcon className="h-3.5 w-3.5" />
              {copied ? 'Copied!' : 'Copy'}
            </button>
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-200 transition-colors">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
        {/* Body */}
        <div className="flex-1 overflow-auto p-4">
          <pre className="text-xs text-gray-300 font-mono whitespace-pre-wrap break-all">
            {jsonStr}
          </pre>
        </div>
      </div>
    </div>
  );
}

/**
 * ParametersSection — renders operation parameters with optional diff view.
 * When PreviousParameters is available, shows a toggle between "Current State" and "Changes".
 */
function ParametersSection({ parameters, previousParameters, operation }) {
  const params = parseParameters(parameters);
  const prevParams = parseParameters(previousParameters);
  const hasDiff = !!(prevParams && params);
  const isSetOp = operation && operation.startsWith('Set-');
  // For Set-* ops without a baseline, show "first config" mode (all entries are new)
  const isFirstConfig = isSetOp && !prevParams;
  const [viewMode, setViewMode] = useState(hasDiff ? 'diff' : 'current');
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [expandedLists, setExpandedLists] = useState({});

  const diff = useMemo(() => computeParameterDiff(prevParams, params), [prevParams, params]);

  if (!params || params.length === 0) return null;

  const changedDiffs = diff ? diff.filter(d => d.type !== 'unchanged') : [];
  const unchangedDiffs = diff ? diff.filter(d => d.type === 'unchanged') : [];

  const toggleList = (name) => setExpandedLists(prev => ({ ...prev, [name]: !prev[name] }));

  return (
    <div className="mb-3">
      <div className="flex items-center gap-3 mb-2">
        <p className="text-xs font-medium text-gray-500">Rule / Operation Parameters</p>
        {isFirstConfig && (
          <span className="text-[10px] text-amber-400/70 italic">No prior baseline found — cannot determine what changed</span>
        )}
        {hasDiff && (
          <div className="flex gap-1">
            <button onClick={() => setViewMode('diff')}
              className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${viewMode === 'diff' ? 'bg-amber-800 text-amber-200' : 'bg-gray-800 text-gray-400 hover:text-gray-300'}`}>
              Changes ({changedDiffs.length})
            </button>
            <button onClick={() => setViewMode('current')}
              className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${viewMode === 'current' ? 'bg-blue-800 text-blue-200' : 'bg-gray-800 text-gray-400 hover:text-gray-300'}`}>
              Current State
            </button>
          </div>
        )}
      </div>

      {viewMode === 'current' && (
        /* Current state table — original rendering */
        <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 text-xs overflow-x-auto max-h-48 overflow-y-auto">
          <table className="w-full">
            <tbody>
              {params.map((p, i) => (
                <tr key={i} className="border-b border-gray-800 last:border-b-0">
                  <td className="py-1 pr-4 text-gray-400 font-medium whitespace-nowrap align-top">{p.Name || p.name}</td>
                  <td className="py-1 text-gray-300 break-all">{p.Value ?? p.value ?? '(empty)'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewMode === 'diff' && (
        /* Diff view */
        <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 text-xs overflow-x-auto max-h-96 overflow-y-auto space-y-2">
          {changedDiffs.length === 0 && (
            <p className="text-gray-500 italic">No changes detected (parameters identical to previous)</p>
          )}
          {changedDiffs.map((d, i) => (
            <div key={i} className="border-b border-gray-800 last:border-b-0 pb-2">
              {d.type === 'list' && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-gray-400 font-medium">{d.name}</span>
                    {d.added.length > 0 && <span className="text-green-400 text-[10px] font-medium">+{d.added.length} added</span>}
                    {d.removed.length > 0 && <span className="text-red-400 text-[10px] font-medium">-{d.removed.length} removed</span>}
                    {d.unchangedCount > 0 && <span className="text-gray-600 text-[10px]">{d.unchangedCount} unchanged</span>}
                    <button onClick={() => toggleList(d.name)} className="text-[10px] text-blue-400 hover:text-blue-300 ml-auto">
                      {expandedLists[d.name] ? 'collapse' : 'expand'}
                    </button>
                  </div>
                  {(expandedLists[d.name] || (d.added.length + d.removed.length <= 20)) && (
                    <div className="ml-2 space-y-0.5">
                      {d.added.map((addr, j) => (
                        <div key={`a-${j}`} className="text-green-400 font-mono text-[10px]">+ {addr}</div>
                      ))}
                      {d.removed.map((addr, j) => (
                        <div key={`r-${j}`} className="text-red-400 font-mono text-[10px]">- {addr}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {d.type === 'scalar' && (
                <div className="flex items-start gap-2">
                  <span className="text-gray-400 font-medium whitespace-nowrap">{d.name}:</span>
                  <span className="text-red-400 line-through">{d.previous}</span>
                  <span className="text-gray-600">→</span>
                  <span className="text-green-400">{d.current}</span>
                </div>
              )}
              {d.type === 'added_param' && (
                <div className="flex items-start gap-2">
                  <span className="bg-green-900/50 text-green-300 text-[10px] px-1 rounded font-medium">NEW</span>
                  <span className="text-gray-400 font-medium">{d.name}:</span>
                  <span className="text-green-400 break-all">{d.current}</span>
                </div>
              )}
              {d.type === 'removed_param' && (
                <div className="flex items-start gap-2">
                  <span className="bg-red-900/50 text-red-300 text-[10px] px-1 rounded font-medium">REMOVED</span>
                  <span className="text-gray-400 font-medium">{d.name}:</span>
                  <span className="text-red-400 break-all line-through">{d.previous}</span>
                </div>
              )}
            </div>
          ))}
          {unchangedDiffs.length > 0 && (
            <div className="pt-1">
              <button onClick={() => setShowUnchanged(!showUnchanged)} className="text-[10px] text-gray-500 hover:text-gray-400">
                {showUnchanged ? 'Hide' : 'Show'} {unchangedDiffs.length} unchanged parameter{unchangedDiffs.length !== 1 ? 's' : ''}
              </button>
              {showUnchanged && (
                <div className="mt-1 space-y-0.5">
                  {unchangedDiffs.map((d, i) => (
                    <div key={i} className="flex gap-2 text-gray-600">
                      <span className="font-medium whitespace-nowrap">{d.name}:</span>
                      <span className="break-all truncate">{String(d.current).length > 80 ? String(d.current).substring(0, 80) + '...' : d.current}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Explicit classification selector for the expanded-row IP. Renders one button per
// state (Default / Safe / Suspect / Malicious) so a click SETS that classification
// directly — no cycling, so you can never land on an unintended value. The active
// state is filled with its status color + a ✓; the others are muted outlines. Uses
// the same persistence as the IP Addresses tab, so the change is saved and re-colors
// the IP everywhere. Shows the explicit manual status only — auto classifications
// (tenant/service safe, MS-Infra) are reflected in the IP text color.
const IP_CLASS_OPTIONS = [
  { key: 'default', label: 'Default' },
  { key: 'safe', label: 'Safe' },
  { key: 'suspect', label: 'Suspect' },
  { key: 'malicious', label: 'Malicious' },
];
function IpClassSelector({ ip, ipClassifications = {}, onSetIPStatus }) {
  if (!ip || !onSetIPStatus) return null;
  const current = ipClassifications[ip] || 'default';
  return (
    <span className="inline-flex items-center gap-1 align-middle flex-wrap">
      <span className="text-[10px] text-gray-500 mr-0.5">Set:</span>
      {IP_CLASS_OPTIONS.map((opt) => {
        const active = current === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            aria-pressed={active}
            onClick={(e) => { e.stopPropagation(); onSetIPStatus(ip, opt.key); }}
            className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border transition-colors ${
              active
                ? getIPStatusBgClass(opt.key)
                : 'bg-transparent border-gray-700 text-gray-500 hover:text-gray-300 hover:border-gray-500'
            }`}
            title={active ? `Classification: ${opt.label} (current)` : `Set classification to ${opt.label}`}
          >
            {active ? `✓ ${opt.label}` : opt.label}
          </button>
        );
      })}
    </span>
  );
}

// ── Teams meeting / call row (v1.189.4) ─────────────────────────────────────
// One row per meeting or call (utils/teamsMeetings.js). The expanded view separates the case
// user's OWN participation -- with the evidence an analyst needs to judge it -- from the other
// participants, whose records arrive in this log only because the case user was in it.
const mtgs = (n) => `${n} other meeting${n === 1 ? '' : 's'} or call${n === 1 ? '' : 's'}`;
const MEETING_STATE_TEXT = {
  before_window: { cls: 'text-green-300', text: (n) => `Before the attacker's first recorded activity${n ? ` · same device, app and address as ${mtgs(n)}` : ''}` },
  known: { cls: 'text-green-300', text: (n) => `Same device, Teams app and address as ${mtgs(n)} before the attacker's first recorded activity` },
  partial: { cls: 'text-amber-300', text: (n) => `Same device and Teams app as ${mtgs(n)} before the attacker's first recorded activity, from a different address` },
  new: { cls: 'text-red-300', text: () => "This device and Teams app were not seen in any meeting or call before the attacker's first recorded activity" },
  no_window: { cls: 'text-gray-400', text: () => 'No attacker window on this case, so there is no earlier participation to compare against' },
};

function ExpandedMeetingDetail({ event, selectedTz, ipClassifications = {}, safeIPSet = new Set(), meetingContext = null, dismissedEventIds = new Set(), onDismissEvent }) {
  const m = event._meeting;
  const ids = event._collapsedIds || [event.EventId];
  const isDismissed = ids.every((id) => dismissedEventIds.has(id));
  const ipStatus = (ip) => ipClassifications[ip] || (safeIPSet.has(ip) ? 'safe' : 'default');
  const span = (a, b) => `${a ? formatTimestamp(a, selectedTz) : '?'} – ${b ? formatTimestamp(b, selectedTz) : '?'}`;
  const others = m.attendees.filter((a) => !a.isSubject);
  const isCall = m.type === 'call';
  const noun = isCall ? 'call' : 'meeting';
  return (
    <div className="space-y-4" data-testid="teams-meeting-detail">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
        <DetailItem label={isCall ? 'Call' : 'Meeting'} value={span(m.start, m.end)} />
        {isCall
          ? <DetailItem label="Outcome" value={m.outcome} />
          : <DetailItem label="Organizer" value={m.organizerIsSubject ? `${event.UserPrincipalName || 'This account'} (this account)` : `Another account (${m.organizerObjectId || 'unknown'})`} />}
        {!isCall && <DetailItem label="Type" value={[m.kind, m.modalities].filter(Boolean).join(' · ')} />}
        <DetailItem label="Records" value={`${event._meetingRowCount} shown (one per participant${isCall ? '' : ', plus the meeting'})${m.hiddenRecordCount ? ` · ${m.hiddenRecordCount} hidden by a dismissal or filter` : ''}`} />
      </div>
      <p className="text-[11px] text-gray-500">
        Times are when the {noun} happened. Microsoft wrote these records later
        {event.RecordedTimeUTC ? ` (${formatTimestamp(event.RecordedTimeUTC, selectedTz)})` : ''}.
        The addresses are as the Teams app reports them &mdash; usually the device&apos;s local network address, which identifies a home or office network, not a person.
      </p>

      <div>
        <h4 className="text-xs font-semibold text-gray-300 mb-2">This account&apos;s {isCall ? 'participation' : 'attendance'}</h4>
        {m.subjectAttendances.length === 0 ? (
          <p className="text-xs text-gray-500">
            {m.hiddenRecordCount
              ? `Not shown: ${m.hiddenRecordCount} record${m.hiddenRecordCount === 1 ? '' : 's'} of this ${noun} ${m.hiddenRecordCount === 1 ? 'is' : 'are'} hidden by a dismissal or filter.`
              : `No ${isCall ? 'participation' : 'attendance'} by this account is recorded for this ${noun}.`}
          </p>
        ) : m.subjectAttendances.map((a) => {
          const assess = assessAttendance(a, meetingContext?.baseline, m.meetingId);
          const st = MEETING_STATE_TEXT[assess?.state] || MEETING_STATE_TEXT.no_window;
          const near = nearbySignIns(a, meetingContext || {});
          return (
            <div key={a.eventId} className="rounded border border-gray-700 bg-gray-900/40 p-3 mb-2 space-y-2" data-testid="meeting-own-attendance">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
                <DetailItem label="Attended" value={span(a.join, a.leave)} />
                <DetailItem label="Device" value={[a.device, a.clientLabel].filter(Boolean).join(' · ')} />
                <DetailItem label="Address (as reported by Teams)" value={a.localIp} />
              </div>
              <p className={`text-xs ${st.cls}`} data-testid="meeting-familiarity">{st.text(assess?.earlierMeetings || 0)}</p>
              <div>
                <p className="text-[11px] text-gray-500 mb-1">This account&apos;s sign-ins within {MEETING_SIGNIN_WINDOW_MIN} minutes of joining</p>
                {near.length === 0 ? (
                  <p className="text-xs text-gray-500">None recorded.</p>
                ) : (
                  <table className="text-xs max-w-4xl">
                    <tbody>
                      {near.map((s) => (
                        <tr key={`${s.eventId}-${s.feed}`} className="border-t border-gray-800">
                          <td className="py-1 pr-3 text-gray-400 whitespace-nowrap font-mono">
                            {formatTimestampShort(s.time, selectedTz)}{s.lastTime ? ` \u2013 ${formatTimestampShort(s.lastTime, selectedTz)}` : ''}
                          </td>
                          <td className={`py-1 pr-3 font-mono ${getIPStatusTextClass(ipStatus(s.ip))}`} title={getIPStatusLabel(ipStatus(s.ip))}>{s.ip || '—'}</td>
                          <td className="py-1 pr-3 text-gray-300">{s.device || '—'}</td>
                          <td className="py-1 pr-3 text-gray-400" title={s.app}>
                            {s.apps.length > 3 ? `${s.apps.slice(0, 3).join(', ')} +${s.apps.length - 3} more` : (s.app || '—')}
                          </td>
                          <td className="py-1 pr-3 text-gray-500">{s.feed}</td>
                          <td className={`py-1 ${/fail/i.test(s.result) ? 'text-red-300' : 'text-gray-400'}`}>{s.result || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {others.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-300 mb-1">Other {isCall ? 'participants' : 'attendees'} ({others.length})</h4>
          <p className="text-[11px] text-gray-500 mb-2">Their participation, recorded in this account&apos;s log because this account was in the {noun}. It is not this account&apos;s activity.</p>
          <table className="text-xs w-full">
            <thead>
              <tr className="text-gray-500 text-left">
                <th className="py-1 pr-3 font-normal">{isCall ? 'Participant' : 'Attendee'}</th>
                <th className="py-1 pr-3 font-normal">Attended</th>
                <th className="py-1 pr-3 font-normal">Device</th>
                <th className="py-1 font-normal">Address</th>
              </tr>
            </thead>
            <tbody>
              {others.map((a) => (
                <tr key={a.eventId} className="border-t border-gray-800">
                  <td className="py-1 pr-3 text-gray-300">{a.upn || a.name || a.objectId}{a.isGuest && <span className="ml-1 text-[10px] text-amber-300">guest</span>}</td>
                  <td className="py-1 pr-3 text-gray-400 font-mono whitespace-nowrap">{span(a.join, a.leave)}</td>
                  <td className="py-1 pr-3 text-gray-400">{[a.device, a.clientLabel].filter(Boolean).join(' · ') || '—'}</td>
                  <td className="py-1 text-gray-500 font-mono">{a.localIp || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {onDismissEvent && (
        <button
          type="button"
          onClick={() => onDismissEvent(ids)}
          className="btn-secondary text-xs"
          data-testid="meeting-dismiss"
        >
          {isDismissed ? `Restore this ${noun} (${ids.length} records)` : `Hide this ${noun} (${ids.length} records)`}
        </button>
      )}
    </div>
  );
}

// Bulk hide, offered only for meetings where EVERY attendance by this account was from a
// device, app and local address seen before the attacker's first recorded activity. A
// toolbar button that opens a popover with the reason and the count; it asks first and
// never hides anything on its own (a device match is a lead). Absent when nothing qualifies.
function MeetingBulkHideButton({ events, meetingContext, dismissedEventIds = new Set(), onDismissEvent }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const eligible = useMemo(() => (events || []).filter((e) => e._meeting
    && meetingSeenBeforeAttacker(e, meetingContext?.baseline)
    && !(e._collapsedIds || [e.EventId]).every((id) => dismissedEventIds.has(id))), [events, meetingContext, dismissedEventIds]);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  if (!onDismissEvent || eligible.length === 0) return null;
  const ids = eligible.flatMap((e) => e._collapsedIds || [e.EventId]);
  const n = eligible.length;
  const noun = `meeting${n === 1 ? '' : 's'}/call${n === 1 ? '' : 's'}`;
  return (
    <div ref={wrapRef} className="relative" data-testid="meeting-bulk-hide">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border transition-colors ${
          open ? 'bg-gray-700 border-gray-500 text-gray-100' : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700 hover:text-gray-100'
        }`}
        title="Teams meetings and calls this account joined only from devices, apps and addresses seen before the attacker"
      >
        <VideoCameraIcon className="h-4 w-4" />
        Familiar meetings ({n})
        <ChevronDownIcon className="h-3 w-3" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 z-30 rounded-lg border border-gray-700 bg-gray-900 shadow-xl p-3 text-xs text-gray-300 space-y-3">
          <p>
            {n} Teams meeting{n === 1 ? '' : 's'} or call{n === 1 ? ' was' : 's were'} joined by this account only from devices, Teams apps and addresses seen before the attacker&apos;s first recorded activity.
          </p>
          <p className="text-amber-300">
            Hide {n} {noun} ({ids.length} records) from the timeline and the reports? You can restore them with the Dismissed filter.
          </p>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary text-xs" onClick={() => setOpen(false)}>Cancel</button>
            <button type="button" className="btn-primary text-xs" onClick={() => { onDismissEvent(ids); setOpen(false); }}>Hide {n}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ExpandedEventDetail({
  event,
  ipLookup,
  selectedTz = 'ET',
  safeIPSet,
  ipClassifications = {},
  revealSubjects = false,
  // Session-classification props (v1.53.2)
  sessionClassifications = {},
  compromisedSessions = new Map(),
  onCycleSessionStatus,
  onSetIPStatus,
}) {
  const isSignIn = event._eventType === 'SignIn';
  return (
    <div>
      <AnalystMarkControl event={event} />
      <CorrelatedActionPanel event={event} selectedTz={selectedTz} />
      <AccessPeriodPanel event={event} selectedTz={selectedTz} />
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-x-6 gap-y-1 text-xs mb-3">
        <DetailItem label="Timestamp (UTC)" value={event.EventTimeUTC ? format(parseISO(event.EventTimeUTC), 'MMM d, yyyy HH:mm:ss') : null} />
        <DetailItem label="User" value={event.UserPrincipalName} />
        <DetailItem label="Operation" value={event.Operation} />
        {/* Spans two grid columns and stacks the classifier onto its own line.
            Previously the label, a break-all address, the comment note and four
            buttons all shared one narrow column, so an address could split
            mid-octet (68.72.141.24 / 9) and the options wrapped illegibly.
            This block deliberately keeps the four explicit buttons rather than
            the portal-standard status circle: the circle cycles, and here an
            analyst needs to see and reach all four classifications at once. */}
        <div className="col-span-2">
          <div className="flex items-baseline gap-1.5 flex-wrap">
            <span className="text-[11px] text-gray-500 whitespace-nowrap" title={event._eventType === 'MessageTrace' ? 'IP of the sending mail server, not the sender\u2019s device' : ''}>
              {event._eventType === 'MessageTrace' ? 'Sending Server IP' : 'IP Address'}
            </span>
            <span className={`text-xs whitespace-nowrap ${event.IPAddress ? resolvedIPClassName(classifyIP(event.IPAddress, safeIPSet, ipLookup, ipClassifications, event)) : 'text-gray-300'}`}>{event.IPAddress || '\u2014'}</span>
            <IpCommentNote ip={event.IPAddress} />
            {event._eventType === 'MessageTrace' && event.IPAddress && <span className="text-gray-500 text-[9px]">{'\u2709'}</span>}
          </div>
          {event.IPAddress && (
            <div className="mt-1">
              <IpClassSelector ip={event.IPAddress} ipClassifications={ipClassifications} onSetIPStatus={onSetIPStatus} />
            </div>
          )}
        </div>
        {!event.IPAddress && event.InferredIP && (
          <div className="flex items-baseline gap-1.5">
            <span className="text-[11px] text-gray-500 whitespace-nowrap">Inferred IP</span>
            <span className={`text-xs break-all italic ${resolvedIPClassName(classifyIP(event.InferredIP, safeIPSet, ipLookup, ipClassifications))}`}>
              {event.InferredIP}
              {' '}
              <span className={`text-[9px] font-medium ${
                event.InferredIPConfidence === 'high' ? 'text-green-400' :
                event.InferredIPConfidence === 'medium' ? 'text-amber-400' : 'text-gray-500'
              }`}>
                {event.InferredIPConfidence === 'high' ? '\u2713' : event.InferredIPConfidence === 'medium' ? '~' : '?'}
              </span>
              <span className="text-[10px] text-gray-500 ml-1">
                ({event.InferredIPConfidence || 'low'}{event.InferredIPTimeDelta != null ? `, ${event.InferredIPTimeDelta}s` : ''}{event.InferredIPPairedLogType ? `, from ${event.InferredIPPairedLogType}` : ''})
              </span>
              <IpCommentNote ip={event.InferredIP} />
            </span>
          </div>
        )}
        {isSignIn && (
          <>
            <DetailItem label="Application" value={event.ApplicationName} />
            <DetailItem label="OS" value={event.DeviceOS} />
            <DetailItem label="Device" value={event.DeviceHostname} />
            <DetailItem label="Browser" value={parseBrowser(event.UserAgent)} />
            <DetailItem label="Status" value={event.ResultStatus} />
            <DetailItem
              label="Custom Risk Score"
              value={event.RiskScore != null ? `${event.RiskScore} (${getRiskLabel(event.RiskScore)})` : null}
            />
            <DetailItem label="Intune Device Compliant" value={event.IsDeviceCompliant === true ? 'Yes' : event.IsDeviceCompliant === false ? 'No' : null} valueClass={event.IsDeviceCompliant === true ? 'text-green-400' : undefined} />
            <DetailItem label="DRMM Name Match" value={event.DRMM_HostnameMatched === true ? 'True' : event.DRMM_HostnameMatched === false ? 'False' : null} valueClass={event.DRMM_HostnameMatched === true ? 'text-green-400' : undefined} />
            {event.SessionId
              ? <SessionDetailWithActions
                  label="Session ID"
                  sessionId={event.SessionId}
                  sessionClassifications={sessionClassifications}
                  compromisedSessions={compromisedSessions}
                  onCycleSessionStatus={onCycleSessionStatus}
                />
              : <DetailItem label="Session ID" value={event.SessionId} />}
          </>
        )}
        {!isSignIn && (
          <>
            <DetailItem label="Result" value={(event._eventType === 'SecurityAlert' || event._eventType === 'AlertEvidence' || event._eventType === 'RiskDetection') && event.ResultStatus ? `Sign-in ${event.ResultStatus}` : event.ResultStatus} />
            {/* NOT "Application": ClientAppId/ClientAppName come from the top-level
                ApplicationId, which is the API PATHWAY rather than the calling app on
                26.6% of Exchange rows. The calling app is shown in the Item block
                below, from CallingAppName. Two different values must not both be
                labelled "Application". */}
            {event.ClientAppName && <DetailItem label="Recorded application (API pathway)" value={`${event.ClientAppName}${event.ClientAppId ? ` (${event.ClientAppId})` : ''}`} />}
            <DetailItem label="Client Info" value={event.ClientInfo} />
            {event.Operation === 'MailItemsAccessed' && (() => {
              const accessType = getMailAccessType(event);
              const labels = { Sync: 'Sync (bulk download)', Read: 'Read (user action)', AppAccess: 'App access (background)' };
              const classes = {
                Sync: 'bg-amber-900/60 text-amber-300 border border-amber-700/50',
                Read: 'bg-blue-900/60 text-blue-300 border border-blue-700/50',
                AppAccess: 'bg-purple-900/60 text-purple-300 border border-purple-700/50',
              };
              const label = labels[accessType] || 'Unknown';
              const badgeClass = classes[accessType] || 'bg-gray-800 text-gray-400 border border-gray-700';
              return (
                <div className="flex items-baseline gap-1.5">
                  <span className="text-[11px] text-gray-500 whitespace-nowrap">Access Type</span>
                  <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium ${badgeClass}`}>
                    {label}
                  </span>
                </div>
              );
            })()}
            {event.OriginalIPAddress && event.OriginalIPAddress !== event.IPAddress && (
              <div className="flex items-baseline gap-1.5">
                <span className="text-[11px] text-gray-500 whitespace-nowrap">Original IP</span>
                <span className="text-xs break-all text-green-400">{event.OriginalIPAddress}</span>
              </div>
            )}
            {event.GuessedIP && (
              <DetailItem
                label="Guessed IP (not observed)"
                value={`possibly ${event.GuessedIP} — from ${guessedIpBasis(event)}`}
                valueClass="text-gray-400 italic"
              />
            )}
            {appOnlyReader(event) && (
              <DetailItem label="Read by" value={`${appOnlyReader(event)} (app-only, no user session)`} />
            )}
            {event.IPResolutionMethod && event.IPResolutionMethod !== 'Direct' && (
              <DetailItem label="IP Resolution" value={event.IPResolutionMethod} />
            )}
            {event.ServiceAppsSeen && (
              <DetailItem label="Service App" value={event.ServiceAppsSeen} />
            )}
            {event.AADSessionId && (
              <SessionDetailWithActions
                label="AAD Session"
                sessionId={event.AADSessionId}
                sessionClassifications={sessionClassifications}
                compromisedSessions={compromisedSessions}
                onCycleSessionStatus={onCycleSessionStatus}
              />
            )}
            {event.ResolvedIPCount > 1 && event.AllResolvedIPs && (
              <div className="flex items-baseline gap-1.5">
                <span className="text-[11px] text-gray-500 whitespace-nowrap">All Resolved IPs</span>
                <div className="flex flex-col text-xs">
                  {event.AllResolvedIPs.split(',').map((rip) => {
                    const trimmed = rip.trim();
                    const cls = classifyIP(trimmed, safeIPSet, ipLookup, ipClassifications);
                    const label = resolvedIPLabel(cls);
                    return (
                      <span key={trimmed}>
                        <span className={resolvedIPClassName(cls)}>{trimmed}</span>
                        {label && <span className="text-[9px] text-gray-500 ml-1">{label}</span>}
                        <IpCommentNote ip={trimmed} />
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {event._eventType === 'MessageTrace' && (
        <div className="mb-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 text-xs">
            <DetailItem label="Direction" value={event.Direction} />
            <DetailItem label="Sender" value={event.SenderAddress} />
            {!event._collapsed && <DetailItem label="Recipient" value={event.RecipientAddress} />}
            <DetailItem label="Subject" value={event.Subject ? maskSubject(event.Subject, revealSubjects) : event.Subject} />
            <DetailItem label="Status" value={event._collapsed ? recipientStatusSummary(event) : event.Status} />
            <DetailItem label="Message ID" value={event.MessageId} />
          </div>
          {event._collapsed && (
            <div className="mt-2">
              <span className="text-[11px] text-gray-500">
                {`Recipients (${event._recipientCount}${event._recipientsPartial ? ` of ${event._declaredRecipientCount} on the message` : ''})`}
              </span>
              <ul className="mt-0.5 space-y-0.5">
                {event._recipients.map(r => (
                  <li key={r.eventId} className="flex items-baseline gap-2 text-xs">
                    <span className="text-gray-300 break-all">{r.address || '(no address)'}</span>
                    <span className={`text-[10px] ${getStatusClass(r.status)}`}>{r.status || 'unknown'}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {event.Parameters && (
        <ParametersSection parameters={event.Parameters} previousParameters={event.PreviousParameters} operation={event.Operation} />
      )}
      {/* Item / change detail (v1.151.0).
          Rendered from its OWN predicate, deliberately NOT nested inside the
          `event.Folders &&` block below: the `Update` / `Create` rows this exists for
          carry `Item`, not `Folders`, so gating on Folders would hide the block on
          exactly the records that motivated the work. */}
      {(event.ItemSubject || event.ItemAttachments || event.ItemFolderPath
        || event.ModifiedProperties || event.DeviceId || event.CallingAppId) && (() => {
        const props = parseModifiedProperties(event.ModifiedProperties);
        const att = event.ItemAttachments ? parseItemAttachments(event.ItemAttachments) : null;
        const attCount = event.ItemAttachmentCount != null ? event.ItemAttachmentCount : (att ? att.count : null);
        return (
          <div className="mb-3">
            <p className="text-xs font-medium text-gray-500 mb-2">Item / Change Detail</p>
            <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 text-xs space-y-1">
              {event.ItemSubject && (
                <div className="flex gap-2">
                  <span className="text-gray-500 font-medium flex-shrink-0 w-32">Subject:</span>
                  <span className={`break-all${revealSubjects ? ' text-gray-300' : ' text-gray-600 select-none'}`}>
                    {maskSubject(event.ItemSubject, revealSubjects)}
                  </span>
                </div>
              )}
              {event.ItemFolderPath && (
                <div className="flex gap-2">
                  <span className="text-gray-500 font-medium flex-shrink-0 w-32">Folder:</span>
                  {/* FULL path here; the one-line description shows only the top
                      segment, because nested paths carry client / matter names. */}
                  <span className="text-gray-300 break-all">{event.ItemFolderPath}</span>
                </div>
              )}
              {props && props.length > 0 && (
                <div className="flex gap-2">
                  <span className="text-gray-500 font-medium flex-shrink-0 w-32">Changed:</span>
                  <span className="text-gray-300 break-all">{props.join(', ')}</span>
                </div>
              )}
              {event.ItemAttachments && (
                <div className="flex gap-2">
                  <span className="text-gray-500 font-medium flex-shrink-0 w-32">
                    Attachments{attCount != null ? ` (${attCount})` : ''}:
                  </span>
                  {/* Microsoft: Attachments lists ALL items attached to the message —
                      a STATE, never a delta. The wording must not imply the actor
                      attached them. Names are masked with subjects. */}
                  <span className={`break-all${revealSubjects ? ' text-gray-300' : ' text-gray-600 select-none'}`}>
                    {revealSubjects
                      ? (att ? att.names.join('; ') : event.ItemAttachments)
                      : FILENAME_MASK /* the count is already in the label */}
                  </span>
                </div>
              )}
              {event.ItemInternetMessageId && (
                <div className="flex gap-2">
                  <span className="text-gray-500 font-medium flex-shrink-0 w-32">Message ID:</span>
                  <span className="text-gray-400 break-all font-mono text-[10px]">{event.ItemInternetMessageId}</span>
                </div>
              )}
              {event.ItemSizeInBytes != null && event.ItemSizeInBytes !== '' && (
                <div className="flex gap-2">
                  <span className="text-gray-500 font-medium flex-shrink-0 w-32">Size:</span>
                  <span className="text-gray-400">{(Number(event.ItemSizeInBytes) / 1024).toFixed(1)} KB</span>
                </div>
              )}
              {event.CallingAppId && (
                <div className="flex gap-2 group">
                  <span className="text-gray-500 font-medium flex-shrink-0 w-32">Calling application:</span>
                  {/* An unnamed calling app is the NORMAL case here, not a failure, so it
                      must not read like one. Microsoft records AppAccessContext.ClientAppId
                      on Exchange audit events but leaves ClientAppName EMPTY -- measured
                      blank on all top-14 calling apps across 26+ tenants and ~1.9M rows --
                      and the tenant app registry is built from user sign-ins, so it misses
                      backend/app-only callers entirely. We do not invent a name. The id is
                      shown mono + copyable so an analyst can identify it once and label it
                      via Known Apps, after which it resolves everywhere. */}
                  {event.CallingAppName ? (
                    <span className="text-gray-300 break-all">
                      {event.CallingAppName}
                      <span className="text-gray-500 font-mono text-[10px] ml-1">({event.CallingAppId})</span>
                    </span>
                  ) : (
                    <span className="break-all">
                      <span className="text-gray-300 font-mono text-[10px]">{event.CallingAppId}</span>
                      <CopyButton value={event.CallingAppId} />
                      <span
                        className="text-gray-500 ml-1"
                        title="Microsoft records the calling application's ID on Exchange audit events but not its display name, and this ID is not in the tenant's app registry (built from user sign-ins) or its Entra service principals. Label it under Settings > Known Applications to name it everywhere."
                      >
                        — no display name in tenant app data
                      </span>
                    </span>
                  )}
                </div>
              )}
              {event.DeviceId && (
                <div className="flex gap-2">
                  <span className="text-gray-500 font-medium flex-shrink-0 w-32">Device ID:</span>
                  {/* Microsoft populates this only for a registered / domain-joined
                      device, so its presence is itself an attribution signal. */}
                  <span className="text-gray-400 break-all font-mono text-[10px]">{event.DeviceId}</span>
                </div>
              )}
            </div>
          </div>
        );
      })()}
      {event.Folders && (() => {
        const parsed = parseFolders(event.Folders);
        if (!parsed) return null;
        // Normalize to array of folder objects (real M365 data is an array; mock is a single object)
        const folders = Array.isArray(parsed) ? parsed : [parsed];
        // Check if this is a SharePoint-style entry (has FileName or URL path)
        const isFileEntry = folders.some(f => f.FileName || (f.Path && f.Path.startsWith('http')));
        // Subjects AND file names are gated by the one reveal control (v1.151.0), which
        // sits in the All Events toolbar rather than in each expanded row.
        return (
          <div className="mb-3">
            <p className="text-xs font-medium text-gray-500 mb-2">
              {isFileEntry ? 'File / Site Data' : 'Folders / Mail Items'}
            </p>
            <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 text-xs overflow-x-auto max-h-64 overflow-y-auto space-y-3">
              {folders.map((folder, fi) => (
                <div key={fi} className={fi > 0 ? 'pt-3 border-t border-gray-800' : ''}>
                  {folder.Path && (
                    <div className="flex gap-2 mb-1">
                      <span className="text-gray-500 font-medium flex-shrink-0">{isFileEntry ? 'Site:' : 'Path:'}</span>
                      <span className="text-gray-300 break-all">{folder.Path}</span>
                    </div>
                  )}
                  {folder.FileName && (
                    <div className="flex gap-2 mb-1">
                      <span className="text-gray-500 font-medium flex-shrink-0">File:</span>
                      <span className={`break-all${revealSubjects ? ' text-gray-300' : ' text-gray-600 select-none'}`}
                            title={revealSubjects ? folder.FileName : undefined}>
                        {maskFileName(folder.FileName, revealSubjects)}
                      </span>
                    </div>
                  )}
                  {folder.FolderItems && folder.FolderItems.length > 0 && (
                    <div className="mt-2">
                      <span className="text-gray-500 font-medium">
                        {folder.FolderItems.length} item{folder.FolderItems.length !== 1 ? 's' : ''} accessed
                      </span>
                      <table className="w-full mt-1">
                        <thead>
                          <tr className="border-b border-gray-700">
                            <th className="py-1 pr-3 text-left text-gray-500 font-medium">#</th>
                            <th className="py-1 pr-3 text-left text-gray-500 font-medium">Subject</th>
                            {folder.FolderItems.some(i => i.InternetMessageId) && (
                              <th className="py-1 pr-3 text-left text-gray-500 font-medium">Message ID</th>
                            )}
                            {folder.FolderItems.some(i => i.SizeInBytes != null) && (
                              <th className="py-1 text-left text-gray-500 font-medium">Size</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {folder.FolderItems.map((item, ii) => (
                            <tr key={ii} className="border-b border-gray-800 last:border-b-0">
                              <td className="py-1 pr-3 text-gray-500">{ii + 1}</td>
                              <td className={`py-1 pr-3 break-all${item.Subject && !revealSubjects ? ' text-gray-600 select-none' : ' text-gray-300'}`}>{item.Subject ? maskSubject(item.Subject, revealSubjects) : '(no subject)'}</td>
                              {folder.FolderItems.some(i => i.InternetMessageId) && (
                                <td className="py-1 pr-3 text-gray-400 break-all font-mono text-[10px]">{item.InternetMessageId || ''}</td>
                              )}
                              {folder.FolderItems.some(i => i.SizeInBytes != null) && (
                                <td className="py-1 text-gray-400 whitespace-nowrap">
                                  {item.SizeInBytes != null ? `${(item.SizeInBytes / 1024).toFixed(1)} KB` : ''}
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function ExpandedSignInDetail({
  event,
  safeIPSet,
  ipLookup,
  ipClassifications = {},
  // Session-classification props (v1.53.2)
  sessionClassifications = {},
  compromisedSessions = new Map(),
  onCycleSessionStatus,
  onSetIPStatus,
}) {
  // Parse CA policies from JSON string or array
  const caPolicies = useMemo(() => {
    if (!event.ConditionalAccessPolicies) return [];
    try {
      const parsed = typeof event.ConditionalAccessPolicies === 'string'
        ? JSON.parse(event.ConditionalAccessPolicies)
        : event.ConditionalAccessPolicies;
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  }, [event.ConditionalAccessPolicies]);

  // Parse JSON array fields
  const parseJsonArray = (val) => {
    if (!val) return [];
    try { const p = typeof val === 'string' ? JSON.parse(val) : val; return Array.isArray(p) ? p : []; } catch { return []; }
  };

  const authMethods = parseJsonArray(event.AuthenticationMethodsUsed);
  const signInTypes = parseJsonArray(event.SignInEventTypes);
  const riskEventTypes = parseJsonArray(event.RiskEventTypes);
  const networkLocations = parseJsonArray(event.NetworkLocationDetails);

  const riskDuringSignIn = event.RiskLevelDuringSignIn;
  const hasEnrichment = event.GraphSignInEnriched;
  const isFailed = event.ResultStatus && event.ResultStatus.toLowerCase() === 'failed';

  return (
    <div>
      <AnalystMarkControl event={event} />
      <RecoveredLegBanner event={event} />
      {/^https?:\/\//i.test(String(event.ApplicationId || '')) && (
        <div className="mb-3 px-3 py-2 rounded border bg-red-900/30 border-red-700/50">
          <div className="flex items-start gap-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-red-900/50 text-red-300 border border-red-700/50 whitespace-nowrap">
              Phishing-kit app
            </span>
            <p className="text-[11px] text-red-200/90 leading-snug">
              The sign-in <code className="text-red-300">Application</code> is a URL (<code className="text-red-300">{String(event.ApplicationId)}</code>), not a normal application ID. A URL-shaped application is the hallmark of an adversary-in-the-middle phishing kit — a spoofed sign-in domain relaying the real Microsoft login. Treat this as high-attention regardless of the risk score, and assume the password <em>and</em> an MFA-satisfied token were captured.
            </p>
          </div>
        </div>
      )}
      {hasEnrichment && (
        <div className="mb-3">
          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-blue-900/40 text-blue-300 border border-blue-700/40">
            Enriched with Graph Sign-In Data
          </span>
        </div>
      )}
      {/* Row 1: Identity & target */}
      <div className="flex flex-wrap gap-x-6 gap-y-0.5 text-xs mb-1.5">
        <DetailItem label="User" value={event.UserPrincipalName} />
        <DetailItem label="Application" value={event.ApplicationName} />
        <div className="flex items-baseline gap-1.5">
          <span className="text-[11px] text-gray-500 whitespace-nowrap">IP</span>
          <span className={`text-xs break-all ${event.IPAddress ? resolvedIPClassName(classifyIP(event.IPAddress, safeIPSet, ipLookup, ipClassifications, event)) : 'text-gray-300'}`}>{event.IPAddress || '\u2014'}<IpCommentNote ip={event.IPAddress} /></span>
          {event.IPAddress && <IpClassSelector ip={event.IPAddress} ipClassifications={ipClassifications} onSetIPStatus={onSetIPStatus} />}
        </div>
        <DetailItem label="Location" value={[event.IP_City || event.Location?.City, event.IP_State || event.Location?.State, event.IP_Country || event.Location?.Country].filter(Boolean).join(', ') || null} />
        <DetailItem label="Provider" value={event.IP_Provider} />
        {event.ResourceDisplayName && <DetailItem label="Resource" value={event.ResourceDisplayName} />}
        {event.ClientAppUsed && <DetailItem label="Client App" value={event.ClientAppUsed} />}
        {event.SessionId && (
          <SessionDetailWithActions
            label="Session"
            sessionId={event.SessionId}
            sessionClassifications={sessionClassifications}
            compromisedSessions={compromisedSessions}
            onCycleSessionStatus={onCycleSessionStatus}
          />
        )}
        {event.AutonomousSystemNumber && <DetailItem label="ASN" value={event.AutonomousSystemNumber} />}
      </div>

      {/* Row 2: Authentication details */}
      <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs mb-1.5">
        {event.IncomingTokenType && <DetailItem label="Token Type" value={event.IncomingTokenType} />}
        {event.AuthenticationProtocol && <DetailItem label="Auth Protocol" value={event.AuthenticationProtocol} />}
        {event.AuthenticationRequirement && <DetailItem label="Auth Requirement" value={event.AuthenticationRequirement} />}
        {event.RequestType && <DetailItem label="Request Type" value={event.RequestType} />}
        {event.IsInteractive != null && (
          <div className="flex items-baseline gap-1.5">
            <span className="text-[11px] text-gray-500">Interactive</span>
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${event.IsInteractive ? 'bg-green-900/40 text-green-300 border border-green-700/40' : 'bg-amber-900/40 text-amber-300 border border-amber-700/40'}`}>
              {event.IsInteractive ? 'Yes' : 'No'}
            </span>
          </div>
        )}
        {authMethods.length > 0 && <DetailItem label="Auth Methods" value={authMethods.join(', ')} />}
        {signInTypes.length > 0 && <DetailItem label="Event Types" value={signInTypes.join(', ')} />}
        {event.TokenIssuerType && <DetailItem label="Token Issuer" value={event.TokenIssuerName ? `${event.TokenIssuerType} (${event.TokenIssuerName})` : event.TokenIssuerType} />}
        {event.SignInTokenProtectionStatus && event.SignInTokenProtectionStatus !== 'none' && (
          <div className="flex items-baseline gap-1.5">
            <span className="text-[11px] text-gray-500">Token Protection</span>
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${event.SignInTokenProtectionStatus === 'bound' ? 'bg-green-900/40 text-green-300 border border-green-700/40' : 'bg-red-900/40 text-red-300 border border-red-700/40'}`}>
              {event.SignInTokenProtectionStatus}
            </span>
          </div>
        )}
        {event.CorrelationId && <DetailItem label="Correlation ID" value={event.CorrelationId} />}
      </div>

      {/* Row 3: Cross-tenant (only when relevant) */}
      {(event.CrossTenantAccessType && event.CrossTenantAccessType !== 'none') || event.HomeTenantId || event.ResourceTenantId ? (
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs mb-1.5">
          {event.CrossTenantAccessType && event.CrossTenantAccessType !== 'none' && <DetailItem label="Cross-Tenant" value={event.CrossTenantAccessType} />}
          {event.HomeTenantId && <DetailItem label="Home Tenant" value={event.HomeTenantId} />}
          {event.ResourceTenantId && <DetailItem label="Resource Tenant" value={event.ResourceTenantId} />}
        </div>
      ) : null}

      {/* Row 4: Network location (only when present) */}
      {networkLocations.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs mb-1.5">
          <span className="text-[11px] text-gray-500">Network Location</span>
          {networkLocations.map((loc, i) => (
            <span key={i} className="text-xs text-gray-300">{loc.networkType || loc.networkNames?.join(', ') || JSON.stringify(loc)}</span>
          ))}
        </div>
      )}

      {/* Compact table for device/status fields */}
      <table className="text-xs mb-1.5">
        <thead>
          <tr className="text-[10px] text-gray-600 uppercase tracking-wider">
            <th className="pr-4 py-0.5 text-left font-medium">Device</th>
            <th className="pr-4 py-0.5 text-left font-medium">OS</th>
            {event.DeviceTrustType && <th className="pr-4 py-0.5 text-left font-medium">Trust Type</th>}
            <th className="pr-4 py-0.5 text-left font-medium">Browser</th>
            <th className="pr-4 py-0.5 text-left font-medium">Status</th>
            {isFailed && event.ErrorCode != null && event.ErrorCode !== 0 && <th className="pr-4 py-0.5 text-left font-medium">Error</th>}
            {isFailed && event.FailureReason && <th className="pr-4 py-0.5 text-left font-medium">Failure Reason</th>}
            {!isFailed && <th className="pr-4 py-0.5 text-left font-medium">Detail</th>}
            <th className="pr-4 py-0.5 text-left font-medium">Intune Compliant</th>
            <th className="pr-4 py-0.5 text-left font-medium">DRMM</th>
            <th className="pr-4 py-0.5 text-left font-medium">Risk Score</th>
            {event.MfaDetail && <th className="pr-4 py-0.5 text-left font-medium">MFA</th>}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="pr-4 py-0.5 text-gray-300">{event.DeviceHostname || '\u2014'}</td>
            <td className="pr-4 py-0.5 text-gray-300">{event.DeviceOS || '\u2014'}</td>
            {event.DeviceTrustType && <td className="pr-4 py-0.5 text-gray-300">{event.DeviceTrustType}</td>}
            <td className="pr-4 py-0.5 text-gray-300">{parseBrowser(event.UserAgent) || '\u2014'}</td>
            <td className={`pr-4 py-0.5 ${isFailed ? 'text-red-400' : 'text-gray-300'}`}>{event.ResultStatus || '\u2014'}</td>
            {isFailed && event.ErrorCode != null && event.ErrorCode !== 0 && <td className="pr-4 py-0.5 text-red-400">{event.ErrorCode}</td>}
            {isFailed && event.FailureReason && <td className="pr-4 py-0.5 text-red-400 max-w-xs truncate" title={event.FailureReason}>{event.FailureReason}</td>}
            {!isFailed && <td className="pr-4 py-0.5 text-gray-300">{event.ResultStatusDetail || '\u2014'}</td>}
            <td className={`pr-4 py-0.5 ${event.IsDeviceCompliant === true ? 'text-green-400' : 'text-gray-300'}`}>{event.IsDeviceCompliant != null ? (event.IsDeviceCompliant ? 'Yes' : 'No') : '\u2014'}</td>
            <td className={`pr-4 py-0.5 ${event.DRMM_HostnameMatched === true ? 'text-green-400' : 'text-gray-300'}`}>{event.DRMM_HostnameMatched === true ? 'True' : event.DRMM_HostnameMatched === false ? 'False' : '\u2014'}</td>
            <td className="pr-4 py-0.5 text-gray-300">{event.RiskScore != null ? `${event.RiskScore} (${getRiskLabel(event.RiskScore)})` : '\u2014'}</td>
            {event.MfaDetail && <td className="pr-4 py-0.5 text-gray-300">{event.MfaDetail}</td>}
          </tr>
        </tbody>
      </table>

      {/* Risk & Policy badges (inline, only when present) */}
      {(riskDuringSignIn && riskDuringSignIn !== 'none') || (event.RiskLevelAggregated && event.RiskLevelAggregated !== 'none') || event.RiskFactors || (event.RiskState && event.RiskState !== 'none') || riskEventTypes.length > 0 || event.ConditionalAccessStatus || caPolicies.length > 0 ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs mt-1">
          {riskDuringSignIn && riskDuringSignIn !== 'none' && (
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-gray-500">MSFT Sign-In Risk</span>
              {riskDuringSignIn === 'hidden' ? (
                <span className="text-[10px] text-gray-600 italic" title="Tenant does not have Entra ID P2 / Identity Protection license">(not licensed)</span>
              ) : (
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                  riskDuringSignIn === 'high' ? 'bg-red-900/40 text-red-300 border border-red-700/40' :
                  riskDuringSignIn === 'medium' ? 'bg-amber-900/40 text-amber-300 border border-amber-700/40' :
                  riskDuringSignIn === 'low' ? 'bg-yellow-900/40 text-yellow-300 border border-yellow-700/40' :
                  'bg-gray-800 text-gray-400 border border-gray-700'
                }`}>
                  {riskDuringSignIn.charAt(0).toUpperCase() + riskDuringSignIn.slice(1)}
                </span>
              )}
            </div>
          )}
          {event.RiskLevelAggregated && event.RiskLevelAggregated !== 'none' && (
            event.RiskLevelAggregated === 'hidden'
              ? <div className="flex items-center gap-1.5"><span className="text-[11px] text-gray-500">MSFT Aggregated Risk</span><span className="text-[10px] text-gray-600 italic" title="Tenant does not have Entra ID P2 / Identity Protection license">(not licensed)</span></div>
              : <DetailItem label="MSFT Aggregated Risk" value={event.RiskLevelAggregated} />
          )}
          {event.RiskState && event.RiskState !== 'none' && (
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-gray-500">Risk State</span>
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                event.RiskState === 'atRisk' || event.RiskState === 'confirmedCompromised' ? 'bg-red-900/40 text-red-300 border border-red-700/40' :
                event.RiskState === 'confirmedSafe' || event.RiskState === 'remediated' || event.RiskState === 'dismissed' ? 'bg-green-900/40 text-green-300 border border-green-700/40' :
                'bg-gray-800 text-gray-400 border border-gray-700'
              }`}>
                {event.RiskState}
              </span>
            </div>
          )}
          {riskEventTypes.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[11px] text-gray-500">Risk Events</span>
              {riskEventTypes.map((rt, i) => (
                <span key={i} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-900/40 text-red-300 border border-red-700/40">{rt}</span>
              ))}
            </div>
          )}
          {event.RiskFactors && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[11px] text-gray-500">Risk Factors</span>
              {Object.entries(event.RiskFactors || {}).map(([key, val]) => (
                val === true && (
                  <span key={key} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-900/40 text-red-300 border border-red-700/40">
                    {key}
                  </span>
                )
              ))}
            </div>
          )}
          {event.ConditionalAccessStatus && (
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-gray-500">CA Status</span>
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                event.ConditionalAccessStatus === 'success' ? 'bg-green-900/40 text-green-300 border border-green-700/40' :
                event.ConditionalAccessStatus === 'failure' ? 'bg-red-900/40 text-red-300 border border-red-700/40' :
                'bg-gray-800 text-gray-400 border border-gray-700'
              }`}>
                {event.ConditionalAccessStatus}
              </span>
            </div>
          )}
          {caPolicies.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[11px] text-gray-500">CA Policies ({caPolicies.length})</span>
              {caPolicies.map((policy, idx) => (
                <span key={idx} className="inline-flex items-center gap-1">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                    policy.result === 'success' ? 'bg-green-900/40 text-green-300 border border-green-700/40' :
                    policy.result === 'failure' ? 'bg-red-900/40 text-red-300 border border-red-700/40' :
                    'bg-gray-800 text-gray-500 border border-gray-700'
                  }`}>
                    {policy.result === 'success' ? 'Applied' : policy.result === 'failure' ? 'Failed' : 'N/A'}
                  </span>
                  <span className="text-gray-400 text-[11px]">{policy.policyName || 'Unnamed'}</span>
                  {policy.enforcedGrantControls && policy.enforcedGrantControls.length > 0 && (
                    <span className="text-gray-600 text-[10px]">({policy.enforcedGrantControls.join(', ')})</span>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

// Expanded row for a portal detection alert (utils/caseAlerts.js). Summary only; the
// full alert (IP intelligence, baseline comparison, source events) is on the Alerts
// page, opened in a NEW tab so the case view is not lost.
function ExpandedCaseAlertDetail({ event, selectedTz }) {
  const pa = event?._portalAlert || {};
  const href = caseAlertHref(event);
  const basisText = pa.matchBasis === 'named'
    ? `Named in the alert's event (alert user: ${pa.userId || 'unknown'})`
    : 'The case user is the alert’s user';
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {pa.severity && <Badge tone={severityTone(pa.severity)}>{pa.severity}</Badge>}
        {pa.status && <Badge tone={alertStatusTone(pa.status)}>{pa.status}</Badge>}
        <span className="text-sm text-gray-200">{pa.ruleName || CASE_ALERT_LABEL}</span>
        {href && (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto text-xs text-blue-400 hover:text-blue-300 underline"
          >
            Open in Alerts &#8599;
          </a>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
        <DetailItem label="Event time" value={pa.eventTime ? formatTimestamp(pa.eventTime, selectedTz) : ''} />
        <DetailItem label="Alert raised" value={pa.alertTime ? formatTimestamp(pa.alertTime, selectedTz) : ''} />
        <DetailItem label="Alert user" value={pa.userId} />
        <DetailItem label="Why it is here" value={basisText} />
        <DetailItem label="Log type" value={pa.logType} />
        <DetailItem label="Operation" value={pa.operation} />
        <DetailItem label="IP address" value={event.IPAddress} />
        <DetailItem label="Application" value={event.AppDisplayName} />
        {pa.status === 'closed' && (
          <>
            <DetailItem label="Closed by" value={pa.closedBy} />
            <DetailItem label="Closed" value={pa.closedUtc ? formatTimestamp(pa.closedUtc, selectedTz) : ''} />
            <DetailItem label="Resolution note" value={pa.closeReason} />
            {pa.psaTicketNumber && <DetailItem label="PSA ticket" value={pa.psaTicketNumber} />}
          </>
        )}
      </div>
    </div>
  );
}

function DetailItem({ label, value, valueClass }) {
  // Safely stringify objects (e.g., Azure ARM {value, localizedValue} enums)
  const display = (value && typeof value === 'object')
    ? (value.localizedValue || value.value || JSON.stringify(value))
    : value;
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-[11px] text-gray-500 whitespace-nowrap">{label}</span>
      <span className={`text-xs break-all ${valueClass || 'text-gray-300'}`}>{display || '\u2014'}</span>
    </div>
  );
}

const SESSION_MARK_EXAMPLE = "Created by an attacker relay: the sign-in came from an address seen only this once while the user's own devices were elsewhere, and the session was later replayed from a malicious address.";

// Session id detail with the session classifier (v1.53.2 Safe; v1.190.0 Attacker). Same shape
// as the row control (AnalystMarkControl): Not marked / Safe / Attacker. Attacker means the
// session was STOLEN from the sign-in that created it (utils/sessionMarks.js): every row on it
// is attributed, its compromise start is its FIRST row, and no address is classified.
function SessionDetailWithActions({ label, sessionId, sessionClassifications, compromisedSessions, onCycleSessionStatus }) {
  const markCtx = useContext(SessionMarkContext);
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  if (!sessionId) return null;
  const status = sessionStatusOf(sessionClassifications, sessionId);
  const attackerMark = status === 'attacker' ? sessionClassifications[sessionId] : null;
  const isAttacker = Boolean(attackerMark);
  // Compromised by a malicious-address touch (not by the analyst's mark).
  const ipCompromised = !isAttacker && compromisedSessions && compromisedSessions.has(sessionId);
  const isSafe = status === 'safe' && !ipCompromised;
  const canMark = typeof markCtx?.onSet === 'function';
  const selecting = editing && !isAttacker;
  const none = !isAttacker && !isSafe && !selecting;

  const stop = (e) => e.stopPropagation();
  const setMark = async (value) => {
    setSaving(true);
    setError('');
    try {
      await markCtx.onSet(sessionId, value);
      setEditing(false);
      setReason('');
    } catch (err) {
      setError(err?.message || 'Could not save. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };
  const optClass = (active, statusKey) => `inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border transition-colors disabled:opacity-40 ${
    active ? getIPStatusBgClass(statusKey) : 'bg-transparent border-gray-700 text-gray-500 hover:text-gray-300 hover:border-gray-500'}`;
  const smallBtn = 'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border bg-transparent border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-500 disabled:opacity-40';
  const when = attackerMark && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(attackerMark.at || '')
    ? `${attackerMark.at.slice(0, 10)} ${attackerMark.at.slice(11, 16)}` : (attackerMark?.at || '');
  const trimmed = reason.trim();

  return (
    <div className="text-xs" onClick={stop}>
      <div className="flex items-baseline gap-1.5 flex-wrap">
        <span className="text-[11px] text-gray-500 whitespace-nowrap">{label}</span>
        <span className="text-xs break-all font-mono text-gray-300">{sessionId}</span>
        <span className="inline-flex items-center gap-1 flex-wrap" data-testid="session-mark-control">
          <button
            type="button"
            aria-pressed={none}
            disabled={saving || (none && !selecting)}
            className={optClass(none, 'default')}
            title={isAttacker ? 'Remove the attacker mark from this session' : (isSafe ? 'Remove the Safe classification' : 'Not marked (current)')}
            onClick={() => {
              if (isAttacker) setMark(null);
              else if (selecting) { setEditing(false); setError(''); }
              else if (isSafe && onCycleSessionStatus) onCycleSessionStatus(sessionId);
            }}
          >
            {none ? '✓ Not marked' : (saving && isAttacker ? 'Removing…' : 'Not marked')}
          </button>
          <button
            type="button"
            aria-pressed={isSafe && !selecting}
            disabled={saving || ipCompromised || isAttacker || !onCycleSessionStatus}
            className={optClass(isSafe && !selecting, 'safe')}
            title={ipCompromised
              ? 'Session was touched by a malicious IP — Safe is ignored. Un-classify the malicious IP to enable.'
              : (isAttacker ? 'Remove the attacker mark first.'
                : (isSafe ? 'Session is classified Safe (current)' : 'Mark this session Safe — events under safe sessions can be hidden via the toolbar toggle.'))}
            onClick={() => { if (!isSafe && onCycleSessionStatus) { setEditing(false); onCycleSessionStatus(sessionId); } }}
          >
            {isSafe && !selecting ? '✓ Safe' : 'Safe'}
          </button>
          <button
            type="button"
            aria-pressed={isAttacker || selecting}
            disabled={saving || !canMark}
            className={optClass(isAttacker || selecting, 'malicious')}
            title={isAttacker
              ? 'Marked as the attacker’s session (current)'
              : 'Mark this session as the attacker’s, from the sign-in that created it — you will be asked why. No address is classified.'}
            onClick={() => { if (!isAttacker) setEditing(true); }}
          >
            {isAttacker || selecting ? '✓ Attacker' : 'Attacker'}
          </button>
          {isAttacker && canMark && !editing && (
            <button type="button" className={smallBtn} disabled={saving} onClick={() => { setReason(attackerMark.reason); setEditing(true); }}>
              Edit reason
            </button>
          )}
          {ipCompromised && (
            <span className="text-[10px] text-purple-400" title="Session was touched by a malicious IP. Its compromise starts at that first touch; earlier rows on it are hidden by 'Pre-compromise hidden'. Mark it Attacker if the session was stolen from its creation.">
              compromised
            </span>
          )}
        </span>
      </div>

      {isAttacker && !editing && (
        <div className="mt-1.5 px-2.5 py-1.5 rounded border bg-rose-900/20 border-rose-800/50" data-testid="session-attacker-mark">
          <span className="text-rose-200 font-medium">Session marked as the attacker&rsquo;s</span>
          {attackerMark.by ? <span className="text-rose-300/80"> by {attackerMark.by}</span> : null}
          {when ? <span className="text-rose-300/80"> on {when} UTC</span> : null}
          <div className="mt-0.5 text-gray-200">Reason: {attackerMark.reason}</div>
          <div className="mt-0.5 text-gray-500">
            Every row on this session is counted as the attacker&rsquo;s from its first row, in the window and the reports. No address on it is classified by this mark.
          </div>
        </div>
      )}

      {editing && (
        <div className="mt-1.5 px-2.5 py-1.5 rounded border border-gray-700 bg-gray-900/60">
          <label className="block text-gray-300 mb-1" htmlFor={`session-mark-reason-${sessionId}`}>
            Why is this session the attacker&rsquo;s? <span className="text-gray-500">(required — shown in the reports)</span>
          </label>
          <textarea
            id={`session-mark-reason-${sessionId}`}
            className="input-field w-full text-xs"
            rows={2}
            maxLength={MARK_REASON_MAX}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={`e.g. ${SESSION_MARK_EXAMPLE}`}
          />
          <div className="mt-1.5 flex items-center gap-1 flex-wrap">
            <button type="button" className={optClass(Boolean(trimmed), 'malicious')} disabled={!trimmed || saving} onClick={() => setMark(trimmed)}>
              {saving ? 'Saving…' : (isAttacker ? 'Save reason' : 'Save mark')}
            </button>
            <button type="button" className={smallBtn} disabled={saving} onClick={() => setReason(SESSION_MARK_EXAMPLE)}>
              Use example
            </button>
            <button type="button" className={smallBtn} disabled={saving} onClick={() => { setEditing(false); setError(''); setReason(''); }}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {error && <div className="mt-1 text-red-300">{error}</div>}
    </div>
  );
}
